import React, { useEffect, useMemo, useRef, useState } from 'react';
import { db, appId } from '../../../firebase-config';
import {
  addDoc,
  arrayUnion,
  collection,
  doc,
  getDoc,
  getDocs,
  increment,
  onSnapshot,
  query,
  serverTimestamp,
  setDoc,
  updateDoc,
  where,
  writeBatch
} from 'firebase/firestore';

import { getActiveCompanyId } from '../../../core/company/companyConfig';
import {
  resolveContactsCollectionPath,
  resolveRoutesCollectionPath,
  resolveServicesCollectionPath,
  resolveServiceContractsCollectionPath,
  resolveServiceDocPath,
  resolveServiceTemplatesCollectionPath,
  resolveTransportsCollectionPath,
  resolveDriversCollectionPath,
  resolveCompanyLiquidationSettingsPath,
  resolveStockProductsCollectionPath,
  resolveStockProductDocPath,
  resolveStockOutboundOrdersCollectionPath,
  resolveStockOutboundOrderDocPath,
  resolveStockPurchaseLinesCollectionPath,
  resolveStockPurchaseLineDocPath,
  resolveStockPurchaseDocPath,
  resolveStockMovementsCollectionPath
} from '../../../core/company/companyPaths';
import { buildOutboundOrderHeaderSummary, buildPurchaseHeaderSummary } from '../../stock/stockFlowUtils';

import SucursalSelectionModal from './SucursalSelectionModal';
import DatePickerModal from './DatePickerModal';
import {
  Icon,
  ARGENTINA_PROVINCES,
  getLocalDateAsString,
  geocodeAddress,
  getDestinationOperationRoleLabel,
  inferDestinationOperationRole,
  normalizeDestinationOperationalFields,
  normalizeLiquidationSettings,
  normalizeOperationalLoad,
  OPERATIONAL_LOAD_OPERATION_TYPES,
  OPERATIONAL_LOAD_UNITS,
  STOCK_OPERATION_REASONS
} from './LogisticsUtils';
import { resolveTransportIdentity } from './transportIdentity';
import {
  buildDriverDisplayName,
  buildDriverSnapshot,
  buildOperationalAccessPath,
  buildTransportDisplayName,
  buildTransportMeta
} from './driverUtils';
import { buildServiceAddressText, buildServiceRouteDestinations } from '../../service/serviceRouteBridge';
import { estimateServiceCommercialMetrics } from '../../service/servicePricingEstimate';
import {
  resolveServiceBillingCadence,
  resolveBillingCadenceLabel,
  resolveWeekdayLabel,
} from '../../service/serviceBilling';

const DEFAULT_COMPANY_ID = 'default';
const DEFAULT_COMPANY_NAME = 'Empresa Base';
const ACTIVE_ROUTE_STATUSES = new Set([
  'active',
  'activa',
  'activo',
  'in_progress',
  'in-progress',
  'en_curso',
  'en-curso',
  'started',
  'pending',
  'pendiente',
  'assigned',
  'asignada',
  'asignado'
]);
const LOGISTICS_LOCKED_SERVICE_STATUSES = new Set([
  'assigned',
  'scheduled',
  'in_progress',
  'in-progress',
  'en_curso',
  'en-curso'
]);
const LOGISTICS_VISIBLE_SERVICE_STATUSES = new Set([
  'pending_logistics',
  ...LOGISTICS_LOCKED_SERVICE_STATUSES
]);
const PROVIDER_TYPE_HINTS = ['proveedor', 'corralon', 'corralón', 'distribuidor', 'mayorista', 'fabricante', 'deposito', 'depósito'];
const CLIENT_TYPE_HINTS = ['cliente', 'destino', 'obra', 'sucursal', 'entrega'];
const TRANSPORT_TYPE_HINTS = ['transporte', 'fletero', 'flete', 'correo', 'logistica', 'logística', 'mensajeria', 'mensajería'];
const DEFAULT_PROVINCE = 'CABA';
const GEOAPIFY_API_KEY = process.env.REACT_APP_GEOAPIFY_API_KEY || '';
const formatMoney = (value) =>
  new Intl.NumberFormat('es-AR', {
    style: 'currency',
    currency: 'ARS',
    maximumFractionDigits: 0
  }).format(Number(value || 0));

const WEEKDAY_ORDER = ['monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday', 'sunday'];
const WEEKDAY_INDEX_BY_KEY = WEEKDAY_ORDER.reduce((acc, day, index) => {
  acc[day] = index;
  return acc;
}, {});

const parseDateInputValue = (value = '') => {
  const [year, month, day] = String(value || '')
    .split('-')
    .map((item) => Number(item || 0));
  if (!year || !month || !day) return null;
  const parsed = new Date(year, month - 1, day, 0, 0, 0, 0);
  if (Number.isNaN(parsed.getTime())) return null;
  return parsed;
};

const toDateInputValue = (date = null) => {
  if (!(date instanceof Date) || Number.isNaN(date.getTime())) return '';
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
};

const startOfDay = (date = null) => {
  if (!(date instanceof Date) || Number.isNaN(date.getTime())) return null;
  return new Date(date.getFullYear(), date.getMonth(), date.getDate(), 0, 0, 0, 0);
};

const resolveWeekdayKeyFromDate = (date = null) => {
  if (!(date instanceof Date) || Number.isNaN(date.getTime())) return 'monday';
  const jsDay = date.getDay();
  const normalizedIndex = jsDay === 0 ? 6 : jsDay - 1;
  return WEEKDAY_ORDER[normalizedIndex] || 'monday';
};

const buildWeekDaysForDate = (value = '') => {
  const selectedDate = parseDateInputValue(value) || new Date();
  const safeDate = startOfDay(selectedDate);
  const weekdayKey = resolveWeekdayKeyFromDate(safeDate);
  const currentIndex = WEEKDAY_INDEX_BY_KEY[weekdayKey] ?? 0;

  return WEEKDAY_ORDER.map((dayKey, index) => {
    const currentDate = new Date(safeDate);
    currentDate.setDate(safeDate.getDate() - currentIndex + index);
    return {
      key: dayKey,
      label: resolveWeekdayLabel(dayKey),
      shortLabel: resolveWeekdayLabel(dayKey).slice(0, 3),
      date: currentDate,
      dateValue: toDateInputValue(currentDate),
      isSelected: index === currentIndex,
    };
  });
};

const isDateInsideRange = ({ targetDate, startDate = '', endDate = '' }) => {
  const safeTarget = startOfDay(targetDate);
  if (!safeTarget) return false;

  const safeStart = parseDateInputValue(startDate);
  const safeEnd = parseDateInputValue(endDate);

  if (safeStart && safeTarget < startOfDay(safeStart)) return false;
  if (safeEnd && safeTarget > startOfDay(safeEnd)) return false;
  return true;
};

const isTemplateScheduledForDate = (template = {}, targetDate = null) => {
  const safeTarget = startOfDay(targetDate);
  if (!safeTarget) return false;
  const configuredDays = Array.isArray(template?.serviceDaysOfWeek) ? template.serviceDaysOfWeek.filter(Boolean) : [];
  if (!isDateInsideRange({
    targetDate: safeTarget,
    startDate: template?.serviceStartDate,
    endDate: template?.serviceEndDate,
  })) {
    return false;
  }
  if (configuredDays.length === 0) return true;
  return configuredDays.includes(resolveWeekdayKeyFromDate(safeTarget));
};

const normalizeCalendarDateValue = (value = null) => {
  if (!value) return '';
  if (typeof value === 'string') {
    const trimmed = value.trim();
    if (!trimmed) return '';
    if (/^\d{4}-\d{2}-\d{2}$/.test(trimmed)) return trimmed;
    const parsed = new Date(trimmed);
    return Number.isNaN(parsed.getTime()) ? '' : toDateInputValue(parsed);
  }

  if (value instanceof Date) {
    return Number.isNaN(value.getTime()) ? '' : toDateInputValue(value);
  }

  if (typeof value?.toDate === 'function') {
    const parsed = value.toDate();
    return parsed instanceof Date && !Number.isNaN(parsed.getTime()) ? toDateInputValue(parsed) : '';
  }

  return '';
};

const SERVICE_BADGE_THEMES = [
  {
    shell: 'border-cyan-200 bg-cyan-50/90',
    text: 'text-cyan-800',
    soft: 'bg-cyan-100 text-cyan-800 border-cyan-200',
  },
  {
    shell: 'border-violet-200 bg-violet-50/90',
    text: 'text-violet-800',
    soft: 'bg-violet-100 text-violet-800 border-violet-200',
  },
  {
    shell: 'border-emerald-200 bg-emerald-50/90',
    text: 'text-emerald-800',
    soft: 'bg-emerald-100 text-emerald-800 border-emerald-200',
  },
  {
    shell: 'border-amber-200 bg-amber-50/90',
    text: 'text-amber-800',
    soft: 'bg-amber-100 text-amber-800 border-amber-200',
  },
];

const getServiceBadgeTheme = (serviceId = '') => {
  const seed = String(serviceId || '').split('').reduce((acc, char) => acc + char.charCodeAt(0), 0);
  return SERVICE_BADGE_THEMES[seed % SERVICE_BADGE_THEMES.length];
};

const normalizeRouteSelectionStatus = (value = '') =>
  String(value || '')
    .trim()
    .toLowerCase()
    .replace(/\s+/g, '_');

const DRIVER_BLOCKED_STATUSES = new Set(['in_route', 'en_ruta', 'inactive', 'inactiva']);
const TRANSPORT_BLOCKED_STATUSES = new Set(['in_route', 'en_ruta', 'service', 'fuera_de_servicio', 'inactive', 'inactiva']);

const getDriverRouteSelectionMeta = (driver = {}, selectedTransportId = '', selectedDriverId = '') => {
  if (!driver) return { disabled: false, reason: '' };
  if (selectedDriverId && driver.id === selectedDriverId) return { disabled: false, reason: '' };

  const driverStatus = normalizeRouteSelectionStatus(driver.driverStatus || driver.status || '');
  const currentTransportId = String(driver.currentTransportId || '').trim();

  if (DRIVER_BLOCKED_STATUSES.has(driverStatus)) {
    return {
      disabled: true,
      reason: driverStatus === 'in_route' || driverStatus === 'en_ruta' ? 'En ruta' : 'Inactivo'
    };
  }

  if (currentTransportId) {
    if (selectedTransportId && currentTransportId === selectedTransportId) {
      return { disabled: false, reason: '' };
    }

    return {
      disabled: true,
      reason: `Asignado a ${driver.currentTransportName || 'otra unidad'}`
    };
  }

  return { disabled: false, reason: '' };
};

const getTransportRouteSelectionMeta = (transport = {}, selectedDriverId = '', selectedTransportId = '') => {
  if (!transport) return { disabled: false, reason: '' };
  if (selectedTransportId && transport.id === selectedTransportId) return { disabled: false, reason: '' };

  const transportStatus = normalizeRouteSelectionStatus(transport.transportStatus || '');
  const currentDriverId = String(transport.currentDriverId || '').trim();

  if (TRANSPORT_BLOCKED_STATUSES.has(transportStatus)) {
    const statusReason = transportStatus === 'in_route' || transportStatus === 'en_ruta'
      ? 'En ruta'
      : transportStatus === 'service' || transportStatus === 'fuera_de_servicio'
        ? 'Fuera de servicio'
        : 'Inactiva';

    return {
      disabled: true,
      reason: statusReason
    };
  }

  if (currentDriverId) {
    if (selectedDriverId && currentDriverId === selectedDriverId) {
      return { disabled: false, reason: '' };
    }

    return {
      disabled: true,
      reason: `Asignada a ${transport.currentDriverName || 'otro chofer'}`
    };
  }

  return { disabled: false, reason: '' };
};

const EMPTY_DESTINATION = {
  name: '',
  address: '',
  apartment: '',
  locality: '',
  province: DEFAULT_PROVINCE,
  partido: '',
  postalCode: '',
  type: 'Entrega',
  contactType: 'cliente',
  contactDocId: '',
  deliveries: '',
  pickups: '',
  deliversTo: '',
  operationRole: 'mixed',
  operationalLoads: [],
  incomingOperationalLoads: [],
  priority: 'Normal',
  schedule: '',
  contactName: '',
  contactPhone: '',
  latlng: null
};

const createEmptyOperationalLoad = (destinationId = '', targetStopId = '') =>
  normalizeOperationalLoad({
    id: `load-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`,
    title: '',
    description: '',
    quantity: 1,
    unit: 'unidad',
    operationType: 'pickup',
    status: 'planned',
    category: '',
    referenceCode: '',
    originStopId: destinationId || '',
    targetStopId: targetStopId || '',
    currentStopId: null,
    requiresEvidenceOnPickup: false,
    requiresEvidenceOnDelivery: false,
    pickupEvidence: [],
    deliveryEvidence: [],
    notes: ''
  });

const INPUT_BASE_CLASS =
  'w-full rounded-2xl border border-slate-200 bg-white px-4 py-3 text-sm text-slate-800 outline-none transition focus:border-blue-400 focus:ring-4 focus:ring-blue-100';
const LABEL_CLASS =
  'mb-2 block text-sm font-medium text-slate-700';

const toInputValue = (value) => String(value || '').trim();

const normalizeLookupText = (value = '') =>
  String(value || '')
    .normalize('NFD')
    .replace(/[̀-ͯ]/g, '')
    .trim()
    .toLowerCase();

const PURCHASE_LINE_PENDING_STATUSES = new Set(['pending_pickup', 'partially_assigned', 'assigned_to_route']);
const STOCK_OUTBOUND_OPERATION_TYPES = new Set(['delivery', 'transfer', 'storage', 'return']);
const STOCK_REASON_LABELS = {
  sale: 'Venta',
  installation: 'Instalación',
  loan: 'Préstamo',
  replacement: 'Reposición',
  warranty: 'Garantía',
  return: 'Devolución',
  transfer: 'Traslado interno'
};

const getStockReasonLabel = (reason = '') =>
  STOCK_REASON_LABELS[String(reason || '').trim().toLowerCase()] || 'Salida';

const getStockReasonRouteLabel = (reason = '', suffix = 'salida') =>
  `${getStockReasonLabel(reason)} ${suffix}`.trim();

const OUTBOUND_PENDING_ROUTEABLE_STATUSES = new Set([
  'ready_for_delivery',
  'partially_assigned',
  'assigned_to_route',
  'ready_for_pickup'
]);

const isWarrantyReason = (reason = '') =>
  ['warranty', 'guarantee', 'repair'].includes(
    String(reason || '').trim().toLowerCase()
  );

const getOutboundPendingStage = (order = {}, normalizedStatus = '') => {
  const orderStatus = String(normalizedStatus || order?.status || '').trim().toLowerCase();
  if (orderStatus === 'ready_for_pickup' && isWarrantyReason(order?.operationType)) {
    return 'pickup';
  }
  return 'delivery';
};

const getOutboundPendingLineQuantity = (line = {}, stage = 'delivery') => {
  if (stage === 'pickup') {
    return Math.max(0, Number(line?.quantity || 0));
  }
  return Math.max(0, Number(line?.pendingDeliveryQuantity || 0));
};

const getPendingDeliverySummaryReason = (orders = []) => {
  const stages = Array.from(
    new Set(
      (Array.isArray(orders) ? orders : [])
        .map((order) =>
          getOutboundPendingStage(
            order,
            normalizeLookupText(order?.status || buildOutboundOrderHeaderSummary(order, order?.lines || []).status || '')
          )
        )
        .filter(Boolean)
    )
  );

  if (stages.length === 1 && stages[0] === 'pickup') {
    return 'Retiro garantía';
  }

  const reasons = Array.from(
    new Set(
      (Array.isArray(orders) ? orders : [])
        .map((order) => String(order?.operationType || '').trim().toLowerCase())
        .filter(Boolean)
    )
  );

  if (reasons.length === 1) return getStockReasonRouteLabel(reasons[0]);
  if (reasons.length > 1) return 'Salidas mixtas';
  return 'Pendiente de salida';
};

const getStockProductAvailableOutboundQty = (product = {}) => {
  const buckets = product?.stockBuckets || {};
  return Math.max(0, Number(buckets.physicalQty || 0) - Number(buckets.pendingOutboundQty || 0));
};

const buildOperationalLoadFromPurchaseLine = (line = {}, supplier = {}, quantityOverride = null) =>
  normalizeOperationalLoad({
    id: `purchase-load-${line.id || Date.now()}-${Math.random().toString(36).slice(2, 7)}`,
    title: String(line.productNameSnapshot || line.productName || 'Producto pendiente'),
    description: `Retiro pendiente desde ${supplier?.name || line.supplierNameSnapshot || 'proveedor'}`,
    quantity: Math.max(1, Number(quantityOverride ?? line.pendingPickupQuantity ?? line.orderedQuantity ?? 1) || 1),
    unit: String(line.unitSnapshot || 'unidad'),
    category: String(line.categorySnapshot || ''),
    referenceCode: String(line.purchaseNumberSnapshot || line.purchaseId || line.id || ''),
    operationType: 'pickup',
    status: 'pending_pickup',
    purchaseId: String(line.purchaseId || ''),
    purchaseLineId: String(line.id || ''),
    purchaseNumber: String(line.purchaseNumberSnapshot || ''),
    supplierId: String(supplier?.id || line.supplierId || ''),
    supplierNameSnapshot: String(supplier?.name || line.supplierNameSnapshot || ''),
    stockSourceType: 'purchase',
    stockMode: 'pending_inbound',
    requiresEvidenceOnPickup: false,
    requiresEvidenceOnDelivery: false,
    notes: 'Carga generada automáticamente desde pendiente de retiro de stock.'
  });

const buildOperationalLoadFromOutboundOrderLine = (
  order = {},
  line = {},
  destination = {},
  quantityOverride = null
) =>
  (() => {
    const normalizedReason =
      String(order.operationType || 'sale').trim().toLowerCase() || 'sale';
    const pendingStage = getOutboundPendingStage(
      order,
      normalizeLookupText(order?.status || '')
    );
    const isWarrantyPickup = pendingStage === 'pickup' && isWarrantyReason(normalizedReason);
    const quantity = Math.max(
      1,
      Number(
        quantityOverride
          ?? line.routePendingQuantity
          ?? line.pendingDeliveryQuantity
          ?? line.quantity
          ?? 1
      ) || 1
    );
    const routeLabel = isWarrantyPickup
      ? 'Retiro garantía'
      : getStockReasonRouteLabel(normalizedReason);

    return normalizeOperationalLoad({
      id: `outbound-load-${order.id || Date.now()}-${line.id || Math.random().toString(36).slice(2, 7)}`,
      title: String(line.productNameSnapshot || 'Producto preparado'),
      description: isWarrantyPickup
        ? `${routeLabel} · pedido ${order.orderNumber || order.id || ''} desde ${destination?.name || order?.contactNameSnapshot || 'garantía'}`
        : `${routeLabel} · pedido ${order.orderNumber || order.id || ''} hacia ${destination?.name || order?.contactNameSnapshot || 'destino'}`,
      quantity,
      unit: String(line.unitSnapshot || 'unidad'),
      category: String(line.categorySnapshot || ''),
      referenceCode: String(order.reference || order.orderNumber || line.skuSnapshot || line.id || ''),
      operationType: isWarrantyPickup ? 'pickup' : 'delivery',
      status: isWarrantyPickup ? 'pending_pickup' : 'planned',
      stockSourceType: 'outbound_order',
      stockMode: isWarrantyPickup ? 'pending_warranty_pickup' : 'pending_outbound',
      stockPendingStage: pendingStage,
      stockProductId: String(line.productId || ''),
      stockProductNameSnapshot: String(line.productNameSnapshot || ''),
      stockSkuSnapshot: String(line.skuSnapshot || ''),
      stockBarcodeSnapshot: String(line.barcodeSnapshot || ''),
      stockQrCodeSnapshot: String(line.qrCodeSnapshot || ''),
      stockCategorySnapshot: String(line.categorySnapshot || ''),
      stockUnitSnapshot: String(line.unitSnapshot || 'unidad'),
      stockOperationReason: normalizedReason,
      stockWarrantyOutcome: String(order.warrantyOutcome || line.warrantyOutcome || '').trim().toLowerCase() || null,
      stockOutboundOrderId: String(order.id || ''),
      stockOutboundOrderNumber: String(order.orderNumber || ''),
      stockOutboundOrderLineId: String(line.id || ''),
      purchaseId: String(line.sourcePurchaseId || line.purchaseId || ''),
      purchaseLineId: String(line.sourcePurchaseLineId || line.purchaseLineId || ''),
      purchaseNumber: String(line.sourcePurchaseNumberSnapshot || line.purchaseNumberSnapshot || line.purchaseId || ''),
      batchLabelSnapshot: String(line.sourceBatchLabelSnapshot || line.batchLabelSnapshot || ''),
      costUnitSnapshot:
        line.unitCost == null || line.unitCost === ''
          ? null
          : Number(line.unitCost),
      costTotalSnapshot:
        line.totalCost == null || line.totalCost === ''
          ? null
          : Number(line.totalCost),
      saleUnitPrice: Number(line.saleUnitPrice || 0),
      saleTotalSnapshot:
        line.totalSale == null || line.totalSale === ''
          ? null
          : Number(line.totalSale),
      marginSnapshot:
        line.margin == null || line.margin === ''
          ? null
          : Number(line.margin),
      stockOrderReference: String(order.reference || ''),
      requiresEvidenceOnPickup: false,
      requiresEvidenceOnDelivery: false,
      notes: isWarrantyPickup
        ? 'Carga generada automáticamente desde retiro de garantía aprobado.'
        : `Carga generada automáticamente desde ${getStockReasonRouteLabel(normalizedReason).toLowerCase()} lista para entrega.`
    });
  })();


const shouldUseProvinceInAddressSearch = (values = {}, options = {}) => {
  const province = toInputValue(values.province);
  if (!province) return false;
  if (options.provinceExplicitlySet) return true;
  return province !== DEFAULT_PROVINCE;
};

const buildAutocompleteSearchText = (values = {}, options = {}) => {
  const baseAddress = toInputValue(values.address);
  const locality = toInputValue(values.locality);
  const partido = toInputValue(values.partido);
  const province = shouldUseProvinceInAddressSearch(values, options)
    ? toInputValue(values.province)
    : '';

  return [
    baseAddress,
    locality,
    partido,
    province,
    'Argentina'
  ]
    .filter(Boolean)
    .join(', ')
    .trim();
};

const composeDestinationAddress = (values = {}, options = {}) => {
  const province = shouldUseProvinceInAddressSearch(values, options)
    ? toInputValue(values.province)
    : '';

  return [
    toInputValue(values.address),
    toInputValue(values.locality),
    toInputValue(values.partido),
    province,
    toInputValue(values.postalCode),
    'Argentina'
  ]
    .filter(Boolean)
    .join(', ')
    .trim();
};

const getOperationalLoadTargetLabel = (targetStopId, destinations = [], fallbackTargetId = '') => {
  const resolvedTargetId = targetStopId || fallbackTargetId || '';

  if (!resolvedTargetId) return 'A definir';
  if (resolvedTargetId === 'oficina') return 'Oficina';
  if (resolvedTargetId === 'pending') return 'Pendiente';

  return destinations.find((destination) => destination.id === resolvedTargetId)?.name || 'A definir';
};

const inferDestinationTypeFromLoads = (loads = [], fallbackType = 'Entrega') => {
  const normalizedLoads = Array.isArray(loads) ? loads : [];
  const hasPickupLike = normalizedLoads.some((load) =>
    ['pickup', 'return', 'transfer', 'storage'].includes(String(load?.operationType || '').trim().toLowerCase())
  );
  const hasDelivery = normalizedLoads.some(
    (load) => String(load?.operationType || '').trim().toLowerCase() === 'delivery'
  );

  if (hasPickupLike && hasDelivery) return 'Entrega y Retiro';
  if (hasPickupLike) return 'Retiro';
  if (hasDelivery) return 'Entrega';

  return fallbackType;
};

const buildIncomingOperationalLoadReference = (load = {}, sourceDestination = {}) => ({
  loadId: load?.id || '',
  sourceDestinationId: sourceDestination?.id || '',
  sourceDestinationName: sourceDestination?.name || '',
  title: String(
    load?.title ||
      load?.productNameSnapshot ||
      load?.stockProductNameSnapshot ||
      load?.productName ||
      ''
  ).trim(),
  quantity: Number(load?.quantity ?? 1) || 1,
  unit: String(load?.unit || 'unidad').trim() || 'unidad',
  operationType: String(load?.operationType || '').trim() || 'pickup',
  productNameSnapshot: String(
    load?.productNameSnapshot ||
      load?.stockProductNameSnapshot ||
      load?.productName ||
      load?.title ||
      ''
  ).trim(),
  skuSnapshot: String(load?.skuSnapshot || load?.stockSkuSnapshot || '').trim(),
  categorySnapshot: String(load?.categorySnapshot || load?.stockCategorySnapshot || load?.category || '').trim()
});

const getOperationalLoadsMemoryKey = (companyId = 'default') =>
  `g-centra-operational-loads:${companyId}`;

const readOperationalLoadsMemory = (companyId = 'default') => {
  if (typeof window === 'undefined') return [];

  try {
    const raw = window.localStorage.getItem(getOperationalLoadsMemoryKey(companyId));
    const parsed = raw ? JSON.parse(raw) : [];
    return Array.isArray(parsed) ? parsed.filter(Boolean).slice(0, 30) : [];
  } catch (error) {
    console.warn('No se pudo leer memoria local de cargas operativas.', error);
    return [];
  }
};

const writeOperationalLoadsMemory = (companyId = 'default', items = []) => {
  if (typeof window === 'undefined') return;

  try {
    window.localStorage.setItem(
      getOperationalLoadsMemoryKey(companyId),
      JSON.stringify(items.filter(Boolean).slice(0, 30))
    );
  } catch (error) {
    console.warn('No se pudo guardar memoria local de cargas operativas.', error);
  }
};

const looksSpecificEnoughForFallbackGeocode = (values = {}) => {
  const address = toInputValue(values.address);
  const locality = toInputValue(values.locality);
  const partido = toInputValue(values.partido);
  const postalCode = toInputValue(values.postalCode);

  if (address.length < 6) return false;

  const hasStreetNumber = /\d/.test(address);
  const hasTwoWords = address.split(/\s+/).filter(Boolean).length >= 2;
  const hasContext = Boolean(locality || partido || postalCode);

  return (hasStreetNumber && hasTwoWords) || (hasTwoWords && hasContext);
};

const normalizeAddressValidationState = ({
  status = GEOAPIFY_API_KEY ? 'idle' : 'missing-key',
  text,
  validatedAddress = ''
} = {}) => ({
  status,
  text:
    text ||
    (GEOAPIFY_API_KEY
      ? 'Escribí la dirección y elegí una sugerencia para dejar validado el destino.'
      : 'Podés validar manualmente la dirección o cargar coordenadas si el punto no aparece.'),
  validatedAddress
});

const normalizeNumber = (value, fallback = 0) => {
  if (typeof value === 'number') {
    return Number.isFinite(value) ? value : fallback;
  }

  if (typeof value === 'string') {
    const normalizedText = value.trim();
    if (!normalizedText) return fallback;

    const sanitized = normalizedText
      .replace(/\s+/g, '')
      .replace(/\.(?=\d{3}(?:\D|$))/g, '')
      .replace(',', '.');

    const parsed = Number(sanitized);
    return Number.isFinite(parsed) ? parsed : fallback;
  }

  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : fallback;
};

const formatContactTypeLabel = (value = '') =>
  String(value || '')
    .trim()
    .replace(/-/g, ' ')
    .replace(/\b\w/g, (char) => char.toUpperCase());

const resolveContactTypeFamily = (value = '') => {
  const normalized = normalizeLookupText(String(value || '').replace(/-/g, ' '));
  if (!normalized) return 'cliente';
  if (normalized === 'cliente' || normalized === 'proveedor' || normalized === 'transporte') return normalized;
  if (PROVIDER_TYPE_HINTS.some((hint) => normalized.includes(normalizeLookupText(hint)))) return 'proveedor';
  if (TRANSPORT_TYPE_HINTS.some((hint) => normalized.includes(normalizeLookupText(hint)))) return 'transporte';
  if (CLIENT_TYPE_HINTS.some((hint) => normalized.includes(normalizeLookupText(hint)))) return 'cliente';
  return normalized;
};

const getContactTypeToneClass = (value = '') => {
  const family = resolveContactTypeFamily(value);
  const tones = {
    cliente: 'border-blue-200 bg-blue-50 text-blue-700',
    proveedor: 'border-violet-200 bg-violet-50 text-violet-700',
    transporte: 'border-emerald-200 bg-emerald-50 text-emerald-700'
  };

  return tones[family] || 'border-slate-200 bg-slate-100 text-slate-700';
};

const normalizeTransportType = (value) =>
  (value || '').toString().trim().toLowerCase();

const normalizeCostScales = (costScales = []) => {
  if (!Array.isArray(costScales)) return [];

  return costScales.map((scale) => ({
    from: normalizeNumber(scale?.from, 0),
    to:
      scale?.to === null || scale?.to === undefined || scale?.to === ''
        ? null
        : normalizeNumber(scale?.to, 0),
    price: normalizeNumber(scale?.price, 0)
  }));
};

const normalizeLiquidationConfig = (config = {}, fallback = {}) => ({
  costModel:
    config?.costModel === 'byScale'
      ? 'byScale'
      : fallback?.costModel === 'byScale'
      ? 'byScale'
      : 'perKm',
  pricePerKm: normalizeNumber(
    config?.pricePerKm,
    normalizeNumber(fallback?.pricePerKm, 0)
  ),
  baseRouteFee: normalizeNumber(
    config?.baseRouteFee,
    normalizeNumber(fallback?.baseRouteFee, 0)
  ),
  minimumRouteFee: normalizeNumber(
    config?.minimumRouteFee,
    normalizeNumber(fallback?.minimumRouteFee, 0)
  ),
  costScales: normalizeCostScales(
    Array.isArray(config?.costScales) ? config.costScales : fallback?.costScales || []
  )
});

const buildLiquidationSnapshot = ({ transport, liquidationSettingsDoc }) => {
  const normalizedSettings = normalizeLiquidationSettings({
    liquidationSettings: liquidationSettingsDoc || {}
  });

  const transportType = normalizeTransportType(
    transport?.liquidationTransportType || transport?.transportType || transport?.transport
  );

  const typeConfig =
    transportType && normalizedSettings?.transports?.[transportType]
      ? normalizedSettings.transports[transportType]
      : null;

  const hasCustomOverride =
    transport?.liquidation?.mode === 'custom' &&
    transport?.liquidation?.customSettings;

  const finalConfig = hasCustomOverride
    ? normalizeLiquidationConfig(
        transport.liquidation.customSettings,
        typeConfig || {
          costModel: normalizedSettings.defaultCostModel,
          pricePerKm: normalizedSettings.defaultPricePerKm,
          baseRouteFee: 0,
          minimumRouteFee: 0,
          costScales: []
        }
      )
    : normalizeLiquidationConfig(
        typeConfig || {},
        {
          costModel: normalizedSettings.defaultCostModel,
          pricePerKm: normalizedSettings.defaultPricePerKm,
          baseRouteFee: 0,
          minimumRouteFee: 0,
          costScales: []
        }
      );

  return {
    source: hasCustomOverride
      ? 'transportOverride'
      : typeConfig
      ? 'transportType'
      : 'legacyFallback',
    mode: hasCustomOverride ? 'custom' : 'inherit',
    transportType,
    transportId: transport?.id || '',
    transportSlug: transport?.slug || '',
    transportName: transport?.name || '',
    ...finalConfig,
    savedAt: new Date().toISOString()
  };
};

const isRouteOperationallyActive = (route = {}) => {
  if (!route || typeof route !== 'object') return false;
  if (route.active === true || route.isActive === true) return true;

  const normalizedStatus = String(route.status || route.state || '')
    .trim()
    .toLowerCase();

  return ACTIVE_ROUTE_STATUSES.has(normalizedStatus);
};

const SectionBadge = ({ children, tone = 'slate' }) => {
  const tones = {
    slate: 'border-slate-200 bg-slate-50 text-slate-700',
    cyan: 'border-cyan-200 bg-cyan-50 text-cyan-700',
    blue: 'border-blue-200 bg-blue-50 text-blue-700',
    emerald: 'border-emerald-200 bg-emerald-50 text-emerald-700',
    violet: 'border-violet-200 bg-violet-50 text-violet-700'
  };

  return (
    <span
      className={`inline-flex items-center gap-2 rounded-full border px-3 py-1 text-xs font-semibold ${tones[tone] || tones.slate}`}
    >
      {children}
    </span>
  );
};

const PanelHeader = ({ eyebrow, title, description, extra }) => (
  <div className="mb-5 flex flex-col gap-4 md:flex-row md:items-start md:justify-between">
    <div className="max-w-2xl">
      <p className="text-[11px] font-semibold uppercase tracking-[0.28em] text-slate-500">
        {eyebrow}
      </p>
      <h3 className="mt-2 text-2xl font-bold tracking-[-0.03em] text-slate-900">
        {title}
      </h3>
      {description && (
        <p className="mt-2 text-sm leading-relaxed text-slate-500">{description}</p>
      )}
    </div>

    {extra ? <div className="flex flex-wrap items-center gap-2">{extra}</div> : null}
  </div>
);

const RouteFlowSection = ({
  id,
  step,
  title,
  description,
  tone = 'slate',
  expanded = false,
  onToggle,
  badge = null,
  recommended = false,
  children,
  maxHeightClassName = 'max-h-[2600px]',
}) => {
  const tones = {
    slate: {
      shell: 'border-slate-200 bg-white/95',
      icon: 'border-slate-200 bg-slate-100 text-slate-700',
      eyebrow: 'text-slate-500',
      title: 'text-slate-900',
      description: 'text-slate-500',
      button: 'border-slate-200 bg-white text-slate-600',
      content: 'border-slate-200',
    },
    indigo: {
      shell: 'border-indigo-200 bg-[linear-gradient(180deg,#eef2ff_0%,#ffffff_100%)]',
      icon: 'border-indigo-200 bg-indigo-100 text-indigo-700',
      eyebrow: 'text-indigo-600',
      title: 'text-slate-900',
      description: 'text-slate-600',
      button: 'border-indigo-200 bg-white text-indigo-700',
      content: 'border-indigo-100',
    },
    cyan: {
      shell: 'border-cyan-200 bg-[linear-gradient(180deg,#ecfeff_0%,#ffffff_100%)]',
      icon: 'border-cyan-200 bg-cyan-100 text-cyan-700',
      eyebrow: 'text-cyan-600',
      title: 'text-slate-900',
      description: 'text-slate-600',
      button: 'border-cyan-200 bg-white text-cyan-700',
      content: 'border-cyan-100',
    },
    violet: {
      shell: 'border-violet-200 bg-[linear-gradient(180deg,#f5f3ff_0%,#ffffff_100%)]',
      icon: 'border-violet-200 bg-violet-100 text-violet-700',
      eyebrow: 'text-violet-600',
      title: 'text-slate-900',
      description: 'text-slate-600',
      button: 'border-violet-200 bg-white text-violet-700',
      content: 'border-violet-100',
    },
    emerald: {
      shell: 'border-emerald-200 bg-[linear-gradient(180deg,#ecfdf5_0%,#ffffff_100%)]',
      icon: 'border-emerald-200 bg-emerald-100 text-emerald-700',
      eyebrow: 'text-emerald-600',
      title: 'text-slate-900',
      description: 'text-slate-600',
      button: 'border-emerald-200 bg-white text-emerald-700',
      content: 'border-emerald-100',
    },
    amber: {
      shell: 'border-amber-200 bg-[linear-gradient(180deg,#fff7ed_0%,#ffffff_100%)]',
      icon: 'border-amber-200 bg-amber-100 text-amber-700',
      eyebrow: 'text-amber-600',
      title: 'text-slate-900',
      description: 'text-slate-600',
      button: 'border-amber-200 bg-white text-amber-700',
      content: 'border-amber-100',
    },
  };

  const theme = tones[tone] || tones.slate;

  return (
    <div id={id} className={`rounded-[24px] border shadow-[0_12px_30px_rgba(15,23,42,0.05)] ${theme.shell}`}>
      <button
        type="button"
        onClick={onToggle}
        className="flex w-full items-start justify-between gap-4 px-4 py-4 text-left"
      >
        <div className="flex min-w-0 flex-1 items-start gap-3">
          <span
            className={`inline-flex h-11 w-11 shrink-0 items-center justify-center rounded-2xl border text-sm font-black shadow-sm ${theme.icon}`}
          >
            {step}
          </span>
          <div className="min-w-0 flex-1">
            <div className="flex flex-wrap items-center gap-2">
              <p className={`text-[11px] font-semibold uppercase tracking-[0.22em] ${theme.eyebrow}`}>
                Estación operativa
              </p>
              {recommended ? (
                <span className="rounded-full border border-emerald-200 bg-emerald-50 px-2.5 py-1 text-[10px] font-black uppercase tracking-[0.18em] text-emerald-700">
                  Paso sugerido
                </span>
              ) : null}
              {badge}
            </div>
            <h4 className={`mt-1 text-base font-bold ${theme.title}`}>{title}</h4>
            {description ? (
              <p className={`mt-1 text-sm leading-5 ${theme.description}`}>{description}</p>
            ) : null}
          </div>
        </div>

        <span
          className={`inline-flex h-10 w-10 shrink-0 items-center justify-center rounded-2xl border transition ${theme.button}`}
          aria-hidden="true"
        >
          <Icon
            path={expanded ? 'M19.5 12h-15' : 'M12 4.5v15m7.5-7.5h-15'}
            className="h-4 w-4"
          />
        </span>
      </button>

      <div
        className={`overflow-hidden transition-all duration-500 ease-out ${
          expanded ? `${maxHeightClassName} opacity-100` : 'max-h-0 opacity-0'
        }`}
      >
        <div className={`border-t px-4 pb-4 pt-4 ${theme.content}`}>{children}</div>
      </div>
    </div>
  );
};

const RouteCreator = ({
  userId,
  showMessage,
  addLogisticsNotification,
  onBack,
  onCreateSuccess,
  selectedTransport,
  companyId: companyIdProp,
  companyName: companyNameProp,
  planLabel = 'Plan actual',
  onStartTutorial,
  tutorialSeen = false,
  tutorialActive = false,
  tutorialFocusTarget = null
}) => {
  const resolvedCompanyId =
    companyIdProp ||
    getActiveCompanyId?.() ||
    selectedTransport?.companyId ||
    DEFAULT_COMPANY_ID;

  const resolvedCompanyName =
    companyNameProp ||
    selectedTransport?.companyName ||
    DEFAULT_COMPANY_NAME;

  const [companyLiquidationSettings, setCompanyLiquidationSettings] = useState(null);
  const [availableTransports, setAvailableTransports] = useState([]);
  const [availableDrivers, setAvailableDrivers] = useState([]);
  const [selectedTransportId, setSelectedTransportId] = useState(selectedTransport?.id || '');
  const [selectedDriverId, setSelectedDriverId] = useState(selectedTransport?.currentDriverId || selectedTransport?.driverId || '');

  const initialIdentity = resolveTransportIdentity(selectedTransport || {});

  const [newRoute, setNewRoute] = useState({
    companyId: resolvedCompanyId,
    companyName: resolvedCompanyName,
    transport: selectedTransport?.transportType || '',
    transportType: selectedTransport?.transportType || '',
    transporterId: selectedTransport?.id || '',
    transporterSlug: selectedTransport?.slug || '',
    transporterName: selectedTransport?.name || '',
    driverId: selectedTransport?.currentDriverId || selectedTransport?.driverId || '',
    driverName: selectedTransport?.currentDriverName || selectedTransport?.driverName || '',
    driverLastName: selectedTransport?.currentDriverLastName || selectedTransport?.driverLastName || '',
    driverPhone: selectedTransport?.currentDriverPhone || selectedTransport?.phone || '',
    driverDni: selectedTransport?.currentDriverDni || selectedTransport?.driverDni || '',
    driverPhotoURL: selectedTransport?.currentDriverPhotoURL || selectedTransport?.photoURL || '',
    identityColor: initialIdentity.identityColor || '',
    identitySoftColor: initialIdentity.identitySoftColor || '',
    date: getLocalDateAsString(new Date()),
    destinations: []
  });

  const [newDestination, setNewDestination] = useState(EMPTY_DESTINATION);
  const [editingDestinationId, setEditingDestinationId] = useState(null);
  const [routePanelExpanded, setRoutePanelExpanded] = useState(false);
  const [assignmentSectionExpanded, setAssignmentSectionExpanded] = useState(false);
  const [previewPanelExpanded, setPreviewPanelExpanded] = useState(true);
  const [pendingServicesExpanded, setPendingServicesExpanded] = useState(false);
  const [destinationSectionExpanded, setDestinationSectionExpanded] = useState(false);
  const [destinationDetailsExpanded, setDestinationDetailsExpanded] = useState(false);
  const [operationalLoadsExpanded, setOperationalLoadsExpanded] = useState(false);
  const [operationalLoadMemory, setOperationalLoadMemory] = useState([]);
  const [provinceExplicitlySet, setProvinceExplicitlySet] = useState(false);
  const [isDraggingOver, setIsDraggingOver] = useState(false);
  const [showSucursalModal, setShowSucursalModal] = useState(false);
  const [contactForSucursal, setContactForSucursal] = useState(null);
  const [addressGeocodingError, setAddressGeocodingError] = useState('');
  const [showDatePickerModal, setShowDatePickerModal] = useState(false);

  const [searchTerm, setSearchTerm] = useState('');
  const [filteredContacts, setFilteredContacts] = useState([]);
  const [showContactSuggestions, setShowContactSuggestions] = useState(false);
  const [allContacts, setAllContacts] = useState([]);
  const [availableContracts, setAvailableContracts] = useState([]);
  const [serviceTemplates, setServiceTemplates] = useState([]);
  const [pendingServices, setPendingServices] = useState([]);
  const [activatingTemplateId, setActivatingTemplateId] = useState('');
  const [showScheduledTemplatesPanel, setShowScheduledTemplatesPanel] = useState(false);
  const [showProgrammedExceptions, setShowProgrammedExceptions] = useState(false);
  const [showTakenPendingServices, setShowTakenPendingServices] = useState(false);
  const [stockProducts, setStockProducts] = useState([]);
  const [stockOutboundOrders, setStockOutboundOrders] = useState([]);
  const [pendingPurchaseLines, setPendingPurchaseLines] = useState([]);
  const [selectedPendingPurchaseLineIds, setSelectedPendingPurchaseLineIds] = useState([]);
  const [selectedPendingDeliveryLineIds, setSelectedPendingDeliveryLineIds] = useState([]);
  const [selectedPendingDeliveryContext, setSelectedPendingDeliveryContext] = useState(null);
  const [addressValidation, setAddressValidation] = useState(() =>
    normalizeAddressValidationState()
  );
  const [addressSuggestions, setAddressSuggestions] = useState([]);
  const [loadingAddressSuggestions, setLoadingAddressSuggestions] = useState(false);
  const [showAddressSuggestions, setShowAddressSuggestions] = useState(false);
  const [showManualCoordinates, setShowManualCoordinates] = useState(false);
  const [manualLat, setManualLat] = useState('');
  const [manualLng, setManualLng] = useState('');

  const addressAutocompleteRef = useRef(null);
  const contactAutocompleteRef = useRef(null);
  const debounceRef = useRef(null);
  const abortRef = useRef(null);

  const destinationAddressForAutocomplete = useMemo(
    () =>
      buildAutocompleteSearchText(newDestination, {
        provinceExplicitlySet
      }),
    [newDestination, provinceExplicitlySet]
  );
  const fullDestinationAddress = useMemo(
    () =>
      composeDestinationAddress(newDestination, {
        provinceExplicitlySet
      }),
    [newDestination, provinceExplicitlySet]
  );
  const hasAutocompleteContext = useMemo(
    () =>
      Boolean(
        toInputValue(newDestination.locality) ||
          toInputValue(newDestination.partido) ||
          (provinceExplicitlySet && toInputValue(newDestination.province))
      ),
    [newDestination.locality, newDestination.partido, newDestination.province, provinceExplicitlySet]
  );
  const destinationGuidedField = useMemo(() => {
    if (!toInputValue(searchTerm || newDestination.name)) return 'name';
    if (!toInputValue(newDestination.address)) return 'address';
    if (!toInputValue(newDestination.locality)) return 'locality';
    if (!provinceExplicitlySet) return 'province';
    if (newDestination.province === 'Buenos Aires' && !toInputValue(newDestination.partido)) {
      return 'partido';
    }
    return null;
  }, [
    searchTerm,
    newDestination.name,
    newDestination.address,
    newDestination.locality,
    newDestination.province,
    newDestination.partido,
    provinceExplicitlySet
  ]);
  const hasStartedDestinationForm = useMemo(
    () =>
      Boolean(
        editingDestinationId ||
          toInputValue(searchTerm || newDestination.name) ||
          toInputValue(newDestination.address) ||
          toInputValue(newDestination.locality) ||
          toInputValue(newDestination.partido) ||
          newDestination.latlng
      ),
    [
      editingDestinationId,
      searchTerm,
      newDestination.name,
      newDestination.address,
      newDestination.locality,
      newDestination.partido,
      newDestination.latlng,
    ]
  );

  const virtualPendingDeliveryContacts = useMemo(() => {
    const mapped = [];
    const seen = new Set();
    stockOutboundOrders.forEach((order) => {
      const summary = buildOutboundOrderHeaderSummary(order, order?.lines || []);
      const normalizedStatus = normalizeLookupText(summary.status || order?.status || '');
      if (!OUTBOUND_PENDING_ROUTEABLE_STATUSES.has(normalizedStatus)) return;
      const name = String(order?.contactNameSnapshot || order?.destinationNameSnapshot || '').trim();
      if (!name) return;
      const key = `${order.contactId || 'virtual'}:${normalizeLookupText(name)}`;
      if (seen.has(key)) return;
      seen.add(key);
      const snapshot = order.destinationSnapshot || {};
      mapped.push({
        id: order.contactId || `virtual-outbound-${normalizeLookupText(name)}`,
        name,
        type: 'cliente',
        addresses: [
          {
            address: order.destinationAddressSnapshot || snapshot.address || '',
            apartment: snapshot.apartment || '',
            locality: snapshot.locality || '',
            province: snapshot.province || DEFAULT_PROVINCE,
            partido: snapshot.partido || '',
            postalCode: snapshot.postalCode || '',
            latlng: snapshot.latlng || null,
          },
        ],
        contacts: [],
        _virtualPendingDelivery: true,
      });
    });
    return mapped;
  }, [stockOutboundOrders]);

  const virtualPendingPurchaseContacts = useMemo(() => {
    const mapped = [];
    const seen = new Set();
    pendingPurchaseLines.forEach((line) => {
      const pendingQty = Math.max(0, Number(line?.pendingPickupQuantity || 0));
      const normalizedStatus = normalizeLookupText(line?.status || '');
      if (!pendingQty || !PURCHASE_LINE_PENDING_STATUSES.has(normalizedStatus)) return;

      const supplierName = String(line?.supplierNameSnapshot || '').trim();
      if (!supplierName) return;

      const supplierId = String(line?.supplierId || '').trim();
      const key = `${supplierId || 'virtual'}:${normalizeLookupText(supplierName)}`;
      if (seen.has(key)) return;
      seen.add(key);

      mapped.push({
        id: supplierId || `virtual-purchase-${normalizeLookupText(supplierName)}`,
        name: supplierName,
        type: 'proveedor',
        addresses: [],
        contacts: [],
        _virtualPendingPurchase: true,
      });
    });
    return mapped;
  }, [pendingPurchaseLines]);

  const lookupContacts = useMemo(() => {
    const merged = [...allContacts];
    const existingNames = new Set(
      merged.map((contact) => normalizeLookupText(contact?.name || '')).filter(Boolean)
    );
    virtualPendingPurchaseContacts.forEach((contact) => {
      const normalizedName = normalizeLookupText(contact?.name || '');
      if (!normalizedName || existingNames.has(normalizedName)) return;
      merged.push(contact);
      existingNames.add(normalizedName);
    });
    virtualPendingDeliveryContacts.forEach((contact) => {
      const normalizedName = normalizeLookupText(contact?.name || '');
      if (!normalizedName || existingNames.has(normalizedName)) return;
      merged.push(contact);
      existingNames.add(normalizedName);
    });
    return merged;
  }, [allContacts, virtualPendingDeliveryContacts, virtualPendingPurchaseContacts]);
  const getGuidedFieldClass = (fieldName, extra = '') =>
    `${INPUT_BASE_CLASS} ${extra} ${
      destinationGuidedField === fieldName
        ? 'border-amber-300 bg-amber-50/70 ring-4 ring-amber-100 shadow-[0_0_0_1px_rgba(251,191,36,0.28),0_0_22px_rgba(251,191,36,0.14)] animate-pulse'
        : ''
    }`.trim();
  const getGuidedLabelClass = (fieldName) =>
    `${LABEL_CLASS} ${
      destinationGuidedField === fieldName ? 'text-amber-700' : ''
    }`.trim();
  const renderGuidedHint = (fieldName, text) =>
    destinationGuidedField === fieldName ? (
      <div className="mb-2 inline-flex items-center gap-2 rounded-full border border-amber-200 bg-[linear-gradient(180deg,#fff8db_0%,#fffdf5_100%)] px-3 py-1 text-[11px] font-semibold text-amber-700 shadow-[0_8px_20px_rgba(251,191,36,0.14)]">
        <span className="inline-flex h-1.5 w-1.5 rounded-full bg-amber-400 animate-pulse" />
        {text}
      </div>
    ) : null;
  const selectedContactRecord = useMemo(() => {
    if (newDestination.contactDocId) {
      const contactById = lookupContacts.find((contact) => contact.id === newDestination.contactDocId);
      if (contactById) return contactById;
    }

    const normalizedName = String(newDestination.name || '').trim().toLowerCase();
    const normalizedType = String(newDestination.contactType || '').trim().toLowerCase();

    if (!normalizedName) return null;

    const exactMatch = lookupContacts.find(
      (contact) =>
        String(contact?.name || '').trim().toLowerCase() === normalizedName &&
        String(contact?.type || '').trim().toLowerCase() === normalizedType
    );

    if (exactMatch) return exactMatch;

    return (
      lookupContacts.find(
        (contact) => String(contact?.name || '').trim().toLowerCase() === normalizedName
      ) || null
    );
  }, [lookupContacts, newDestination.contactDocId, newDestination.contactType, newDestination.name]);

  const supplierPendingSummaryByKey = useMemo(() => {
    return pendingPurchaseLines.reduce((acc, line) => {
      const pendingQty = Math.max(0, Number(line?.pendingPickupQuantity || 0));
      const normalizedStatus = normalizeLookupText(line?.status || '');
      if (!pendingQty || !PURCHASE_LINE_PENDING_STATUSES.has(normalizedStatus)) return acc;

      const keys = [
        line?.supplierId ? `id:${line.supplierId}` : '',
        line?.supplierNameSnapshot ? `name:${normalizeLookupText(line.supplierNameSnapshot)}` : ''
      ].filter(Boolean);

      keys.forEach((key) => {
        if (!acc[key]) {
          acc[key] = { lines: [], totalUnits: 0 };
        }
        acc[key].lines.push(line);
        acc[key].totalUnits += pendingQty;
      });
      return acc;
    }, {});
  }, [pendingPurchaseLines]);

  const selectedSupplierPendingLines = useMemo(() => {
    if (!selectedContactRecord) return [];
    const byId = selectedContactRecord.id ? supplierPendingSummaryByKey[`id:${selectedContactRecord.id}`] : null;
    if (byId?.lines?.length) return byId.lines;
    const byName = supplierPendingSummaryByKey[`name:${normalizeLookupText(selectedContactRecord.name)}`];
    return byName?.lines || [];
  }, [selectedContactRecord, supplierPendingSummaryByKey]);

  const supplierPendingContacts = useMemo(() => {
    return lookupContacts
      .map((contact) => {
        const summary =
          (contact?.id && supplierPendingSummaryByKey[`id:${contact.id}`]) ||
          supplierPendingSummaryByKey[`name:${normalizeLookupText(contact?.name)}`] ||
          null;

        if (!summary?.lines?.length) return null;

        return {
          contact,
          lines: summary.lines,
          totalUnits: Number(summary.totalUnits || 0),
        };
      })
      .filter(Boolean)
      .filter(
        (item, index, arr) =>
          arr.findIndex(
            (candidate) =>
              normalizeLookupText(candidate.contact?.name) === normalizeLookupText(item.contact?.name) &&
              String(candidate.contact?.type || '').trim().toLowerCase() ===
                String(item.contact?.type || '').trim().toLowerCase()
          ) === index
      )
      .sort((a, b) => b.totalUnits - a.totalUnits);
  }, [lookupContacts, supplierPendingSummaryByKey]);

  const selectedSupplierPendingUnits = useMemo(
    () => selectedSupplierPendingLines.reduce((acc, line) => acc + Math.max(0, Number(line?.pendingPickupQuantity || 0)), 0),
    [selectedSupplierPendingLines]
  );

  const globallyLinkedPendingDeliveryLineIds = useMemo(() => {
    const linked = new Set();
    (newRoute.destinations || []).forEach((destination) => {
      (destination.operationalLoads || []).forEach((load) => {
        if (load?.stockOutboundOrderId && load?.stockOutboundOrderLineId) {
          linked.add(`${load.stockOutboundOrderId}:${load.stockOutboundOrderLineId}`);
        }
      });
    });
    (newDestination.operationalLoads || []).forEach((load) => {
      if (load?.stockOutboundOrderId && load?.stockOutboundOrderLineId) {
        linked.add(`${load.stockOutboundOrderId}:${load.stockOutboundOrderLineId}`);
      }
    });
    return linked;
  }, [newDestination.operationalLoads, newRoute.destinations]);

  const pendingDeliverySummaryByKey = useMemo(() => {
    return stockOutboundOrders.reduce((acc, order) => {
      const summary = buildOutboundOrderHeaderSummary(order, order?.lines || []);
      const normalizedStatus = normalizeLookupText(summary.status || order?.status || '');
      if (!OUTBOUND_PENDING_ROUTEABLE_STATUSES.has(normalizedStatus)) {
        return acc;
      }

      const pendingStage = getOutboundPendingStage(order, normalizedStatus);
      const normalizedLines = Array.isArray(order?.lines) ? order.lines : [];
      const visibleLines = normalizedLines
        .map((line) => ({
          ...line,
          orderId: String(order.id || ''),
          orderNumber: String(order.orderNumber || order.id || ''),
          operationType: String(order.operationType || 'sale').trim().toLowerCase() || 'sale',
          reference: String(order.reference || '').trim(),
          routePendingStage: pendingStage,
          routePendingQuantity: getOutboundPendingLineQuantity(line, pendingStage),
          pendingDeliveryQuantity: Math.max(0, Number(line?.pendingDeliveryQuantity || 0)),
          lineKey: `${String(order.id || '')}:${String(line.id || '')}`,
        }))
        .filter((line) => line.routePendingQuantity > 0)
        .filter((line) => !globallyLinkedPendingDeliveryLineIds.has(line.lineKey));

      if (!visibleLines.length) return acc;

      const keys = [
        order?.contactId ? `id:${order.contactId}` : '',
        order?.contactNameSnapshot ? `name:${normalizeLookupText(order.contactNameSnapshot)}` : '',
        order?.destinationNameSnapshot ? `name:${normalizeLookupText(order.destinationNameSnapshot)}` : '',
      ].filter(Boolean);

      keys.forEach((key) => {
        if (!acc[key]) {
          acc[key] = { orders: [], totalUnits: 0 };
        }
        acc[key].orders.push({
          ...order,
          lines: visibleLines,
        });
        acc[key].totalUnits += visibleLines.reduce(
          (sum, line) => sum + Number(line.routePendingQuantity || 0),
          0
        );
      });

      return acc;
    }, {});
  }, [globallyLinkedPendingDeliveryLineIds, stockOutboundOrders]);

  const selectedPendingDeliveryOrders = useMemo(() => {
    if (selectedPendingDeliveryContext?.id) {
      const byContextId = pendingDeliverySummaryByKey[`id:${selectedPendingDeliveryContext.id}`];
      if (byContextId?.orders?.length) return byContextId.orders;
    }

    if (selectedPendingDeliveryContext?.name) {
      const byContextName =
        pendingDeliverySummaryByKey[`name:${normalizeLookupText(selectedPendingDeliveryContext.name)}`];
      if (byContextName?.orders?.length) return byContextName.orders;
    }

    if (!selectedContactRecord && !String(newDestination.name || '').trim()) return [];
    const byId = selectedContactRecord?.id
      ? pendingDeliverySummaryByKey[`id:${selectedContactRecord.id}`]
      : null;
    if (byId?.orders?.length) return byId.orders;

    const lookupName = normalizeLookupText(selectedContactRecord?.name || newDestination.name);
    const byName = pendingDeliverySummaryByKey[`name:${lookupName}`];
    return byName?.orders || [];
  }, [newDestination.name, pendingDeliverySummaryByKey, selectedContactRecord, selectedPendingDeliveryContext]);

  const selectedPendingDeliveryItems = useMemo(() => {
    const deduped = new Map();

    selectedPendingDeliveryOrders.forEach((order) => {
      (order.lines || []).forEach((line) => {
        const lineKey = `${order.id}:${line.id}`;
        if (globallyLinkedPendingDeliveryLineIds.has(lineKey)) return;
        if (deduped.has(lineKey)) return;

        deduped.set(lineKey, {
          ...line,
          order,
          lineKey,
        });
      });
    });

    return Array.from(deduped.values());
  }, [globallyLinkedPendingDeliveryLineIds, selectedPendingDeliveryOrders]);

  const selectedPendingDeliveryUnits = useMemo(
    () =>
      selectedPendingDeliveryItems.reduce(
        (acc, item) => acc + Math.max(0, Number(item.routePendingQuantity || item.pendingDeliveryQuantity || 0)),
        0
      ),
    [selectedPendingDeliveryItems]
  );

  const selectedPendingDeliveryStage = useMemo(() => {
    const stages = Array.from(new Set(selectedPendingDeliveryItems.map((item) => item.routePendingStage || 'delivery')));
    if (stages.length === 1) return stages[0];
    return stages.includes('pickup') ? 'mixed' : 'delivery';
  }, [selectedPendingDeliveryItems]);

  const linkedPendingPurchaseLineIds = useMemo(() => {
    const linked = new Set();
    (newRoute.destinations || []).forEach((destination) => {
      (destination.operationalLoads || []).forEach((load) => {
        if (
          load?.purchaseLineId &&
          String(load?.stockMode || '').trim().toLowerCase() === 'pending_inbound'
        ) {
          linked.add(String(load.purchaseLineId));
        }
      });
    });
    (newDestination.operationalLoads || []).forEach((load) => {
      if (
        load?.purchaseLineId &&
        String(load?.stockMode || '').trim().toLowerCase() === 'pending_inbound'
      ) {
        linked.add(String(load.purchaseLineId));
      }
    });
    return linked;
  }, [newRoute.destinations, newDestination.operationalLoads]);

  const stockProductsById = useMemo(
    () =>
      stockProducts.reduce((acc, product) => {
        acc[String(product.id)] = product;
        return acc;
      }, {}),
    [stockProducts]
  );

  const stockOutboundOrdersById = useMemo(
    () =>
      stockOutboundOrders.reduce((acc, order) => {
        acc[String(order.id)] = order;
        return acc;
      }, {}),
    [stockOutboundOrders]
  );

  const stockProductOptions = useMemo(
    () =>
      stockProducts
        .slice()
        .sort((a, b) => String(a.name || '').localeCompare(String(b.name || ''), 'es')),
    [stockProducts]
  );

  const contractsByClientId = useMemo(() => {
    const map = new Map();
    availableContracts.forEach((contract) => {
      const clientId = String(contract?.clientId || '').trim();
      if (!clientId) return;
      const current = map.get(clientId);
      if (!current) {
        map.set(clientId, contract);
        return;
      }
      const currentStatus = String(current?.status || '').trim().toLowerCase();
      const nextStatus = String(contract?.status || '').trim().toLowerCase();
      if (currentStatus !== 'active' && nextStatus === 'active') {
        map.set(clientId, contract);
      }
    });
    return map;
  }, [availableContracts]);

  const shouldShowPendingDeliverySuggestions = useMemo(
    () =>
      selectedPendingDeliveryItems.length > 0 &&
      (showContactSuggestions || Boolean(selectedContactRecord) || Boolean(String(newDestination.name || '').trim())),
    [
      newDestination.name,
      selectedContactRecord,
      selectedPendingDeliveryItems.length,
      showContactSuggestions
    ]
  );

  useEffect(() => {
    const availableIds = selectedSupplierPendingLines
      .filter((line) => !linkedPendingPurchaseLineIds.has(String(line.id)))
      .map((line) => String(line.id));

    setSelectedPendingPurchaseLineIds((prev) => prev.filter((id) => availableIds.includes(id)));
  }, [selectedSupplierPendingLines, linkedPendingPurchaseLineIds]);

  useEffect(() => {
    const availableIds = selectedPendingDeliveryItems.map((item) => item.lineKey);
    setSelectedPendingDeliveryLineIds((prev) => prev.filter((id) => availableIds.includes(id)));
  }, [selectedPendingDeliveryItems]);

  const availableContactPeople = useMemo(() => {
    const people = Array.isArray(selectedContactRecord?.contacts)
      ? selectedContactRecord.contacts
          .map((person) => ({
            name: String(person?.name || '').trim(),
            phone: String(person?.phone || '').trim()
          }))
          .filter((person) => person.name || person.phone)
      : [];

    return people;
  }, [selectedContactRecord]);
  const availableContactTypes = useMemo(() => {
    const defaults = ['cliente', 'proveedor', 'transporte'];
    const dynamic = allContacts
      .map((contact) => String(contact?.type || '').trim().toLowerCase())
      .filter(Boolean);
    const current = String(newDestination.contactType || '').trim().toLowerCase();
    return Array.from(new Set([...defaults, ...dynamic, ...(current ? [current] : [])]));
  }, [allContacts, newDestination.contactType]);
  const pendingOperationalLoadOptions = useMemo(
    () =>
      newRoute.destinations.flatMap((destination) =>
        (destination.operationalLoads || [])
          .filter((load) => String(load?.targetStopId || '').trim().toLowerCase() === 'pending')
          .map((load) => ({
            value: `${destination.id}::${load.id}`,
            label: `${load.title || 'Producto'} · destino ${Number(destination.order ?? 0) + 1} · ${
              destination.name || 'Sin nombre'
            }`,
            load,
            sourceDestination: destination
          }))
      ),
    [newRoute.destinations]
  );
  const linkedServiceIds = useMemo(() => {
    const linked = new Set();
    (newRoute.destinations || []).forEach((destination) => {
      const serviceId = String(destination?.serviceId || '').trim();
      if (serviceId) linked.add(serviceId);
    });
    return linked;
  }, [newRoute.destinations]);

  const visiblePendingServices = useMemo(() => {
    return pendingServices.filter((service) => {
      const serviceId = String(service?.id || '').trim();
      if (serviceId && linkedServiceIds.has(serviceId)) return false;

      const transportMode = String(service?.transportAssignmentModeSnapshot || 'free').trim().toLowerCase();
      const fixedTransportId = String(service?.fixedTransportIdSnapshot || '').trim();
      if (transportMode === 'fixed' && fixedTransportId && fixedTransportId !== String(selectedTransportId || '').trim()) {
        return false;
      }

      const driverMode = String(service?.driverAssignmentModeSnapshot || 'free').trim().toLowerCase();
      const fixedDriverId = String(service?.fixedDriverIdSnapshot || service?.driverId || '').trim();
      if (driverMode === 'fixed' && fixedDriverId && fixedDriverId !== String(selectedDriverId || '').trim()) {
        return false;
      }

      return true;
    });
  }, [linkedServiceIds, pendingServices, selectedDriverId, selectedTransportId]);

  const routePendingServices = useMemo(() => {
    const normalizedSelectedTransportId = String(selectedTransportId || '').trim();
    const normalizedSelectedDriverId = String(selectedDriverId || '').trim();

    return visiblePendingServices
      .map((service) => {
        const normalizedStatus = String(service?.status || '').trim().toLowerCase();
        const lockedByOtherRoute = LOGISTICS_LOCKED_SERVICE_STATUSES.has(normalizedStatus);
        const assignedTransportId = String(service?.transportId || '').trim();
        const assignedDriverId = String(service?.driverId || '').trim();
        const takenBySameSelection =
          Boolean(normalizedSelectedTransportId) &&
          assignedTransportId === normalizedSelectedTransportId &&
          (!assignedDriverId || assignedDriverId === normalizedSelectedDriverId);

        const lockTone =
          normalizedStatus === 'in_progress' || normalizedStatus === 'in-progress' || normalizedStatus === 'en_curso' || normalizedStatus === 'en-curso'
            ? 'in_transit'
            : lockedByOtherRoute
            ? 'assigned'
            : 'pending';

        return {
          ...service,
          normalizedStatus,
          lockedByOtherRoute,
          takenBySameSelection,
          isAttachBlocked: lockedByOtherRoute,
          lockTone,
        };
      })
      .sort((a, b) => {
        if (a.isAttachBlocked !== b.isAttachBlocked) {
          return a.isAttachBlocked ? 1 : -1;
        }
        return 0;
      });
  }, [selectedDriverId, selectedTransportId, visiblePendingServices]);

  const routePendingAvailableServices = useMemo(
    () => routePendingServices.filter((service) => !service.isAttachBlocked),
    [routePendingServices]
  );

  const routePendingTakenServices = useMemo(
    () => routePendingServices.filter((service) => service.isAttachBlocked),
    [routePendingServices]
  );

  useEffect(() => {
    setNewRoute((prev) => ({
      ...prev,
      companyId: resolvedCompanyId,
      companyName: resolvedCompanyName
    }));
  }, [resolvedCompanyId, resolvedCompanyName]);

  useEffect(() => {
    if (!selectedTransport) return;

    setSelectedTransportId(selectedTransport.id || '');
    setSelectedDriverId(selectedTransport.currentDriverId || selectedTransport.driverId || '');
  }, [selectedTransport]);

  useEffect(() => {
    const transportsRef = collection(db, resolveTransportsCollectionPath(resolvedCompanyId));
    const unsubscribe = onSnapshot(transportsRef, (snapshot) => {
      setAvailableTransports(snapshot.docs.map((docSnap) => ({ id: docSnap.id, ...docSnap.data() })));
    });

    return () => unsubscribe();
  }, [resolvedCompanyId]);

  useEffect(() => {
    const driversRef = collection(db, resolveDriversCollectionPath(resolvedCompanyId));
    const unsubscribe = onSnapshot(driversRef, (snapshot) => {
      setAvailableDrivers(snapshot.docs.map((docSnap) => ({ id: docSnap.id, ...docSnap.data() })));
    });

    return () => unsubscribe();
  }, [resolvedCompanyId]);

  useEffect(() => {
    const servicesRef = collection(db, resolveServicesCollectionPath(resolvedCompanyId));
    const unsubscribe = onSnapshot(
      servicesRef,
      (snapshot) => {
        const nextServices = snapshot.docs
          .map((docSnap) => ({ id: docSnap.id, ...docSnap.data() }))
          .filter((service) => LOGISTICS_VISIBLE_SERVICE_STATUSES.has(String(service?.status || '').trim().toLowerCase()));
        setPendingServices(nextServices);
      },
      (error) => {
        console.error('Error cargando servicios pendientes en logística:', error);
        setPendingServices([]);
      }
    );

    return () => unsubscribe();
  }, [resolvedCompanyId]);

  useEffect(() => {
    const contractsRef = collection(db, resolveServiceContractsCollectionPath(resolvedCompanyId));
    const unsubscribe = onSnapshot(
      contractsRef,
      (snapshot) => {
        setAvailableContracts(snapshot.docs.map((docSnap) => ({ id: docSnap.id, ...docSnap.data() })));
      },
      (error) => {
        console.error('Error cargando contratos fijos para logística:', error);
        setAvailableContracts([]);
      }
    );

    return () => unsubscribe();
  }, [resolvedCompanyId]);

  useEffect(() => {
    const templatesRef = collection(db, resolveServiceTemplatesCollectionPath(resolvedCompanyId));
    const unsubscribe = onSnapshot(
      templatesRef,
      (snapshot) => {
        setServiceTemplates(snapshot.docs.map((docSnap) => ({ id: docSnap.id, ...docSnap.data() })));
      },
      (error) => {
        console.error('Error cargando plantillas programadas en logística:', error);
        setServiceTemplates([]);
      }
    );

    return () => unsubscribe();
  }, [resolvedCompanyId]);

  const resolvedSelectedTransport = useMemo(() => {
    return availableTransports.find((transport) => transport.id === selectedTransportId) || selectedTransport || null;
  }, [availableTransports, selectedTransport, selectedTransportId]);

  const transportNameById = useMemo(() => {
    const map = new Map();
    availableTransports.forEach((transport) => {
      map.set(
        String(transport?.id || '').trim(),
        transport?.name || transport?.displayName || transport?.plate || transport?.id || 'Unidad'
      );
    });
    return map;
  }, [availableTransports]);

  const driverNameById = useMemo(() => {
    const map = new Map();
    availableDrivers.forEach((driver) => {
      map.set(
        String(driver?.id || '').trim(),
        [driver?.firstName || driver?.name || '', driver?.lastName || ''].filter(Boolean).join(' ').trim() || driver?.id || 'Chofer'
      );
    });
    return map;
  }, [availableDrivers]);

  const transportAssignmentSummaryById = useMemo(() => {
    const summaryById = new Map();

    const ensureEntry = (transportId) => {
      const key = String(transportId || '').trim();
      if (!key) return null;
      if (!summaryById.has(key)) {
        summaryById.set(key, {
          contractedClients: [],
          contractedClientNames: new Set(),
          pendingServiceClients: [],
          pendingServiceKeys: new Set()
        });
      }
      return summaryById.get(key);
    };

    availableContracts.forEach((contract) => {
      const transportId = String(contract?.fixedTransportId || '').trim();
      if (!transportId) return;
      if (String(contract?.transportAssignmentMode || '').trim().toLowerCase() !== 'fixed') return;
      if (String(contract?.status || 'active').trim().toLowerCase() === 'closed') return;

      const entry = ensureEntry(transportId);
      if (!entry) return;
      const clientName = String(contract?.clientNameSnapshot || contract?.name || '').trim() || 'Cliente';
      if (entry.contractedClientNames.has(clientName)) return;
      entry.contractedClientNames.add(clientName);
      entry.contractedClients.push(clientName);
    });

    routePendingServices.forEach((service) => {
      const transportId = String(service?.fixedTransportIdSnapshot || service?.transportId || '').trim();
      if (!transportId) return;

      const entry = ensureEntry(transportId);
      if (!entry) return;
      const clientName = String(service?.clientNameSnapshot || '').trim() || 'Cliente';
      const serviceKey = `${clientName}::${String(service?.id || '').trim()}`;
      if (entry.pendingServiceKeys.has(serviceKey)) return;
      entry.pendingServiceKeys.add(serviceKey);
      entry.pendingServiceClients.push(clientName);
    });

    return summaryById;
  }, [availableContracts, routePendingServices]);

  const selectedTransportAssignmentSummary = useMemo(() => {
    const transportId = String(resolvedSelectedTransport?.id || selectedTransportId || '').trim();
    return transportAssignmentSummaryById.get(transportId) || null;
  }, [resolvedSelectedTransport, selectedTransportId, transportAssignmentSummaryById]);

  const routeWeekDays = useMemo(
    () => buildWeekDaysForDate(newRoute.date),
    [newRoute.date]
  );

  const selectedRouteDate = useMemo(
    () => parseDateInputValue(newRoute.date) || new Date(),
    [newRoute.date]
  );

  const activatedTemplateKeys = useMemo(() => {
    const keys = new Set();
    pendingServices.forEach((service) => {
      const templateId = String(service?.templateId || '').trim();
      const activatedForDate = normalizeCalendarDateValue(
        service?.calendarActivatedForDate || service?.scheduledDate || service?.date || ''
      );
      if (templateId && activatedForDate) {
        keys.add(`${templateId}::${activatedForDate}`);
      }
    });
    return keys;
  }, [pendingServices]);

  const routeScheduledTemplates = useMemo(() => {
    const selectedDate = startOfDay(selectedRouteDate) || startOfDay(new Date());
    if (!selectedDate) return [];
    const selectedDateValue = toDateInputValue(selectedDate);

    const selectedTransportKey = String(selectedTransportId || '').trim();
    const selectedDriverKey = String(selectedDriverId || '').trim();

    return serviceTemplates
      .filter((template) => {
        const normalizedStatus = String(template?.status || 'active').trim().toLowerCase();
        if (normalizedStatus === 'paused' || normalizedStatus === 'closed' || normalizedStatus === 'inactive') return false;

        const linkedContract = contractsByClientId.get(String(template?.clientId || '').trim()) || null;
        const transportMode = String(template?.transportAssignmentModeSnapshot || linkedContract?.transportAssignmentMode || 'free').trim().toLowerCase();
        const fixedTransportId = String(template?.fixedTransportIdSnapshot || linkedContract?.fixedTransportId || '').trim();
        if (transportMode === 'fixed' && fixedTransportId && fixedTransportId !== selectedTransportKey) return false;

        const driverMode = String(template?.driverAssignmentModeSnapshot || linkedContract?.driverAssignmentMode || 'free').trim().toLowerCase();
        const fixedDriverId = String(template?.fixedDriverIdSnapshot || linkedContract?.fixedDriverId || '').trim();
        if (driverMode === 'fixed' && fixedDriverId && fixedDriverId !== selectedDriverKey) return false;

        if (
          !isDateInsideRange({
            targetDate: selectedDate,
            startDate: template?.serviceStartDate,
            endDate: template?.serviceEndDate,
          })
        ) {
          return false;
        }

        const templateId = String(template?.id || '').trim();
        if (templateId && selectedDateValue && activatedTemplateKeys.has(`${templateId}::${selectedDateValue}`)) {
          return false;
        }

        return true;
      })
      .map((template) => {
        const linkedContract = contractsByClientId.get(String(template?.clientId || '').trim()) || null;
        const scheduledToday = isTemplateScheduledForDate(template, selectedDate);
        const billingCadence = resolveServiceBillingCadence({
          linkedContract,
          frequency: template?.frequency || 'manual',
          outsideContract: Boolean(template?.outsideContract),
          explicitCadence: template?.billingCadenceSnapshot || template?.billingCadence,
        });
        const fixedTransportId = String(template?.fixedTransportIdSnapshot || linkedContract?.fixedTransportId || '').trim();
        const fixedDriverId = String(template?.fixedDriverIdSnapshot || linkedContract?.fixedDriverId || '').trim();

        const serviceLike = {
          ...template,
          serviceTransport: template?.serviceTransport || template?.serviceType || 'camioneta',
          serviceType: template?.serviceTransport || template?.serviceType || 'camioneta',
          commercialModel: template?.commercialModel || linkedContract?.commercialModel || 'trip_rate',
          pricingRule:
            template?.pricingRule ||
            linkedContract?.pricingRule ||
            (String(template?.commercialModel || linkedContract?.commercialModel || '').trim().toLowerCase() === 'monthly_fixed'
              ? ''
              : 'percentage_margin'),
          monthlyFeeSnapshot: Number(template?.monthlyFeeSnapshot ?? linkedContract?.monthlyFee ?? template?.monthlyFee ?? 0) || 0,
          agreedPricePerTripSnapshot: Number(template?.agreedPricePerTripSnapshot ?? linkedContract?.agreedPricePerTrip ?? template?.agreedPricePerTrip ?? 0) || 0,
          fixedMarginAmountSnapshot: Number(template?.fixedMarginAmountSnapshot ?? linkedContract?.fixedMarginAmount ?? template?.fixedMarginAmount ?? 0) || 0,
          marginPercentSnapshot: Number(template?.marginPercentSnapshot ?? linkedContract?.marginPercent ?? template?.marginPercent ?? 0) || 0,
        };
        const estimate = estimateServiceCommercialMetrics({
          service: serviceLike,
          liquidationSettings: companyLiquidationSettings || {},
        });

        return {
          ...template,
          linkedContract,
          billingCadenceSnapshot: billingCadence,
          billingCadenceLabel: resolveBillingCadenceLabel(billingCadence),
          scheduledToday,
          fixedTransportId,
          fixedTransportName:
            template?.fixedTransportNameSnapshot ||
            linkedContract?.fixedTransportNameSnapshot ||
            transportNameById.get(fixedTransportId) ||
            '',
          fixedDriverId,
          fixedDriverName:
            template?.fixedDriverNameSnapshot ||
            linkedContract?.fixedDriverNameSnapshot ||
            driverNameById.get(fixedDriverId) ||
            '',
          estimatedOperationalCost: Number(estimate?.estimatedOperationalCost || 0) || 0,
          estimatedMargin: Number(estimate?.estimatedMargin || 0) || 0,
          estimatedTotalToCharge: Number(estimate?.estimatedTotalToCharge || 0) || 0,
          weekDayLabels: (Array.isArray(template?.serviceDaysOfWeek) ? template.serviceDaysOfWeek : []).map(resolveWeekdayLabel),
        };
      })
      .sort((a, b) => {
        if (a.scheduledToday !== b.scheduledToday) return a.scheduledToday ? -1 : 1;
        return String(a.clientNameSnapshot || '').localeCompare(String(b.clientNameSnapshot || ''), 'es');
      });
  }, [
    companyLiquidationSettings,
    contractsByClientId,
    driverNameById,
    selectedDriverId,
    selectedRouteDate,
    selectedTransportId,
    activatedTemplateKeys,
    serviceTemplates,
    transportNameById,
  ]);

  const routeScheduledTodayTemplates = useMemo(
    () => routeScheduledTemplates.filter((template) => template.scheduledToday),
    [routeScheduledTemplates]
  );

  const routeScheduledExceptionTemplates = useMemo(
    () => routeScheduledTemplates.filter((template) => !template.scheduledToday),
    [routeScheduledTemplates]
  );

  const fallbackSelectedDriver = useMemo(() => {
    if (!resolvedSelectedTransport) return null;
    return {
      id: resolvedSelectedTransport.currentDriverId || resolvedSelectedTransport.driverId || '',
      firstName: resolvedSelectedTransport.currentDriverName || resolvedSelectedTransport.driverName || '',
      lastName: resolvedSelectedTransport.currentDriverLastName || resolvedSelectedTransport.driverLastName || '',
      dni: resolvedSelectedTransport.currentDriverDni || resolvedSelectedTransport.driverDni || '',
      phone: resolvedSelectedTransport.currentDriverPhone || resolvedSelectedTransport.phone || '',
      photoURL: resolvedSelectedTransport.currentDriverPhotoURL || resolvedSelectedTransport.photoURL || '',
      accessSlug: resolvedSelectedTransport.driverAccessSlug || resolvedSelectedTransport.slug || ''
    };
  }, [resolvedSelectedTransport]);

  const resolvedSelectedDriver = useMemo(() => {
    if (selectedDriverId) {
      return availableDrivers.find((driver) => driver.id === selectedDriverId) || fallbackSelectedDriver;
    }

    return fallbackSelectedDriver;
  }, [availableDrivers, fallbackSelectedDriver, selectedDriverId]);

  const lockedDriverIdFromSelectedTransport = String(
    resolvedSelectedTransport?.currentDriverId || resolvedSelectedTransport?.driverId || ''
  ).trim();
  const lockedTransportIdFromSelectedDriver = String(
    resolvedSelectedDriver?.currentTransportId || ''
  ).trim();

  const driverSelectionOptions = useMemo(
    () =>
      availableDrivers.map((driver) => {
        const baseMeta = getDriverRouteSelectionMeta(driver, selectedTransportId, selectedDriverId);

        if (
          lockedDriverIdFromSelectedTransport &&
          driver.id !== lockedDriverIdFromSelectedTransport &&
          driver.id !== selectedDriverId
        ) {
          return {
            driver,
            disabled: true,
            reason: `Vinculado a ${resolvedSelectedTransport?.name || 'la unidad elegida'}`
          };
        }

        return {
          driver,
          ...baseMeta
        };
      }),
    [availableDrivers, lockedDriverIdFromSelectedTransport, resolvedSelectedTransport?.name, selectedDriverId, selectedTransportId]
  );

  const transportSelectionOptions = useMemo(
    () =>
      availableTransports.map((transport) => {
        const baseMeta = getTransportRouteSelectionMeta(transport, selectedDriverId, selectedTransportId);

        if (
          lockedTransportIdFromSelectedDriver &&
          transport.id !== lockedTransportIdFromSelectedDriver &&
          transport.id !== selectedTransportId
        ) {
          return {
            transport,
            disabled: true,
            reason: `Vinculada a ${buildDriverDisplayName(resolvedSelectedDriver || {}, 'el chofer elegido')}`
          };
        }

        return {
          transport,
          ...baseMeta
        };
      }),
    [availableTransports, lockedTransportIdFromSelectedDriver, resolvedSelectedDriver, selectedDriverId, selectedTransportId]
  );

  useEffect(() => {
    if (!resolvedSelectedTransport) return;

    const selectedIdentity = resolveTransportIdentity(resolvedSelectedTransport);

    setNewRoute((prev) => ({
      ...prev,
      companyId: resolvedSelectedTransport.companyId || resolvedCompanyId,
      companyName: resolvedSelectedTransport.companyName || resolvedCompanyName,
      transport: resolvedSelectedTransport.transportType || '',
      transportType: resolvedSelectedTransport.transportType || '',
      transporterId: resolvedSelectedTransport.id || '',
      transporterSlug: resolvedSelectedTransport.driverAccessSlug || resolvedSelectedTransport.slug || '',
      transporterName: resolvedSelectedTransport.name || '',
      transportModel: resolvedSelectedTransport.transportModel || resolvedSelectedTransport.vehicleLabel || '',
      identityColor: resolvedSelectedTransport.identityColor || selectedIdentity.identityColor || '',
      identitySoftColor:
        resolvedSelectedTransport.identitySoftColor || selectedIdentity.identitySoftColor || ''
    }));

    if (!selectedDriverId && (resolvedSelectedTransport.currentDriverId || resolvedSelectedTransport.driverId)) {
      setSelectedDriverId(resolvedSelectedTransport.currentDriverId || resolvedSelectedTransport.driverId || '');
    }
  }, [resolvedSelectedTransport, resolvedCompanyId, resolvedCompanyName, selectedDriverId]);

  useEffect(() => {
    const driverSnapshot = buildDriverSnapshot(resolvedSelectedDriver || {}, resolvedSelectedTransport || {});
    setNewRoute((prev) => ({
      ...prev,
      driverId: driverSnapshot.driverId || '',
      driverName: driverSnapshot.driverName || '',
      driverLastName: driverSnapshot.driverLastName || '',
      driverDni: driverSnapshot.driverDni || '',
      driverPhone: driverSnapshot.driverPhone || '',
      driverPhotoURL: driverSnapshot.driverPhotoURL || '',
      transporterSlug: driverSnapshot.driverAccessSlug || prev.transporterSlug || ''
    }));
  }, [resolvedSelectedDriver, resolvedSelectedTransport]);

  useEffect(() => {
    const contactsPath = resolveContactsCollectionPath(resolvedCompanyId);
    const q = query(collection(db, contactsPath));

    const unsubscribe = onSnapshot(q, (snapshot) => {
      setAllContacts(snapshot.docs.map((docSnap) => ({ id: docSnap.id, ...docSnap.data() })));
    });

    return () => unsubscribe();
  }, [resolvedCompanyId]);

  useEffect(() => {
    const productsRef = collection(db, resolveStockProductsCollectionPath(resolvedCompanyId));
    const unsubscribe = onSnapshot(productsRef, (snapshot) => {
      setStockProducts(snapshot.docs.map((docSnap) => ({ id: docSnap.id, ...docSnap.data() })));
    });

    return () => unsubscribe();
  }, [resolvedCompanyId]);

  useEffect(() => {
    setOperationalLoadMemory(readOperationalLoadsMemory(resolvedCompanyId));
  }, [resolvedCompanyId]);

  useEffect(() => {
    setRoutePanelExpanded(false);
    const timeoutId = window.setTimeout(() => {
      setRoutePanelExpanded(true);
    }, 1000);

    return () => window.clearTimeout(timeoutId);
  }, []);

  useEffect(() => {
    setPreviewPanelExpanded(true);
    const timeoutId = window.setTimeout(() => {
      setPreviewPanelExpanded(false);
    }, 1000);

    return () => window.clearTimeout(timeoutId);
  }, []);

  useEffect(() => {
    if ((newRoute.destinations || []).length > 0) {
      setPreviewPanelExpanded(true);
      return;
    }

    setPreviewPanelExpanded(false);
  }, [newRoute.destinations]);

  useEffect(() => {
    if ((newDestination.operationalLoads || []).length > 0) {
      setDestinationSectionExpanded(true);
      setOperationalLoadsExpanded(true);
    }
  }, [newDestination.operationalLoads]);

  useEffect(() => {
    if (!tutorialActive || !tutorialFocusTarget) return;

    if (
      [
        '#logistics-route-hero',
        '#logistics-route-destination-form',
        '#logistics-route-route-meta',
        '#logistics-route-loads-panel'
      ].includes(tutorialFocusTarget)
    ) {
      setRoutePanelExpanded(true);
      if (tutorialFocusTarget === '#logistics-route-route-meta') {
        setAssignmentSectionExpanded(true);
      }
      if (tutorialFocusTarget === '#logistics-route-destination-form') {
        setDestinationSectionExpanded(true);
      }
      if (tutorialFocusTarget === '#logistics-route-loads-panel') {
        setDestinationSectionExpanded(true);
        setOperationalLoadsExpanded(true);
      }
      return;
    }

    if (tutorialFocusTarget === '#logistics-route-scheduled-services') {
      setRoutePanelExpanded(true);
      setShowScheduledTemplatesPanel(true);
      return;
    }

    if (
      ['#logistics-route-preview', '#logistics-route-save-actions'].includes(
        tutorialFocusTarget
      )
    ) {
      setPreviewPanelExpanded(true);
    }
  }, [tutorialActive, tutorialFocusTarget]);


  useEffect(() => {
    const purchaseLinesRef = collection(db, resolveStockPurchaseLinesCollectionPath(resolvedCompanyId));
    const unsubscribe = onSnapshot(
      purchaseLinesRef,
      (snapshot) => {
        setPendingPurchaseLines(snapshot.docs.map((docSnap) => ({ id: docSnap.id, ...docSnap.data() })));
      },
      (error) => {
        console.error('Error cargando pendientes de stock para RouteCreator:', error);
        setPendingPurchaseLines([]);
      }
    );

    return () => unsubscribe();
  }, [resolvedCompanyId]);

  useEffect(() => {
    const outboundOrdersRef = collection(db, resolveStockOutboundOrdersCollectionPath(resolvedCompanyId));
    const unsubscribe = onSnapshot(
      outboundOrdersRef,
      (snapshot) => {
        setStockOutboundOrders(snapshot.docs.map((docSnap) => ({ id: docSnap.id, ...docSnap.data() })));
      },
      (error) => {
        console.error('Error cargando pedidos de salida para RouteCreator:', error);
        setStockOutboundOrders([]);
      }
    );

    return () => unsubscribe();
  }, [resolvedCompanyId]);

  useEffect(() => {
    const liquidationRef = doc(
      db,
      resolveCompanyLiquidationSettingsPath(resolvedCompanyId)
    );

    const unsubscribe = onSnapshot(
      liquidationRef,
      (docSnap) => {
        setCompanyLiquidationSettings(docSnap.exists() ? docSnap.data() : null);
      },
      (error) => {
        console.error('Error cargando liquidación de empresa en RouteCreator:', error);
        setCompanyLiquidationSettings(null);
      }
    );

    return () => unsubscribe();
  }, [resolvedCompanyId]);

  useEffect(() => {
    const term = toInputValue(searchTerm).toLowerCase();

    const getSupplierPendingSummary = (contact = {}) =>
      (contact?.id && supplierPendingSummaryByKey[`id:${contact.id}`])
      || supplierPendingSummaryByKey[`name:${normalizeLookupText(contact?.name)}`]
      || { lines: [], totalUnits: 0 };

    const getOutboundPendingSummary = (contact = {}) =>
      (contact?.id && pendingDeliverySummaryByKey[`id:${contact.id}`])
      || pendingDeliverySummaryByKey[`name:${normalizeLookupText(contact?.name)}`]
      || { orders: [], totalUnits: 0 };

    const scoreContact = (contact = {}) => {
      const supplierSummary = getSupplierPendingSummary(contact);
      const outboundSummary = getOutboundPendingSummary(contact);
      return Number(supplierSummary.totalUnits || 0) + Number(outboundSummary.totalUnits || 0);
    };

    if (!term) {
      const pendingContacts = lookupContacts
        .filter((contact) => {
          const supplierSummary = getSupplierPendingSummary(contact);
          const outboundSummary = getOutboundPendingSummary(contact);
          return Boolean(supplierSummary?.lines?.length || outboundSummary?.orders?.length);
        })
        .sort((a, b) => scoreContact(b) - scoreContact(a));

      setFilteredContacts(pendingContacts.slice(0, 12));
      return;
    }

    const matches = lookupContacts.filter((contact) => {
      const primaryAddress = contact.addresses?.[0] || {};
      const haystack = [
        contact.name,
        primaryAddress.address,
        primaryAddress.locality,
        primaryAddress.partido,
        primaryAddress.province,
        primaryAddress.postalCode,
        contact.type
      ]
        .filter(Boolean)
        .join(' ')
        .toLowerCase();

      return haystack.includes(term);
    });

    const orderedMatches = [...matches].sort((a, b) => scoreContact(b) - scoreContact(a));

    setFilteredContacts(orderedMatches.slice(0, 12));
  }, [searchTerm, lookupContacts, supplierPendingSummaryByKey, pendingDeliverySummaryByKey]);

  const handleOpenContactSuggestions = () => {
    setShowContactSuggestions(true);
    if (!toInputValue(searchTerm)) {
      const pendingContacts = lookupContacts
        .filter((contact) => {
          const supplierSummary = (contact?.id && supplierPendingSummaryByKey[`id:${contact.id}`])
            || supplierPendingSummaryByKey[`name:${normalizeLookupText(contact?.name)}`]
            || null;
          const outboundSummary = (contact?.id && pendingDeliverySummaryByKey[`id:${contact.id}`])
            || pendingDeliverySummaryByKey[`name:${normalizeLookupText(contact?.name)}`]
            || null;
          return Boolean(supplierSummary?.lines?.length || outboundSummary?.orders?.length);
        })
        .sort((a, b) => {
          const aScore =
            Number(((a?.id && supplierPendingSummaryByKey[`id:${a.id}`]) || supplierPendingSummaryByKey[`name:${normalizeLookupText(a?.name)}`] || {}).totalUnits || 0) +
            Number(((a?.id && pendingDeliverySummaryByKey[`id:${a.id}`]) || pendingDeliverySummaryByKey[`name:${normalizeLookupText(a?.name)}`] || {}).totalUnits || 0);
          const bScore =
            Number(((b?.id && supplierPendingSummaryByKey[`id:${b.id}`]) || supplierPendingSummaryByKey[`name:${normalizeLookupText(b?.name)}`] || {}).totalUnits || 0) +
            Number(((b?.id && pendingDeliverySummaryByKey[`id:${b.id}`]) || pendingDeliverySummaryByKey[`name:${normalizeLookupText(b?.name)}`] || {}).totalUnits || 0);
          return bScore - aScore;
        });
      setFilteredContacts(pendingContacts.slice(0, 12));
    }
  };

  useEffect(() => {
    const handleClickOutside = (event) => {
      const clickedInsideAddress = addressAutocompleteRef.current?.contains(event.target);
      const clickedInsideContact = contactAutocompleteRef.current?.contains(event.target);

      if (!clickedInsideAddress) {
        setShowAddressSuggestions(false);
      }

      if (!clickedInsideContact) {
        setShowContactSuggestions(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  useEffect(() => {
    if (!GEOAPIFY_API_KEY) return undefined;

    const queryText = toInputValue(newDestination.address);
    const readyForAutocomplete =
      queryText.length >= 6 &&
      /\d/.test(queryText) &&
      queryText.split(/\s+/).filter(Boolean).length >= 2;

    if (abortRef.current) {
      abortRef.current.abort();
    }

    if (debounceRef.current) {
      clearTimeout(debounceRef.current);
    }

    if (!readyForAutocomplete) {
      setAddressSuggestions([]);
      setShowAddressSuggestions(false);
      setLoadingAddressSuggestions(false);
      if (queryText && addressValidation.status !== 'success') {
        setAddressValidation((prev) =>
          normalizeAddressValidationState({
            status: 'idle',
            text: 'Completá calle y número para empezar a sugerir direcciones válidas.',
            validatedAddress: prev.validatedAddress
          })
        );
      }
      return undefined;
    }

    setLoadingAddressSuggestions(true);

    if (addressValidation.status !== 'success') {
      setAddressValidation((prev) =>
        normalizeAddressValidationState({
          status: 'loading',
          text: 'Buscando direcciones sugeridas para este destino...',
          validatedAddress: prev.validatedAddress
        })
      );
    }

    debounceRef.current = setTimeout(async () => {
      const controller = new AbortController();
      abortRef.current = controller;

      try {
        const url =
          `https://api.geoapify.com/v1/geocode/autocomplete?text=${encodeURIComponent(destinationAddressForAutocomplete)}` +
          '&filter=countrycode:ar&limit=5&lang=es&format=json' +
          `&apiKey=${GEOAPIFY_API_KEY}`;

        const response = await fetch(url, { signal: controller.signal });

        if (!response.ok) {
          throw new Error(`Geoapify autocomplete error: ${response.status}`);
        }

        const data = await response.json();
        const results = Array.isArray(data?.results) ? data.results : [];

        setAddressSuggestions(results);
        setShowAddressSuggestions(true);
        setLoadingAddressSuggestions(false);

        if (addressValidation.status !== 'success') {
          setAddressValidation((prev) =>
            normalizeAddressValidationState({
              text: results.length
                ? 'Elegí una dirección de la lista para dejar validado el destino.'
                : hasAutocompleteContext
                ? 'No encontramos coincidencias claras. Ajustá calle, número o localidad y probá de nuevo.'
                : 'Sumá localidad o elegí la provincia correcta para afinar la búsqueda.',
              status: results.length ? 'idle' : hasAutocompleteContext ? 'error' : 'idle',
              validatedAddress: prev.validatedAddress
            })
          );
        }
      } catch (error) {
        if (error.name === 'AbortError') return;

        console.error('Error buscando sugerencias de dirección en RouteCreator:', error);
        setAddressSuggestions([]);
        setShowAddressSuggestions(true);
        setLoadingAddressSuggestions(false);
        setAddressValidation(
          normalizeAddressValidationState({
            status: 'error',
            text: 'No pudimos cargar sugerencias ahora mismo. Probá validar manualmente o usar coordenadas.',
            validatedAddress: ''
          })
        );
      }
    }, 350);

    return () => {
      if (debounceRef.current) {
        clearTimeout(debounceRef.current);
      }
    };
  }, [
    destinationAddressForAutocomplete,
    newDestination.address,
    addressValidation.status,
    hasAutocompleteContext
  ]);

  const resetAddressValidation = (message) => {
    setAddressValidation(
      normalizeAddressValidationState({
        text:
          message ||
          (GEOAPIFY_API_KEY
            ? 'La dirección cambió. Elegí una sugerencia o validala antes de añadir el destino.'
            : 'La dirección cambió. Validala manualmente o cargá coordenadas para continuar.'),
        validatedAddress: ''
      })
    );
    setAddressSuggestions([]);
    setShowAddressSuggestions(false);
  };

  const handleDestinationChange = (field, value) => {
    if (field === 'name' && String(value || '').trim()) {
      setRoutePanelExpanded(true);
      setDestinationSectionExpanded(true);
    }

    setNewDestination((prev) => {
      const next = { ...prev, [field]: value };

      if (field === 'deliversTo') {
        next.operationalLoads = (prev.operationalLoads || []).map((load) =>
          normalizeOperationalLoad(
            {
              ...load,
              targetStopId: load.targetStopId || value || ''
            },
            {
              originStopId: load.originStopId || prev.id,
              targetStopId: value || ''
            }
          )
        );
      }

      return next;
    });
  };

  const resolveServiceSnapshotLatLng = async (snapshot = {}) => {
    if (
      snapshot?.latlng &&
      typeof snapshot.latlng.lat === 'number' &&
      typeof snapshot.latlng.lng === 'number'
    ) {
      return snapshot.latlng;
    }

    const addressText = buildServiceAddressText(snapshot);
    if (!addressText) return null;

    try {
      return await geocodeAddress(addressText);
    } catch (error) {
      console.error('No se pudo geocodificar la dirección del servicio para ruta:', error);
      return null;
    }
  };

  const preparePendingServiceRouteDestinations = async (service = {}) => {
    const originSnapshot = service?.originSnapshot || null;
    const destinationSnapshot = service?.destinationSnapshot || null;
    const additionalStops = Array.isArray(service?.additionalStops) ? service.additionalStops : [];

    if (!originSnapshot?.address || !destinationSnapshot?.address) {
      return {
        ok: false,
        message: 'Este servicio no tiene origen y destino completos para pasar a logística.',
      };
    }

    const latlngs = await Promise.all([
      resolveServiceSnapshotLatLng(originSnapshot),
      resolveServiceSnapshotLatLng(destinationSnapshot),
      ...additionalStops.map((stop) => resolveServiceSnapshotLatLng(stop)),
    ]);
    const [originLatlng, destinationLatlng, ...additionalStopsLatlng] = latlngs;

    if (!originLatlng || !destinationLatlng || additionalStopsLatlng.some((item) => !item)) {
      return {
        ok: false,
        message:
          'No pudimos obtener coordenadas válidas para este servicio. Revisá origen, destino y paradas antes de cargarlo a ruta.',
      };
    }

    const baseOrder = newRoute.destinations.length;
    const rawDestinations = buildServiceRouteDestinations(service, {
      originLatlng,
      destinationLatlng,
      additionalStopsLatlng,
    });

    return {
      ok: true,
      destinations: rawDestinations.map((destination, index) =>
        normalizeDestinationOperationalFields({
          ...destination,
          order: baseOrder + index,
          status: 'pending',
        })
      ),
    };
  };

  const handleAttachPendingService = async (service) => {
    const serviceId = String(service?.id || '').trim();
    if (!serviceId) {
      showMessage('No pudimos leer este servicio pendiente.', 'error');
      return;
    }

    if (linkedServiceIds.has(serviceId)) {
      showMessage('Ese servicio ya está cargado en la ruta actual.', 'info');
      return;
    }

    try {
      const serviceRef = doc(db, resolveServiceDocPath(resolvedCompanyId, serviceId));
      const serviceSnap = await getDoc(serviceRef);
      if (!serviceSnap.exists()) {
        showMessage('Ese servicio ya no está disponible para esta hoja de ruta.', 'warning');
        return;
      }

      const latestServiceData = serviceSnap.data() || {};
      const latestStatus = String(latestServiceData?.status || '').trim().toLowerCase();
      if (latestStatus !== 'pending_logistics') {
        const assignedTransportLabel =
          String(latestServiceData?.transportNameSnapshot || '').trim() ||
          String(latestServiceData?.driverNameSnapshot || '').trim() ||
          String(latestServiceData?.routeNameSnapshot || '').trim() ||
          'otra ruta';
        showMessage(
          latestStatus === 'in_progress' || latestStatus === 'in-progress' || latestStatus === 'en_curso' || latestStatus === 'en-curso'
            ? `Este servicio ya está en tránsito con ${assignedTransportLabel}.`
            : `Este servicio ya fue tomado por ${assignedTransportLabel}.`,
          'warning'
        );
        return;
      }
    } catch (error) {
      console.error('Error verificando disponibilidad del servicio antes de cargarlo a la ruta:', error);
      showMessage('No pudimos verificar si este servicio seguía disponible. Reintentá en unos segundos.', 'error');
      return;
    }

    const preparedDestinations = await preparePendingServiceRouteDestinations(service);
    if (!preparedDestinations.ok) {
      showMessage(preparedDestinations.message, 'warning');
      return;
    }

    setNewRoute((prev) => ({
      ...prev,
      destinations: [
        ...prev.destinations,
        ...preparedDestinations.destinations.map((destination, index) => ({
          ...destination,
          order: prev.destinations.length + index,
        })),
      ],
    }));

    showMessage(
      `Servicio de ${service.clientNameSnapshot || 'este cliente'} cargado a la ruta con retiro y entrega.`,
      'success'
    );
  };

  const handleActivateTemplateToRoute = async (template, options = {}) => {
    if (!resolvedCompanyId || !template?.id) {
      showMessage('No encontramos la empresa o la plantilla para activar este servicio.', 'error');
      return;
    }

    const selectedDate = String(options?.date || newRoute.date || getLocalDateAsString(new Date())).trim();
    const linkedContract = template?.linkedContract || contractsByClientId.get(String(template?.clientId || '').trim()) || null;
    const outsideContract = Boolean(template?.outsideContract);
    const billingCadenceSnapshot = resolveServiceBillingCadence({
      linkedContract,
      frequency: template?.frequency || 'manual',
      outsideContract,
      explicitCadence: template?.billingCadenceSnapshot || template?.billingCadence,
    });
    const commercialModel = template?.commercialModel || linkedContract?.commercialModel || 'trip_rate';
    const pricingRule =
      template?.pricingRule ||
      linkedContract?.pricingRule ||
      (String(commercialModel || '').trim().toLowerCase() === 'monthly_fixed' ? '' : 'percentage_margin');

    const basePayload = {
      companyId: resolvedCompanyId,
      companyName: resolvedCompanyName,
      clientId: template?.clientId || '',
      clientNameSnapshot: template?.clientNameSnapshot || '',
      clientContactPersonSnapshot: template?.clientContactPersonSnapshot || '',
      clientPhoneSnapshot: template?.clientPhoneSnapshot || '',
      clientEmailSnapshot: template?.clientEmailSnapshot || '',
      contactId: template?.clientId || '',
      contactNameSnapshot: template?.clientNameSnapshot || '',
      serviceMode: template?.serviceMode || 'puntual',
      serviceModeLabel: template?.serviceModeLabel || 'Puntual',
      serviceType: template?.serviceTransport || template?.serviceType || 'camioneta',
      serviceTypeLabel: template?.serviceTransportLabel || template?.serviceTypeLabel || 'Camioneta',
      serviceTransport: template?.serviceTransport || template?.serviceType || 'camioneta',
      serviceTransportLabel: template?.serviceTransportLabel || template?.serviceTypeLabel || 'Camioneta',
      serviceTask: template?.serviceTask || 'retiro_entrega',
      serviceTaskLabel: template?.serviceTaskLabel || 'Retiro y entrega',
      origin: template?.origin || template?.originSnapshot?.label || template?.originSnapshot?.address || '',
      destination: template?.destination || template?.destinationSnapshot?.label || template?.destinationSnapshot?.address || '',
      originSnapshot: template?.originSnapshot || null,
      destinationSnapshot: template?.destinationSnapshot || null,
      additionalStops: Array.isArray(template?.additionalStops) ? template.additionalStops : [],
      priority: template?.priority || 'normal',
      priorityLabel: template?.priorityLabel || 'Normal',
      salePrice: 0,
      contractId: linkedContract?.id || '',
      contractNameSnapshot: linkedContract?.name || '',
      commercialModel,
      commercialModelLabel: template?.commercialModelLabel || linkedContract?.commercialModelLabel || '',
      billingCadenceSnapshot,
      billingCadenceLabel: resolveBillingCadenceLabel(billingCadenceSnapshot),
      outsideContract,
      pricingRule,
      pricingRuleLabel: template?.pricingRuleLabel || '',
      serviceDaysOfWeek: Array.isArray(template?.serviceDaysOfWeek) ? template.serviceDaysOfWeek : [],
      serviceTimeWindowStart: template?.serviceTimeWindowStart || '',
      serviceTimeWindowEnd: template?.serviceTimeWindowEnd || '',
      serviceStartDate: template?.serviceStartDate || '',
      serviceEndDate: template?.serviceEndDate || '',
      transportAssignmentModeSnapshot: template?.transportAssignmentModeSnapshot || linkedContract?.transportAssignmentMode || 'free',
      fixedTransportIdSnapshot: template?.fixedTransportIdSnapshot || linkedContract?.fixedTransportId || '',
      fixedTransportNameSnapshot:
        template?.fixedTransportNameSnapshot ||
        linkedContract?.fixedTransportNameSnapshot ||
        transportNameById.get(String(template?.fixedTransportIdSnapshot || linkedContract?.fixedTransportId || '').trim()) ||
        '',
      suggestedTransportIdSnapshot: template?.suggestedTransportIdSnapshot || linkedContract?.suggestedTransportId || '',
      suggestedTransportNameSnapshot:
        template?.suggestedTransportNameSnapshot ||
        linkedContract?.suggestedTransportNameSnapshot ||
        transportNameById.get(String(template?.suggestedTransportIdSnapshot || linkedContract?.suggestedTransportId || '').trim()) ||
        '',
      driverAssignmentModeSnapshot: template?.driverAssignmentModeSnapshot || linkedContract?.driverAssignmentMode || 'free',
      fixedDriverIdSnapshot: template?.fixedDriverIdSnapshot || linkedContract?.fixedDriverId || '',
      fixedDriverNameSnapshot:
        template?.fixedDriverNameSnapshot ||
        linkedContract?.fixedDriverNameSnapshot ||
        driverNameById.get(String(template?.fixedDriverIdSnapshot || linkedContract?.fixedDriverId || '').trim()) ||
        '',
      suggestedDriverIdSnapshot: template?.suggestedDriverIdSnapshot || linkedContract?.suggestedDriverId || '',
      suggestedDriverNameSnapshot:
        template?.suggestedDriverNameSnapshot ||
        linkedContract?.suggestedDriverNameSnapshot ||
        driverNameById.get(String(template?.suggestedDriverIdSnapshot || linkedContract?.suggestedDriverId || '').trim()) ||
        '',
      monthlyFeeSnapshot: Number(template?.monthlyFeeSnapshot ?? linkedContract?.monthlyFee ?? template?.monthlyFee ?? 0) || 0,
      agreedPricePerTripSnapshot:
        Number(template?.agreedPricePerTripSnapshot ?? linkedContract?.agreedPricePerTrip ?? template?.agreedPricePerTrip ?? 0) || 0,
      fixedMarginAmountSnapshot:
        Number(template?.fixedMarginAmountSnapshot ?? linkedContract?.fixedMarginAmount ?? template?.fixedMarginAmount ?? 0) || 0,
      marginPresetSnapshot: template?.marginPresetSnapshot || linkedContract?.marginPreset || '',
      marginPercentSnapshot:
        Number(template?.marginPercentSnapshot ?? linkedContract?.marginPercent ?? template?.marginPercent ?? 0) || 0,
      recognitionPending: false,
      recognizedInBalance: false,
      revenueRecognizedAt: null,
      recognizedAt: null,
      pendingClosureKey: '',
      pendingClosurePeriodStart: null,
      pendingClosurePeriodEnd: null,
      notes: String(template?.notes || '').trim(),
      scheduledDate: selectedDate,
      date: selectedDate,
      status: 'pending_logistics',
      statusLabel: 'Pendiente de logística',
      executionSource: 'service_template',
      templateId: template.id,
      templateFrequency: template?.frequency || 'manual',
      templateFrequencyLabel: template?.frequencyLabel || 'A pedido',
      routeId: '',
      driverId:
        (template?.driverAssignmentModeSnapshot || linkedContract?.driverAssignmentMode) === 'fixed'
          ? template?.fixedDriverIdSnapshot || linkedContract?.fixedDriverId || ''
          : '',
      transportId:
        (template?.transportAssignmentModeSnapshot || linkedContract?.transportAssignmentMode) === 'fixed'
          ? template?.fixedTransportIdSnapshot || linkedContract?.fixedTransportId || ''
          : '',
      transportNameSnapshot:
        (template?.transportAssignmentModeSnapshot || linkedContract?.transportAssignmentMode) === 'fixed'
          ? template?.fixedTransportNameSnapshot || linkedContract?.fixedTransportNameSnapshot || ''
          : '',
      calendarActivationMode: options?.asException ? 'manual_exception' : 'scheduled',
      calendarActivatedForDate: selectedDate,
      calendarOriginalDaysSnapshot: Array.isArray(template?.serviceDaysOfWeek) ? template.serviceDaysOfWeek : [],
      createdAt: serverTimestamp(),
      updatedAt: serverTimestamp(),
    };

    const preparedDestinations = await preparePendingServiceRouteDestinations(basePayload);
    if (!preparedDestinations.ok) {
      showMessage(preparedDestinations.message, 'warning');
      return;
    }

    setActivatingTemplateId(template.id);
    try {
      const estimate = estimateServiceCommercialMetrics({
        service: basePayload,
        liquidationSettings: companyLiquidationSettings || {},
      });
      const payloadToSave = {
        ...basePayload,
        salePrice: Number(estimate?.estimatedTotalToCharge || 0) || 0,
        estimatedTransportType: estimate?.transportType || '',
        estimatedDistanceKm: Number(estimate?.estimatedDistanceKm || 0) || 0,
        estimatedOperationalCost: Number(estimate?.estimatedOperationalCost || 0) || 0,
        estimatedMargin: Number(estimate?.estimatedMargin || 0) || 0,
        estimatedTotalToCharge: Number(estimate?.estimatedTotalToCharge || 0) || 0,
        monthlyFeeSnapshot: Number(estimate?.monthlyFeeSnapshot || basePayload.monthlyFeeSnapshot || 0) || 0,
        hasEstimate: Boolean(estimate?.hasEstimate),
      };

      const docRef = await addDoc(collection(db, resolveServicesCollectionPath(resolvedCompanyId)), payloadToSave);
      await updateDoc(doc(db, resolveServiceTemplatesCollectionPath(resolvedCompanyId), template.id), {
        lastGeneratedAt: serverTimestamp(),
        updatedAt: serverTimestamp(),
      });

      await handleAttachPendingService({
        ...payloadToSave,
        id: docRef.id,
      });
    } catch (error) {
      console.error('Error activando plantilla programada desde logística:', error);
      showMessage('No pudimos activar este paquete programado para la ruta.', 'error');
    } finally {
      setActivatingTemplateId('');
    }
  };

  const handleSelectContactPerson = (value) => {
    if (value === '__new__') {
      setNewDestination((prev) => ({
        ...prev,
        contactName: '',
        contactPhone: ''
      }));
      return;
    }

    const selectedPerson = availableContactPeople.find(
      (person) => `${person.name}::${person.phone}` === value
    );

    if (!selectedPerson) return;

    setNewDestination((prev) => ({
      ...prev,
      contactName: selectedPerson.name,
      contactPhone: selectedPerson.phone
    }));
  };

  const handleAttachPendingOperationalLoad = (value) => {
    if (!value) return;

    const selectedOption = pendingOperationalLoadOptions.find((option) => option.value === value);
    if (!selectedOption) return;

    setNewDestination((prev) => {
      const alreadyAttached = (prev.incomingOperationalLoads || []).some(
        (item) =>
          item.loadId === selectedOption.load.id &&
          item.sourceDestinationId === selectedOption.sourceDestination.id
      );

      if (alreadyAttached) return prev;

      return {
        ...prev,
        incomingOperationalLoads: [
          ...(prev.incomingOperationalLoads || []),
          buildIncomingOperationalLoadReference(
            selectedOption.load,
            selectedOption.sourceDestination
          )
        ]
      };
    });
  };

  const handleRemoveIncomingOperationalLoad = (loadId, sourceDestinationId) => {
    setNewDestination((prev) => ({
      ...prev,
      incomingOperationalLoads: (prev.incomingOperationalLoads || []).filter(
        (item) =>
          !(item.loadId === loadId && item.sourceDestinationId === sourceDestinationId)
      )
    }));
  };

  const resolveLoadStockMode = (load = {}) => {
    if (!String(load?.stockProductId || '').trim()) return String(load?.stockMode || '');
    const operationType = String(load?.operationType || '').trim().toLowerCase();
    return STOCK_OUTBOUND_OPERATION_TYPES.has(operationType) ? 'pending_outbound' : String(load?.stockMode || '');
  };

  const handleOperationalLoadChange = (loadId, field, value) => {
    setNewDestination((prev) => ({
      ...prev,
      operationalLoads: (prev.operationalLoads || []).map((load) => {
        if (load.id !== loadId) return load;

        const patch = { [field]: value };
        if (field === 'quantity') {
          patch.quantity = Math.max(1, Number(value) || 1);
        }

        const nextLoad = {
          ...load,
          ...patch
        };
        nextLoad.stockMode = resolveLoadStockMode(nextLoad);

        return normalizeOperationalLoad(nextLoad, {
          originStopId: load.originStopId || prev.id,
          targetStopId: load.targetStopId || ''
        });
      })
    }));
  };

  const handleOperationalLoadStockProductSelect = (loadId, productId) => {
    setNewDestination((prev) => ({
      ...prev,
      operationalLoads: (prev.operationalLoads || []).map((load) => {
        if (load.id !== loadId) return load;

        const selectedProduct = stockProductsById[String(productId)] || null;
        const nextLoad = selectedProduct
          ? {
              ...load,
              title: selectedProduct.name || load.title || '',
              category: selectedProduct.category || load.category || '',
              unit: selectedProduct.unitBase || load.unit || 'unidad',
              stockProductId: selectedProduct.id,
              stockProductNameSnapshot: selectedProduct.name || '',
              stockSkuSnapshot: selectedProduct.sku || '',
              stockBarcodeSnapshot: selectedProduct.barcode || '',
              stockQrCodeSnapshot: selectedProduct.qrCode || '',
              stockCategorySnapshot: selectedProduct.category || '',
              stockUnitSnapshot: selectedProduct.unitBase || 'unidad',
              stockSourceType: 'product',
              stockOperationReason: load.stockOperationReason || 'sale'
            }
          : {
              ...load,
              stockProductId: '',
              stockProductNameSnapshot: '',
              stockSkuSnapshot: '',
              stockBarcodeSnapshot: '',
              stockQrCodeSnapshot: '',
              stockCategorySnapshot: '',
              stockUnitSnapshot: load.unit || 'unidad',
              stockSourceType: '',
              stockMode: '',
              stockOperationReason: ''
            };

        nextLoad.stockMode = resolveLoadStockMode(nextLoad);

        return normalizeOperationalLoad(nextLoad, {
          originStopId: load.originStopId || prev.id,
          targetStopId: load.targetStopId || ''
        });
      })
    }));
  };

  const handleOperationalLoadTitleSelect = (loadId, title) => {
    const matchedMemory = operationalLoadMemory.find(
      (item) => String(item?.title || '').trim().toLowerCase() === String(title || '').trim().toLowerCase()
    );

    setNewDestination((prev) => ({
      ...prev,
      operationalLoads: (prev.operationalLoads || []).map((load) => {
        if (load.id !== loadId) return load;

        return normalizeOperationalLoad(
          {
            ...load,
            title,
            category: matchedMemory?.category || load.category || '',
            unit: matchedMemory?.unit || load.unit || 'unidad'
          },
          {
            originStopId: load.originStopId || prev.id,
            targetStopId: load.targetStopId || ''
          }
        );
      })
    }));
  };

  const handleOperationalLoadTitleInput = (loadId, title) => {
    setNewDestination((prev) => ({
      ...prev,
      operationalLoads: (prev.operationalLoads || []).map((load) => {
        if (load.id !== loadId) return load;

        return normalizeOperationalLoad(
          {
            ...load,
            title
          },
          {
            originStopId: load.originStopId || prev.id,
            targetStopId: load.targetStopId || ''
          }
        );
      })
    }));
  };

  const handleAddOperationalLoad = () => {
    setNewDestination((prev) => ({
      ...prev,
      operationalLoads: [
        ...(prev.operationalLoads || []),
        createEmptyOperationalLoad(prev.id, '')
      ]
    }));
  };

  const handleRemoveOperationalLoad = (loadId) => {
    setNewDestination((prev) => ({
      ...prev,
      operationalLoads: (prev.operationalLoads || []).filter((load) => load.id !== loadId)
    }));
  };


  const handleTogglePendingPurchaseLine = (lineId) => {
    const normalizedId = String(lineId || '');
    if (!normalizedId) return;
    setSelectedPendingPurchaseLineIds((prev) =>
      prev.includes(normalizedId)
        ? prev.filter((id) => id !== normalizedId)
        : [...prev, normalizedId]
    );
  };

  const handleTogglePendingDeliveryLine = (lineKey) => {
    const normalizedId = String(lineKey || '');
    if (!normalizedId) return;
    setSelectedPendingDeliveryLineIds((prev) =>
      prev.includes(normalizedId)
        ? prev.filter((id) => id !== normalizedId)
        : [...prev, normalizedId]
    );
  };

  const attachPendingPurchaseLinesToDestination = (linesToAttach = []) => {
    if (!Array.isArray(linesToAttach) || linesToAttach.length === 0) {
      showMessage('Elegí al menos un pendiente para generar la carga operativa.', 'warning');
      return false;
    }

    const supplier = selectedContactRecord || {};

    setNewDestination((prev) => {
      const nextLoads = [...(prev.operationalLoads || [])];
      linesToAttach.forEach((line) => {
        nextLoads.push(buildOperationalLoadFromPurchaseLine(line, supplier));
      });

      return {
        ...prev,
        type: 'Retiro',
        contactType: 'proveedor',
        operationRole: 'pickup',
        operationalLoads: nextLoads
      };
    });

    setDestinationSectionExpanded(true);
    setOperationalLoadsExpanded(true);
    setSelectedPendingPurchaseLineIds([]);
    showMessage(
      `${linesToAttach.length} carga${linesToAttach.length > 1 ? 's' : ''} operativa${linesToAttach.length > 1 ? 's' : ''} generada${linesToAttach.length > 1 ? 's' : ''} desde pendientes del proveedor.`,
      'success'
    );
    return true;
  };

  const handleAttachSupplierPendingLines = (mode = 'selected') => {
    const sourceLines = selectedSupplierPendingLines.filter(
      (line) => !linkedPendingPurchaseLineIds.has(String(line.id))
    );
    const linesToAttach = mode === 'all'
      ? sourceLines
      : sourceLines.filter((line) => selectedPendingPurchaseLineIds.includes(String(line.id)));

    attachPendingPurchaseLinesToDestination(linesToAttach);
  };

  const handleAttachSinglePendingPurchaseLine = (line) => {
    if (!line) return;
    if (linkedPendingPurchaseLineIds.has(String(line.id))) {
      showMessage('Ese pendiente ya está cargado en la ruta.', 'info');
      return;
    }
    attachPendingPurchaseLinesToDestination([line]);
  };

  const attachPendingDeliveryItemsToDestination = (itemsToAttach = []) => {
    if (!Array.isArray(itemsToAttach) || itemsToAttach.length === 0) {
      showMessage('Elegí al menos un pendiente de entrega para cargar la salida operativa.', 'warning');
      return false;
    }

    setNewDestination((prev) => {
      const nextLoads = [...(prev.operationalLoads || [])];
      itemsToAttach.forEach((item) => {
        nextLoads.push(buildOperationalLoadFromOutboundOrderLine(item.order, item, prev));
      });

      return {
        ...prev,
        type: 'Entrega',
        operationRole: 'delivery',
        operationalLoads: nextLoads
      };
    });

    setDestinationSectionExpanded(true);
    setOperationalLoadsExpanded(true);
    setSelectedPendingDeliveryLineIds([]);
    showMessage(
      `${itemsToAttach.length} ${
        itemsToAttach.every((item) => item.routePendingStage === 'pickup')
          ? 'retiro'
          : 'pendiente'
      }${itemsToAttach.length > 1 ? 's' : ''} agregado${itemsToAttach.length > 1 ? 's' : ''} a la carga operativa.`,
      'success'
    );
    return true;
  };

  const handleAttachPendingDeliveryItems = (mode = 'selected') => {
    const itemsToAttach =
      mode === 'all'
        ? selectedPendingDeliveryItems
        : selectedPendingDeliveryItems.filter((item) =>
            selectedPendingDeliveryLineIds.includes(item.lineKey)
          );

    attachPendingDeliveryItemsToDestination(itemsToAttach);
  };

  const updateAddressField = (field, value) => {
    if (field === 'province') {
      setProvinceExplicitlySet(true);
    }

    setNewDestination((prev) => ({
      ...prev,
      [field]: value,
      latlng: null
    }));
    if (field !== 'apartment') {
      resetAddressValidation();
    }
  };

  const applyAddressSuggestion = (item) => {
    const nextDestination = {
      ...newDestination,
      address: toInputValue(item.address_line1 || item.street || newDestination.address),
      locality: toInputValue(
        item.city || item.town || item.suburb || item.county || newDestination.locality
      ),
      province: toInputValue(item.state || newDestination.province || DEFAULT_PROVINCE),
      partido: toInputValue(item.county || newDestination.partido),
      postalCode: toInputValue(item.postcode || newDestination.postalCode),
      latlng:
        item.lat != null && item.lon != null
          ? {
              lat: Number(item.lat),
              lng: Number(item.lon)
            }
          : null
    };

    setNewDestination(nextDestination);
    setProvinceExplicitlySet(Boolean(item.state || nextDestination.province));
    setAddressSuggestions([]);
    setShowAddressSuggestions(false);
    setShowManualCoordinates(false);
    setManualLat(nextDestination.latlng?.lat != null ? String(nextDestination.latlng.lat) : '');
    setManualLng(nextDestination.latlng?.lng != null ? String(nextDestination.latlng.lng) : '');
    setAddressGeocodingError('');

    if (nextDestination.latlng) {
      setAddressValidation(
        normalizeAddressValidationState({
          status: 'success',
          text: 'Dirección validada correctamente. El destino ya quedó geolocalizado.',
          validatedAddress: composeDestinationAddress(nextDestination)
        })
      );
      return;
    }

    setAddressValidation(
      normalizeAddressValidationState({
        status: 'error',
        text: 'No pudimos validar la sugerencia elegida. Probá con otra opción.',
        validatedAddress: ''
      })
    );
  };

  const resetDestinationForm = () => {
    setNewDestination(EMPTY_DESTINATION);
    setProvinceExplicitlySet(false);
    setEditingDestinationId(null);
    setDestinationDetailsExpanded(false);
    setOperationalLoadsExpanded(false);
    setSelectedPendingPurchaseLineIds([]);
    setSelectedPendingDeliveryLineIds([]);
    setSelectedPendingDeliveryContext(null);
    setSearchTerm('');
    setFilteredContacts([]);
    setShowContactSuggestions(false);
    setAddressGeocodingError('');
    setAddressValidation(normalizeAddressValidationState());
    setAddressSuggestions([]);
    setShowAddressSuggestions(false);
    setShowManualCoordinates(false);
    setManualLat('');
    setManualLng('');
  };

  const handleEditDestination = (destination) => {
    if (!destination) return;

    setRoutePanelExpanded(true);
    setDestinationSectionExpanded(true);
    setEditingDestinationId(destination.id || null);
    setNewDestination({
      ...EMPTY_DESTINATION,
      ...destination,
      operationalLoads: Array.isArray(destination.operationalLoads)
        ? destination.operationalLoads.map((load) => ({ ...load }))
        : [],
      incomingOperationalLoads: Array.isArray(destination.incomingOperationalLoads)
        ? destination.incomingOperationalLoads.map((load) => ({ ...load }))
        : []
    });
    setSelectedPendingPurchaseLineIds([]);
    setSelectedPendingDeliveryLineIds([]);
    setSelectedPendingDeliveryContext(null);
    setProvinceExplicitlySet(Boolean(destination?.province));
    setDestinationDetailsExpanded(
      Boolean(destination?.apartment || destination?.postalCode || destination?.contactType)
    );
    setOperationalLoadsExpanded(
      Boolean((destination.operationalLoads || []).length || (destination.incomingOperationalLoads || []).length)
    );
    setManualLat(destination?.latlng?.lat != null ? String(destination.latlng.lat) : '');
    setManualLng(destination?.latlng?.lng != null ? String(destination.latlng.lng) : '');
    setAddressGeocodingError('');
    setShowAddressSuggestions(false);
    setSearchTerm('');
    setFilteredContacts([]);
    setAddressValidation(
      destination?.latlng
        ? normalizeAddressValidationState({
            status: 'success',
            text: 'Destino cargado para edición. La ubicación ya estaba validada.',
            validatedAddress: composeDestinationAddress(destination)
          })
        : normalizeAddressValidationState()
    );
  };

  const handleMoveDestination = (destinationId, direction) => {
    setNewRoute((prev) => {
      const currentIndex = prev.destinations.findIndex((item) => item.id === destinationId);
      if (currentIndex < 0) return prev;

      const targetIndex = direction === 'up' ? currentIndex - 1 : currentIndex + 1;
      if (targetIndex < 0 || targetIndex >= prev.destinations.length) return prev;

      const reordered = [...prev.destinations];
      const [moved] = reordered.splice(currentIndex, 1);
      reordered.splice(targetIndex, 0, moved);

      return {
        ...prev,
        destinations: reordered.map((item, index) => ({ ...item, order: index }))
      };
    });
  };

  const handleSelectContact = (contact) => {
    setRoutePanelExpanded(true);
    setDestinationSectionExpanded(true);
    if (contact.addresses && contact.addresses.length > 1) {
      setContactForSucursal(contact);
      setShowSucursalModal(true);
      return;
    }

    const typeFamily = resolveContactTypeFamily(contact?.type);
    const typeMap = {
      cliente: 'Entrega',
      proveedor: 'Retiro',
      transporte: 'Transporte'
    };

    const selectedAddress = contact.addresses?.[0];

    const nextDestination = {
        ...newDestination,
        contactDocId: contact.id || '',
        name: contact.name || '',
      address: selectedAddress?.address || '',
      apartment: selectedAddress?.apartment || '',
      locality: selectedAddress?.locality || '',
        type: typeMap[typeFamily] || 'Entrega',
        contactType: contact.type || 'cliente',
        province: selectedAddress?.province || DEFAULT_PROVINCE,
        partido: selectedAddress?.partido || '',
        postalCode: selectedAddress?.postalCode || '',
        latlng: selectedAddress?.latlng || null,
        description: '',
        contactName: contact.contacts?.[0]?.name || '',
        contactPhone: contact.contacts?.[0]?.phone || ''
      };

    setNewDestination(nextDestination);
    setProvinceExplicitlySet(Boolean(selectedAddress?.province));

    const contactPendingSummary = (contact?.id && supplierPendingSummaryByKey[`id:${contact.id}`])
      || supplierPendingSummaryByKey[`name:${normalizeLookupText(contact?.name)}`]
      || null;
    const outboundPendingSummary = (contact?.id && pendingDeliverySummaryByKey[`id:${contact.id}`])
      || pendingDeliverySummaryByKey[`name:${normalizeLookupText(contact?.name)}`]
      || null;
    setSelectedPendingPurchaseLineIds([]);
    setSelectedPendingDeliveryLineIds(
      outboundPendingSummary?.orders?.flatMap((order) =>
        (order.lines || []).map((line) => `${order.id}:${line.id}`)
      ) || []
    );
    setSelectedPendingDeliveryContext(
      outboundPendingSummary?.orders?.length
        ? {
            id: contact?.id || '',
            name: contact?.name || nextDestination.name || ''
          }
        : null
    );

    if (typeFamily === 'proveedor' && contactPendingSummary?.lines?.length) {
      showMessage(`${contact.name} añadido al formulario. Elegí qué pendientes querés retirar o usá "Retirar todo".`, 'success');
    } else if (outboundPendingSummary?.orders?.length) {
      const outboundReasonLabel = getPendingDeliverySummaryReason(outboundPendingSummary.orders);
      showMessage(
        `${contact.name} añadido al formulario. Tenés ${outboundReasonLabel.toLowerCase()} ${outboundReasonLabel === 'Retiro garantía' ? 'listo' : 'lista'} para marcar y asignar a la carga operativa.`,
        'success'
      );
    } else {
      showMessage(`${contact.name} añadido al formulario.`, 'success');
    }
    setAddressGeocodingError('');
    setSearchTerm('');
    setFilteredContacts([]);

    if (selectedAddress?.latlng) {
      setAddressValidation(
        normalizeAddressValidationState({
          status: 'success',
          text: 'Contacto aplicado. La ubicación del destino ya quedó lista.',
          validatedAddress: composeDestinationAddress(nextDestination)
        })
      );
      setManualLat(String(selectedAddress.latlng.lat));
      setManualLng(String(selectedAddress.latlng.lng));
      return;
    }

    setAddressValidation(
      normalizeAddressValidationState({
        status: GEOAPIFY_API_KEY ? 'idle' : 'error',
        text: GEOAPIFY_API_KEY
          ? 'Contacto aplicado. Elegí una sugerencia para validar la ubicación del destino.'
          : 'Contacto aplicado. Validá la dirección manualmente o cargá coordenadas para seguir.',
        validatedAddress: ''
      })
    );

    if (!selectedAddress?.latlng && selectedAddress?.address) {
      const fullAddressParts = [
        selectedAddress.address,
        selectedAddress.apartment,
        selectedAddress.locality,
        selectedAddress.partido,
        selectedAddress.province,
        selectedAddress.postalCode
      ].filter(Boolean);

      const fullAddress = fullAddressParts.join(', ');

      geocodeAddress(fullAddress, showMessage).then((coords) => {
        if (coords) {
          setNewDestination((prev) => ({ ...prev, latlng: coords }));
          setAddressValidation(
            normalizeAddressValidationState({
              status: 'success',
              text: 'Coordenadas del contacto recuperadas correctamente.',
              validatedAddress: fullAddress
            })
          );
          setAddressGeocodingError('');
          setManualLat(String(coords.lat));
          setManualLng(String(coords.lng));
        } else {
          setNewDestination((prev) => ({ ...prev, latlng: null }));
          setAddressGeocodingError(
            'No se pudieron obtener coordenadas. Verifica la dirección.'
          );
        }
      });
    }
  };

  const handleSucursalSelected = (contact, address) => {
    const typeFamily = resolveContactTypeFamily(contact?.type);
    const typeMap = {
      cliente: 'Entrega',
      proveedor: 'Retiro',
      transporte: 'Transporte'
    };

    setRoutePanelExpanded(true);
    setDestinationSectionExpanded(true);
    const nextDestination = {
      ...newDestination,
      contactDocId: contact.id || '',
      name: contact.name,
      address: address.address,
      apartment: address.apartment,
      locality: address.locality,
      type: typeMap[typeFamily] || 'Entrega',
      contactType: contact.type || 'cliente',
      province: address.province,
      partido: address.partido,
      postalCode: address.postalCode,
      latlng: address.latlng || null,
      description: '',
      contactName: contact.contacts?.[0]?.name || '',
      contactPhone: contact.contacts?.[0]?.phone || ''
    };

    setNewDestination(nextDestination);
    setProvinceExplicitlySet(Boolean(address?.province));
    const contactPendingSummary = (contact?.id && supplierPendingSummaryByKey[`id:${contact.id}`])
      || supplierPendingSummaryByKey[`name:${normalizeLookupText(contact?.name)}`]
      || null;
    const outboundPendingSummary = (contact?.id && pendingDeliverySummaryByKey[`id:${contact.id}`])
      || pendingDeliverySummaryByKey[`name:${normalizeLookupText(contact?.name)}`]
      || null;
    setSelectedPendingPurchaseLineIds(
      (contactPendingSummary?.lines || [])
        .filter((line) => !linkedPendingPurchaseLineIds.has(String(line.id)))
        .map((line) => String(line.id))
    );
    setSelectedPendingDeliveryLineIds(
      outboundPendingSummary?.orders?.flatMap((order) =>
        (order.lines || []).map((line) => `${order.id}:${line.id}`)
      ) || []
    );
    setSelectedPendingDeliveryContext(
      outboundPendingSummary?.orders?.length
        ? {
            id: contact?.id || '',
            name: contact?.name || nextDestination.name || ''
          }
        : null
    );
    setShowSucursalModal(false);
    setContactForSucursal(null);
    showMessage(
      outboundPendingSummary?.orders?.length
        ? (() => {
            const outboundReasonLabel = getPendingDeliverySummaryReason(outboundPendingSummary.orders);
            return `Sucursal seleccionada. Tenés ${outboundReasonLabel.toLowerCase()} ${outboundReasonLabel === 'Retiro garantía' ? 'listo' : 'lista'} para marcar y asignar a la carga operativa.`;
          })()
        : 'Sucursal seleccionada para añadir a la ruta.',
      'success'
    );
    setAddressGeocodingError('');

    setAddressValidation(
      normalizeAddressValidationState({
        status: address.latlng ? 'success' : GEOAPIFY_API_KEY ? 'idle' : 'error',
        text: address.latlng
          ? 'Sucursal aplicada. La ubicación ya quedó validada.'
          : GEOAPIFY_API_KEY
          ? 'Sucursal aplicada. Elegí una sugerencia o validá la dirección para continuar.'
          : 'Sucursal aplicada. Validá la dirección manualmente o cargá coordenadas.',
        validatedAddress: address.latlng ? composeDestinationAddress(nextDestination) : ''
      })
    );
    setManualLat(address.latlng?.lat != null ? String(address.latlng.lat) : '');
    setManualLng(address.latlng?.lng != null ? String(address.latlng.lng) : '');
  };

  const handleCloseSucursalModal = () => {
    setShowSucursalModal(false);
    setContactForSucursal(null);
  };

  const handleDrop = (e) => {
    e.preventDefault();
    setIsDraggingOver(false);

    const contactJson = e.dataTransfer.getData('application/json');
    if (contactJson) {
      const contact = JSON.parse(contactJson);
      handleSelectContact(contact);
    }
  };

  const handleValidateAddress = async () => {
    if (!newDestination.address?.trim()) {
      showMessage('Primero ingresá una dirección para validar el destino.', 'warning');
      return null;
    }

    if (!looksSpecificEnoughForFallbackGeocode(newDestination)) {
      setNewDestination((prev) => ({ ...prev, latlng: null }));
      setAddressValidation(
        normalizeAddressValidationState({
          status: 'error',
          text: 'La dirección todavía es muy general. Sumá calle, número y localidad o elegí una sugerencia.',
          validatedAddress: ''
        })
      );
      setAddressGeocodingError(
        'La dirección es demasiado general. Sumá calle, número y localidad para validar el destino.'
      );
      showMessage('Completá calle, número y localidad para validar el destino.', 'warning');
      return null;
    }

    setAddressValidation(
      normalizeAddressValidationState({
        status: 'loading',
        text: 'Validando dirección operativa del destino...',
        validatedAddress: addressValidation.validatedAddress
      })
    );

    const coords = await geocodeAddress(fullDestinationAddress, showMessage);

    if (!coords) {
      setNewDestination((prev) => ({ ...prev, latlng: null }));
      setAddressValidation(
        normalizeAddressValidationState({
          status: 'error',
          text: 'No pudimos validar la dirección. Probá con una sugerencia más específica o cargá coordenadas manuales.',
          validatedAddress: ''
        })
      );
      setAddressGeocodingError(
        'No se pudieron obtener coordenadas. Probá con una dirección más precisa o usá coordenadas manuales.'
      );
      return null;
    }

    setNewDestination((prev) => ({ ...prev, latlng: coords }));
    setManualLat(String(coords.lat));
    setManualLng(String(coords.lng));
    setAddressGeocodingError('');
    setAddressValidation(
      normalizeAddressValidationState({
        status: 'success',
        text: 'Dirección validada. El destino ya quedó listo para añadirse a la ruta.',
        validatedAddress: fullDestinationAddress
      })
    );
    return coords;
  };

  const handleApplyManualCoordinates = () => {
    const lat = Number(manualLat);
    const lng = Number(manualLng);

    if (!Number.isFinite(lat) || !Number.isFinite(lng)) {
      showMessage('Ingresá coordenadas válidas para aplicar la ubicación manual.', 'warning');
      return;
    }

    if (lat < -90 || lat > 90 || lng < -180 || lng > 180) {
      showMessage('Las coordenadas están fuera de rango. Revisá latitud y longitud.', 'warning');
      return;
    }

    const coords = { lat, lng };
    setNewDestination((prev) => ({ ...prev, latlng: coords }));
    setAddressValidation(
      normalizeAddressValidationState({
        status: 'success',
        text: 'Coordenadas cargadas manualmente. El destino también quedó listo para operar.',
        validatedAddress: fullDestinationAddress
      })
    );
    setAddressGeocodingError('');
    showMessage('Coordenadas aplicadas correctamente.', 'success');
  };

  const handleAddDestination = async () => {
    if (!newDestination.name || !newDestination.address) {
      showMessage('El nombre y la dirección son obligatorios.', 'error');
      return;
    }

    const supplierPendingLoadsAttached = (newDestination.operationalLoads || []).filter(
      (load) => String(load?.stockMode || '').trim().toLowerCase() === 'pending_inbound'
    );
    const availableSupplierPendingLines = selectedSupplierPendingLines.filter(
      (line) => !linkedPendingPurchaseLineIds.has(String(line.id))
    );

    if (
      selectedContactRecord &&
      resolveContactTypeFamily(selectedContactRecord?.type) === 'proveedor' &&
      availableSupplierPendingLines.length > 0 &&
      supplierPendingLoadsAttached.length === 0
    ) {
      showMessage(
        `Tenés ${availableSupplierPendingLines.length} pendiente${availableSupplierPendingLines.length > 1 ? 's' : ''} de retiro para este proveedor. Primero cargalos dentro de "Pendientes del proveedor" antes de guardar el destino.`,
        'warning'
      );
      setOperationalLoadsExpanded(true);
      return;
    }

    const pendingDeliveryLoadsAttached = (newDestination.operationalLoads || []).filter(
      (load) =>
        String(load?.stockSourceType || '').trim().toLowerCase() === 'outbound_order' &&
        String(load?.stockMode || '').trim().toLowerCase() === 'pending_outbound'
    );

    if (selectedPendingDeliveryItems.length > 0 && pendingDeliveryLoadsAttached.length === 0) {
      showMessage(
        `Tenés ${getPendingDeliverySummaryReason(selectedPendingDeliveryOrders).toLowerCase()} pendiente para este destino. Primero marcá el pedido y asignalo a la carga operativa antes de guardar.`,
        'warning'
      );
      setOperationalLoadsExpanded(true);
      return;
    }

    let finalLatlng = newDestination.latlng;

    if (!finalLatlng) {
      finalLatlng = await handleValidateAddress();
      if (!finalLatlng) {
        showMessage('No se pudo añadir el destino. Revisá la dirección o cargá coordenadas.', 'error');
        return;
      }
    }

    setAddressGeocodingError('');

    const destinationId = editingDestinationId || Date.now().toString();
    const derivedType = inferDestinationTypeFromLoads(
      newDestination.operationalLoads,
      newDestination.type
    );
    const destinationToSave = normalizeDestinationOperationalFields({
      ...newDestination,
      id: destinationId,
      type: derivedType,
      operationRole: inferDestinationOperationRole(derivedType),
      deliveries: (newDestination.operationalLoads || []).filter(
        (load) => String(load?.operationType || '').trim().toLowerCase() === 'delivery'
      ).length,
      pickups: (newDestination.operationalLoads || []).filter((load) =>
        ['pickup', 'return', 'transfer', 'storage'].includes(
          String(load?.operationType || '').trim().toLowerCase()
        )
      ).length,
      status: 'pending',
      order: editingDestinationId
        ? newRoute.destinations.find((destination) => destination.id === editingDestinationId)?.order ?? newRoute.destinations.length
        : newRoute.destinations.length,
      latlng: finalLatlng,
      incomingOperationalLoads: (newDestination.incomingOperationalLoads || []).map((item) => ({
        loadId: item.loadId || '',
        sourceDestinationId: item.sourceDestinationId || '',
        sourceDestinationName: item.sourceDestinationName || '',
        title: item.title || '',
        quantity: item.quantity || 1,
        unit: item.unit || 'unidad',
        operationType: item.operationType || 'pickup'
      })),
      operationalLoads: (newDestination.operationalLoads || []).map((load) =>
        normalizeOperationalLoad(load, {
          originStopId: load?.originStopId || destinationId,
          targetStopId: load?.targetStopId || newDestination.deliversTo || ''
        })
      )
    });

    for (const key in destinationToSave) {
      if (destinationToSave[key] === undefined) {
        delete destinationToSave[key];
      }
    }

    const contactsPath = resolveContactsCollectionPath(resolvedCompanyId);
    const contactsRef = collection(db, contactsPath);
    const q = query(contactsRef, where('name', '==', newDestination.name.trim()));
    const querySnapshot = await getDocs(q);

    if (querySnapshot.empty) {
      const sanitizedContacts = [];
      if (newDestination.contactName || newDestination.contactPhone) {
        sanitizedContacts.push({
          name: newDestination.contactName,
          phone: newDestination.contactPhone
        });
      }

      const newContactData = {
        companyId: resolvedCompanyId,
        companyName: resolvedCompanyName,
        name: newDestination.name.trim(),
        type: newDestination.contactType,
        addresses: [
          {
            address: newDestination.address,
            apartment: newDestination.apartment,
            locality: newDestination.locality,
            province: newDestination.province,
            partido: newDestination.partido,
            postalCode: newDestination.postalCode,
            latlng: finalLatlng
          }
        ],
        contacts: sanitizedContacts
      };

      await addDoc(contactsRef, newContactData);
      showMessage(
        `Destino añadido y nuevo contacto "${newDestination.name}" guardado.`,
        'success'
      );
    } else {
      const existingContactDoc = querySnapshot.docs[0];
      const existingContactData = existingContactDoc.data();
      const nextContacts = [...(existingContactData.contacts || [])];

      if (newDestination.contactName || newDestination.contactPhone) {
        const alreadyExists = nextContacts.some(
          (contactPerson) =>
            String(contactPerson?.name || '').trim().toLowerCase() ===
              String(newDestination.contactName || '').trim().toLowerCase() &&
            String(contactPerson?.phone || '').trim() ===
              String(newDestination.contactPhone || '').trim()
        );

        if (!alreadyExists) {
          nextContacts.push({
            name: newDestination.contactName,
            phone: newDestination.contactPhone
          });
        }
      }

      const existingAddressIndex = existingContactData.addresses?.findIndex(
        (addr) =>
          addr.address === newDestination.address &&
          addr.apartment === newDestination.apartment
      );

      if (existingAddressIndex === -1) {
        await updateDoc(doc(db, contactsPath, existingContactDoc.id), {
          contacts: nextContacts,
          addresses: [
            ...(existingContactData.addresses || []),
            {
              address: newDestination.address,
              apartment: newDestination.apartment,
              locality: newDestination.locality,
              province: newDestination.province,
              partido: newDestination.partido,
              postalCode: newDestination.postalCode,
              latlng: finalLatlng
            }
          ]
        });

        showMessage(
          `Destino añadido. Nueva dirección guardada para "${newDestination.name}".`,
          'success'
        );
      } else {
        const currentAddressInDb = existingContactData.addresses[existingAddressIndex];

        if (JSON.stringify(currentAddressInDb.latlng) !== JSON.stringify(finalLatlng)) {
          currentAddressInDb.latlng = finalLatlng;

          await updateDoc(doc(db, contactsPath, existingContactDoc.id), {
            contacts: nextContacts,
            addresses: existingContactData.addresses
          });

          showMessage(
            `Destino añadido. Coordenadas actualizadas para "${newDestination.name}".`,
            'info'
          );
        } else {
          if (nextContacts.length !== (existingContactData.contacts || []).length) {
            await updateDoc(doc(db, contactsPath, existingContactDoc.id), {
              contacts: nextContacts
            });
          }

          showMessage(
            `Destino añadido. El contacto "${newDestination.name}" ya existía.`,
            'info'
          );
        }
      }
    }

    setNewRoute((prev) => {
      const existingIndex = prev.destinations.findIndex((destination) => destination.id === destinationToSave.id);
      const baseDestinations = prev.destinations.map((destination) => ({
        ...destination,
        operationalLoads: (destination.operationalLoads || []).map((load) => {
          const linkedIncoming = (destinationToSave.incomingOperationalLoads || []).find(
            (incoming) =>
              incoming.sourceDestinationId === destination.id &&
              incoming.loadId === load.id
          );

          if (!linkedIncoming) return load;

          return normalizeOperationalLoad(load, {
            ...load,
            targetStopId: destinationToSave.id
          });
        })
      }));

      const destinations =
        existingIndex >= 0
          ? baseDestinations.map((destination, index) =>
              index === existingIndex ? destinationToSave : destination
            )
          : [...baseDestinations, destinationToSave];

      return {
        ...prev,
        destinations: destinations.map((destination, index) => ({
          ...destination,
          order: index
        }))
      };
    });

    if ((destinationToSave.operationalLoads || []).length > 0) {
      const mergedMemory = [
        ...destinationToSave.operationalLoads.map((load) => ({
          title: String(load?.title || '').trim(),
          category: String(load?.category || '').trim(),
          unit: String(load?.unit || 'unidad').trim() || 'unidad'
        })),
        ...operationalLoadMemory
      ]
        .filter((item) => item.title)
        .filter(
          (item, index, array) =>
            array.findIndex(
              (candidate) =>
                candidate.title.toLowerCase() === item.title.toLowerCase()
            ) === index
        )
        .slice(0, 30);

      setOperationalLoadMemory(mergedMemory);
      writeOperationalLoadsMemory(resolvedCompanyId, mergedMemory);
    }

    resetDestinationForm();
    showMessage(editingDestinationId ? 'Destino actualizado con éxito.' : 'Destino añadido con éxito.', 'success');
  };

  const handleSaveRoute = async (status, selectedDate = null) => {
    if (!resolvedSelectedTransport?.id) {
      showMessage('Elegí una unidad antes de crear la ruta.', 'error');
      return;
    }

    if (!resolvedSelectedDriver?.id && !newRoute.driverName) {
      showMessage('Elegí un chofer antes de crear la ruta.', 'error');
      return;
    }

    if (newRoute.destinations.length === 0) {
      showMessage('Agrega al menos un destino.', 'error');
      return;
    }

    const destinationsWithCoords = newRoute.destinations.filter(
      (d) => d.latlng && typeof d.latlng.lat === 'number' && typeof d.latlng.lng === 'number'
    );

    if (destinationsWithCoords.length !== newRoute.destinations.length) {
      showMessage(
        'Algunos destinos no tienen coordenadas válidas. Por favor, revisa las direcciones.',
        'error'
      );
      return;
    }

    const routeToSave = JSON.parse(JSON.stringify(newRoute));

    routeToSave.companyId = resolvedSelectedTransport?.companyId || resolvedCompanyId;
    routeToSave.companyName = resolvedSelectedTransport?.companyName || resolvedCompanyName;

    routeToSave.transport = resolvedSelectedTransport?.transportType || newRoute.transport || '';
    routeToSave.transportType = resolvedSelectedTransport?.transportType || newRoute.transportType || '';
    routeToSave.liquidationTransportType =
      resolvedSelectedTransport?.liquidationTransportType ||
      newRoute.liquidationTransportType ||
      routeToSave.transportType ||
      '';

    routeToSave.transporterId = resolvedSelectedTransport?.id || newRoute.transporterId || '';
    routeToSave.transporterSlug = resolvedSelectedTransport?.driverAccessSlug || resolvedSelectedTransport?.slug || newRoute.transporterSlug || '';
    routeToSave.transporterName = resolvedSelectedTransport?.name || newRoute.transporterName || '';
    routeToSave.transportModel = resolvedSelectedTransport?.transportModel || resolvedSelectedTransport?.vehicleLabel || newRoute.transportModel || '';

    const driverSnapshot = buildDriverSnapshot(resolvedSelectedDriver || {}, resolvedSelectedTransport || {});

    routeToSave.driverId = driverSnapshot.driverId || newRoute.driverId || '';
    routeToSave.driverName = driverSnapshot.driverName || newRoute.driverName || '';
    routeToSave.driverLastName = driverSnapshot.driverLastName || newRoute.driverLastName || '';
    routeToSave.driverDni = driverSnapshot.driverDni || newRoute.driverDni || '';
    routeToSave.driverPhone = driverSnapshot.driverPhone || newRoute.driverPhone || '';
    routeToSave.driverPhotoURL = driverSnapshot.driverPhotoURL || newRoute.driverPhotoURL || '';

    const routeIdentity = resolveTransportIdentity({
      ...resolvedSelectedTransport,
      ...newRoute,
      transporterId: routeToSave.transporterId,
      transporterSlug: routeToSave.transporterSlug,
      transporterName: routeToSave.transporterName,
      driverName: routeToSave.driverName,
      companyId: routeToSave.companyId
    });

    routeToSave.identityColor =
      resolvedSelectedTransport?.identityColor || newRoute.identityColor || routeIdentity.identityColor;
    routeToSave.identitySoftColor =
      resolvedSelectedTransport?.identitySoftColor ||
      newRoute.identitySoftColor ||
      routeIdentity.identitySoftColor;

    routeToSave.date = selectedDate || newRoute.date;
    routeToSave.name = `Ruta ${
      routeToSave.transporterName || routeToSave.transport || 'sin transporte'
    } - ${new Date(`${routeToSave.date}T00:00:00`).toLocaleDateString('es-AR')}`;

    routeToSave.createdBy = userId;
    routeToSave.createdAt = serverTimestamp();
    routeToSave.updatedAt = serverTimestamp();
    routeToSave.status = status;

    routeToSave.liquidationSnapshot = buildLiquidationSnapshot({
      transport: resolvedSelectedTransport,
      liquidationSettingsDoc: companyLiquidationSettings
    });

    routeToSave.destinations = routeToSave.destinations.map((dest) => {
      const cleanedDest = {};
      for (const key in dest) {
        if (dest[key] !== undefined) {
          cleanedDest[key] = dest[key];
        }
      }
      return cleanedDest;
    });

    for (const key in routeToSave) {
      if (routeToSave[key] === undefined) {
        delete routeToSave[key];
      }
    }

    try {
      const routesPath = resolveRoutesCollectionPath(routeToSave.companyId);

      if (status !== 'programmed') {
        const routesRef = collection(db, routesPath);
        const existingRoutesSnapshot = await getDocs(routesRef);

        const hasAnotherActiveRouteForTransport = existingRoutesSnapshot.docs.some((docSnap) => {
          const existingRoute = { id: docSnap.id, ...docSnap.data() };

          if (!isRouteOperationallyActive(existingRoute)) return false;

          const sameTransportById =
            routeToSave.transporterId &&
            existingRoute.transporterId &&
            existingRoute.transporterId === routeToSave.transporterId;

          const sameTransportBySlug =
            routeToSave.transporterSlug &&
            existingRoute.transporterSlug &&
            existingRoute.transporterSlug === routeToSave.transporterSlug;

          return sameTransportById || sameTransportBySlug;
        });

        if (hasAnotherActiveRouteForTransport) {
          showMessage(
            'Este transporte ya tiene una ruta activa. Finalizala antes de crear otra operativa.',
            'error'
          );
          return;
        }
      }

      const linkedServiceIdsInRouteDraft = Array.from(
        new Set(
          routeToSave.destinations
            .map((destination) => String(destination?.serviceId || '').trim())
            .filter(Boolean)
        )
      );

      if (linkedServiceIdsInRouteDraft.length > 0) {
        const linkedServiceDocs = await Promise.all(
          linkedServiceIdsInRouteDraft.map(async (serviceId) => {
            const serviceRef = doc(db, resolveServiceDocPath(routeToSave.companyId, serviceId));
            const serviceSnap = await getDoc(serviceRef);
            return serviceSnap.exists() ? { id: serviceId, ...serviceSnap.data() } : null;
          })
        );

        const lockedService = linkedServiceDocs.find((service) => {
          if (!service) return true;
          const normalizedStatus = String(service?.status || '').trim().toLowerCase();
          return normalizedStatus !== 'pending_logistics';
        });

        if (lockedService) {
          const assignedTransportLabel =
            String(lockedService?.transportNameSnapshot || '').trim() ||
            String(lockedService?.driverNameSnapshot || '').trim() ||
            String(lockedService?.routeNameSnapshot || '').trim() ||
            'otra ruta';
          const normalizedLockedStatus = String(lockedService?.status || '').trim().toLowerCase();
          showMessage(
            normalizedLockedStatus === 'in_progress' ||
              normalizedLockedStatus === 'in-progress' ||
              normalizedLockedStatus === 'en_curso' ||
              normalizedLockedStatus === 'en-curso'
              ? `Uno de los servicios ya está en tránsito con ${assignedTransportLabel}. Actualizá la hoja y volvé a intentar.`
              : `Uno de los servicios ya fue tomado por ${assignedTransportLabel}. Actualizá la hoja y volvé a intentar.`,
            'warning'
          );
          return;
        }
      }

      const outboundLoadAssignments = routeToSave.destinations.flatMap((destination) =>
        (destination.operationalLoads || [])
          .filter(
            (load) =>
              load?.stockProductId &&
              resolveLoadStockMode(load) === 'pending_outbound' &&
              STOCK_OUTBOUND_OPERATION_TYPES.has(String(load?.operationType || '').trim().toLowerCase())
          )
          .map((load) => ({
            destinationId: String(destination.id || ''),
            destinationName: String(destination.name || ''),
            destinationContactId: String(destination.contactDocId || ''),
            destinationContactName: String(destination.contactName || destination.name || ''),
            loadId: String(load.id || ''),
            quantity: Math.max(1, Number(load.quantity || 1) || 1),
            productId: String(load.stockProductId || ''),
            productNameSnapshot: String(load.stockProductNameSnapshot || load.title || ''),
            skuSnapshot: String(load.stockSkuSnapshot || ''),
            barcodeSnapshot: String(load.stockBarcodeSnapshot || ''),
            qrCodeSnapshot: String(load.stockQrCodeSnapshot || ''),
            categorySnapshot: String(load.stockCategorySnapshot || load.category || ''),
            unitSnapshot: String(load.stockUnitSnapshot || load.unit || 'unidad'),
            operationType: String(load.operationType || '').trim().toLowerCase() || 'delivery',
            stockOperationReason: String(load.stockOperationReason || 'sale').trim().toLowerCase() || 'sale',
            referenceCode: String(load.referenceCode || '').trim(),
            stockSourceType: String(load.stockSourceType || 'product').trim().toLowerCase() || 'product',
            stockOutboundOrderId: String(load.stockOutboundOrderId || '').trim(),
            stockOutboundOrderNumber: String(load.stockOutboundOrderNumber || '').trim(),
            stockOutboundOrderLineId: String(load.stockOutboundOrderLineId || '').trim()
          }))
      );

      const outboundRequestedByProduct = outboundLoadAssignments
        .filter((item) => item.stockSourceType !== 'outbound_order')
        .reduce((acc, item) => {
        if (!item.productId) return acc;
        acc[item.productId] = (acc[item.productId] || 0) + item.quantity;
        return acc;
      }, {});

      const outboundUnavailable = Object.entries(outboundRequestedByProduct).find(([productId, requestedQty]) => {
        const product = stockProductsById[String(productId)];
        if (!product) return true;
        const buckets = product.stockBuckets || {};
        const availableQty = Math.max(
          0,
          Number(buckets.physicalQty || 0) - Number(buckets.pendingOutboundQty || 0)
        );
        return availableQty < requestedQty;
      });

      if (outboundUnavailable) {
        const [productId, requestedQty] = outboundUnavailable;
        const product = stockProductsById[String(productId)];
        const buckets = product?.stockBuckets || {};
        const availableQty = Math.max(
          0,
          Number(buckets.physicalQty || 0) - Number(buckets.pendingOutboundQty || 0)
        );
        showMessage(
          `No alcanza el disponible de ${product?.name || 'ese producto'} para esta salida. Disponible ${availableQty}, pedido ${requestedQty}.`,
          'error'
        );
        return;
      }

      const docRef = await addDoc(collection(db, routesPath), routeToSave);

      await setDoc(
        doc(db, `artifacts/${appId}/logisticsRoutes`, docRef.id),
        {
          ...routeToSave,
          companyId: routeToSave.companyId || resolvedCompanyId,
          companyName: routeToSave.companyName || resolvedCompanyName,
          legacySourceId: docRef.id,
          updatedAt: serverTimestamp(),
        },
        { merge: true }
      );

      const linkedServiceIdsInRoute = Array.from(
        new Set(
          routeToSave.destinations
            .map((destination) => String(destination?.serviceId || '').trim())
            .filter(Boolean)
        )
      );

      if (linkedServiceIdsInRoute.length > 0) {
        const serviceAssignmentBatch = writeBatch(db);
        linkedServiceIdsInRoute.forEach((serviceId) => {
          serviceAssignmentBatch.update(doc(db, resolveServiceDocPath(routeToSave.companyId, serviceId)), {
            status: 'assigned',
            statusLabel: 'Asignado',
            routeId: docRef.id,
            routeNameSnapshot: routeToSave.name || '',
            transportId: routeToSave.transporterId || '',
            transportNameSnapshot: routeToSave.transporterName || '',
            driverId: routeToSave.driverId || '',
            driverNameSnapshot: [routeToSave.driverName, routeToSave.driverLastName].filter(Boolean).join(' ').trim(),
            assignedAt: serverTimestamp(),
            updatedAt: serverTimestamp(),
          });
        });
        await serviceAssignmentBatch.commit();
      }

      const purchaseLoadAssignments = routeToSave.destinations.flatMap((destination) =>
        (destination.operationalLoads || [])
          .filter(
            (load) =>
              load?.purchaseLineId &&
              String(load?.stockMode || '').trim().toLowerCase() === 'pending_inbound'
          )
          .map((load) => ({
            purchaseId: String(load.purchaseId || ''),
            purchaseLineId: String(load.purchaseLineId || ''),
            quantity: Math.max(1, Number(load.quantity || 1) || 1)
          }))
      );

      if (purchaseLoadAssignments.length > 0) {
        const assignmentBatch = writeBatch(db);
        const lineAdjustments = purchaseLoadAssignments.reduce((acc, item) => {
          if (!item.purchaseLineId) return acc;
          acc[item.purchaseLineId] = (acc[item.purchaseLineId] || 0) + item.quantity;
          return acc;
        }, {});

        const affectedPurchaseIds = new Set();
        Object.entries(lineAdjustments).forEach(([purchaseLineId, assignedQty]) => {
          const currentLine = pendingPurchaseLines.find((line) => String(line.id) === String(purchaseLineId));
          if (!currentLine) return;
          affectedPurchaseIds.add(String(currentLine.purchaseId || ''));
          const nextAssigned = Number(currentLine.assignedQuantity || 0) + assignedQty;
          const nextPending = Math.max(0, Number(currentLine.pendingPickupQuantity || 0) - assignedQty);
          const nextStatus = nextPending > 0 ? 'partially_assigned' : 'assigned_to_route';
          const movementRef = doc(collection(db, resolveStockMovementsCollectionPath(resolvedCompanyId)));
          assignmentBatch.update(doc(db, resolveStockPurchaseLineDocPath(resolvedCompanyId, purchaseLineId)), {
            assignedQuantity: nextAssigned,
            pendingPickupQuantity: nextPending,
            status: nextStatus,
            lastRouteId: docRef.id,
            lastRouteCodeSnapshot: routeToSave.name || '',
            lastMovementId: movementRef.id,
            lastMovementStatus: 'applied',
            updatedAt: new Date()
          });
          assignmentBatch.set(movementRef, {
            companyId: resolvedCompanyId,
            productId: currentLine.productId || '',
            productNameSnapshot: currentLine.productNameSnapshot || '',
            skuSnapshot: currentLine.skuSnapshot || '',
            categorySnapshot: currentLine.categorySnapshot || '',
            unitSnapshot: currentLine.unitSnapshot || 'unidad',
            movementType: 'purchase_assigned_to_route',
            quantity: assignedQty,
            stockBucketsImpact: {
              pendingInboundDelta: 0,
              inboundTransitDelta: 0,
              physicalDelta: 0,
              pendingOutboundDelta: 0,
              outboundTransitDelta: 0,
              repairDelta: 0,
              returnedDelta: 0,
              damagedDelta: 0
            },
            status: 'applied',
            sourceType: 'route',
            sourceId: docRef.id,
            sourceLineId: purchaseLineId,
            supplierId: currentLine.supplierId || '',
            supplierNameSnapshot: currentLine.supplierNameSnapshot || '',
            routeId: docRef.id,
            routeCodeSnapshot: routeToSave.name || '',
            transportId: routeToSave.transporterId || '',
            transportNameSnapshot: routeToSave.transporterName || routeToSave.transportName || '',
            driverId: routeToSave.driverId || '',
            driverNameSnapshot: [routeToSave.driverName, routeToSave.driverLastName].filter(Boolean).join(' ').trim(),
            driverPhotoURLSnapshot: routeToSave.driverPhotoURL || routeToSave.currentDriverPhotoURL || routeToSave.photoURL || '',
            note: 'Pendiente de retiro asignado automáticamente a una ruta.',
            createdByUserId: userId,
            createdByUserName: userId || 'Usuario',
            createdAt: new Date(),
            appliedAt: new Date(),
            cancelledAt: null
          });
        });

        affectedPurchaseIds.forEach((purchaseId) => {
          if (!purchaseId) return;
          const purchaseLinesForPurchase = pendingPurchaseLines
            .filter((line) => String(line.purchaseId || '') === purchaseId)
            .map((line) => {
              const assignedDelta = lineAdjustments[String(line.id)] || 0;
              if (!assignedDelta) return line;
              const nextAssigned = Number(line.assignedQuantity || 0) + assignedDelta;
              const nextPending = Math.max(0, Number(line.pendingPickupQuantity || 0) - assignedDelta);
              return {
                ...line,
                assignedQuantity: nextAssigned,
                pendingPickupQuantity: nextPending,
                status: nextPending > 0 ? 'partially_assigned' : 'assigned_to_route',
              };
            });
          const purchaseSummary = buildPurchaseHeaderSummary({ id: purchaseId }, purchaseLinesForPurchase);
          assignmentBatch.update(doc(db, resolveStockPurchaseDocPath(resolvedCompanyId, purchaseId)), {
            status: purchaseSummary.status,
            updatedAt: new Date(),
            pendingLinesCount: purchaseSummary.pendingLinesCount,
            completedLinesCount: purchaseSummary.completedLinesCount,
            closedAt: purchaseSummary.closedAt,
            assignedRouteIds: arrayUnion(docRef.id)
          });
        });

        await assignmentBatch.commit();
      }

      if (outboundLoadAssignments.length > 0) {
        const outboundBatch = writeBatch(db);
        const now = new Date();
        const reservedOutboundLoads = outboundLoadAssignments.filter(
          (item) => item.stockSourceType !== 'outbound_order'
        );
        const preparedOutboundLoads = outboundLoadAssignments.filter(
          (item) => item.stockSourceType === 'outbound_order'
        );

        reservedOutboundLoads.forEach((item) => {
          const movementRef = doc(collection(db, resolveStockMovementsCollectionPath(resolvedCompanyId)));
          outboundBatch.update(doc(db, resolveStockProductDocPath(resolvedCompanyId, item.productId)), {
            'stockBuckets.pendingOutboundQty': increment(item.quantity),
            'stockSummary.lastMovementAt': now,
            updatedAt: now
          });
          outboundBatch.set(movementRef, {
            companyId: resolvedCompanyId,
            productId: item.productId,
            productNameSnapshot: item.productNameSnapshot,
            skuSnapshot: item.skuSnapshot,
            categorySnapshot: item.categorySnapshot,
            unitSnapshot: item.unitSnapshot,
            movementType: 'stock_outbound_reserved',
            quantity: item.quantity,
            stockBucketsImpact: {
              pendingInboundDelta: 0,
              inboundTransitDelta: 0,
              physicalDelta: 0,
              pendingOutboundDelta: item.quantity,
              outboundTransitDelta: 0,
              repairDelta: 0,
              returnedDelta: 0,
              damagedDelta: 0
            },
            status: 'applied',
            sourceType: 'route',
            sourceId: docRef.id,
            sourceLineId: item.loadId,
            routeId: docRef.id,
            routeCodeSnapshot: routeToSave.name || '',
            transportId: routeToSave.transporterId || '',
            transportNameSnapshot: routeToSave.transporterName || routeToSave.transportName || '',
            driverId: routeToSave.driverId || '',
            driverNameSnapshot: [routeToSave.driverName, routeToSave.driverLastName].filter(Boolean).join(' ').trim(),
            driverPhotoURLSnapshot: routeToSave.driverPhotoURL || routeToSave.currentDriverPhotoURL || routeToSave.photoURL || '',
            destinationId: item.destinationId,
            destinationNameSnapshot: item.destinationName,
            customerId: item.destinationContactId,
            customerNameSnapshot: item.destinationContactName,
            operationType: item.operationType,
            operationReason: item.stockOperationReason,
            referenceCodeSnapshot: item.referenceCode,
            note: 'Producto comprometido para una salida operativa en ruta.',
            createdByUserId: userId,
            createdByUserName: userId || 'Usuario',
            createdAt: now,
            appliedAt: now,
            cancelledAt: null
          });
        });

        const preparedGroupedByOrder = preparedOutboundLoads.reduce((acc, item) => {
          if (!item.stockOutboundOrderId || !item.stockOutboundOrderLineId) return acc;
          if (!acc[item.stockOutboundOrderId]) acc[item.stockOutboundOrderId] = [];
          acc[item.stockOutboundOrderId].push(item);
          return acc;
        }, {});

        Object.entries(preparedGroupedByOrder).forEach(([orderId, items]) => {
          const currentOrder = stockOutboundOrdersById[String(orderId)];
          if (!currentOrder) return;

          const nextLines = (currentOrder.lines || []).map((line) => {
            const matchingItems = items.filter(
              (item) => String(item.stockOutboundOrderLineId) === String(line.id)
            );
            if (!matchingItems.length) return line;
            const assignedDelta = matchingItems.reduce((sum, item) => sum + Number(item.quantity || 0), 0);
            const nextAssigned = Number(line.assignedQuantity || 0) + assignedDelta;
            const nextPending = Math.max(
              0,
              Number(line.pendingDeliveryQuantity || 0) - assignedDelta
            );
            return {
              ...line,
              assignedQuantity: nextAssigned,
              pendingDeliveryQuantity: nextPending,
              status: nextPending > 0 ? 'partially_assigned' : 'assigned_to_route',
              updatedAt: now,
            };
          });

          const orderSummary = buildOutboundOrderHeaderSummary(currentOrder, nextLines);
          outboundBatch.update(doc(db, resolveStockOutboundOrderDocPath(resolvedCompanyId, orderId)), {
            lines: nextLines,
            status: orderSummary.status,
            pendingDeliveryQuantity: orderSummary.totals.pendingDeliveryQuantity,
            assignedQuantity: orderSummary.totals.assignedQuantity,
            inTransitQuantity: orderSummary.totals.inTransitQuantity,
            deliveredQuantity: orderSummary.totals.deliveredQuantity,
            routeIds: arrayUnion(docRef.id),
            updatedAt: now,
            closedAt: orderSummary.closedAt,
          });

          items.forEach((item) => {
            const movementRef = doc(collection(db, resolveStockMovementsCollectionPath(resolvedCompanyId)));
            outboundBatch.set(movementRef, {
              companyId: resolvedCompanyId,
              productId: item.productId,
              productNameSnapshot: item.productNameSnapshot,
              skuSnapshot: item.skuSnapshot,
              categorySnapshot: item.categorySnapshot,
              unitSnapshot: item.unitSnapshot,
              movementType: 'stock_outbound_assigned_to_route',
              quantity: item.quantity,
              stockBucketsImpact: {
                pendingInboundDelta: 0,
                inboundTransitDelta: 0,
                physicalDelta: 0,
                pendingOutboundDelta: 0,
                outboundTransitDelta: 0,
                repairDelta: 0,
                returnedDelta: 0,
                damagedDelta: 0
              },
              status: 'applied',
              sourceType: 'route',
              sourceId: docRef.id,
              sourceLineId: item.loadId,
              routeId: docRef.id,
              routeCodeSnapshot: routeToSave.name || '',
              transportId: routeToSave.transporterId || '',
              transportNameSnapshot: routeToSave.transporterName || routeToSave.transportName || '',
              driverId: routeToSave.driverId || '',
              driverNameSnapshot: [routeToSave.driverName, routeToSave.driverLastName].filter(Boolean).join(' ').trim(),
              driverPhotoURLSnapshot: routeToSave.driverPhotoURL || routeToSave.currentDriverPhotoURL || routeToSave.photoURL || '',
              destinationId: item.destinationId,
              destinationNameSnapshot: item.destinationName,
              customerId: item.destinationContactId,
              customerNameSnapshot: item.destinationContactName,
              outboundOrderId: item.stockOutboundOrderId,
              outboundOrderNumber: item.stockOutboundOrderNumber,
              outboundOrderLineId: item.stockOutboundOrderLineId,
              operationType: item.operationType,
              operationReason: item.stockOperationReason,
              referenceCodeSnapshot: item.referenceCode,
              note: 'Pedido de salida asignado a una ruta operativa.',
              createdByUserId: userId,
              createdByUserName: userId || 'Usuario',
              createdAt: now,
              appliedAt: now,
              cancelledAt: null
            });
          });
        });

        await outboundBatch.commit();
      }

      if (status !== 'programmed' && routeToSave.companyId) {
        const selectedTransportDocRef =
          routeToSave.transporterId
            ? doc(db, resolveTransportsCollectionPath(routeToSave.companyId), routeToSave.transporterId)
            : null;
        const selectedDriverDocRef =
          routeToSave.driverId
            ? doc(db, resolveDriversCollectionPath(routeToSave.companyId), routeToSave.driverId)
            : null;

        const previousTransportDriverId =
          resolvedSelectedTransport?.currentDriverId &&
          resolvedSelectedTransport.currentDriverId !== routeToSave.driverId
            ? resolvedSelectedTransport.currentDriverId
            : '';
        const previousDriverTransportId =
          resolvedSelectedDriver?.currentTransportId &&
          resolvedSelectedDriver.currentTransportId !== routeToSave.transporterId
            ? resolvedSelectedDriver.currentTransportId
            : '';

        const resolvedDriverAccessSlug =
          resolvedSelectedDriver?.accessSlug ||
          resolvedSelectedDriver?.driverAccessSlug ||
          resolvedSelectedTransport?.driverAccessSlug ||
          resolvedSelectedTransport?.slug ||
          '';
        const resolvedDriverPassword =
          resolvedSelectedDriver?.password ||
          resolvedSelectedDriver?.driverPassword ||
          resolvedSelectedTransport?.driverPassword ||
          resolvedSelectedTransport?.password ||
          '';

        if (previousTransportDriverId) {
          await updateDoc(
            doc(db, resolveDriversCollectionPath(routeToSave.companyId), previousTransportDriverId),
            {
              currentTransportId: '',
              currentTransportName: '',
              currentTransportType: '',
              currentTransportStatus: 'available',
              driverStatus: 'available',
              status: 'available',
              updatedAt: serverTimestamp()
            }
          );
        }

        if (previousDriverTransportId) {
          await updateDoc(
            doc(db, resolveTransportsCollectionPath(routeToSave.companyId), previousDriverTransportId),
            {
              currentDriverId: '',
              currentDriverName: '',
              currentDriverLastName: '',
              currentDriverDni: '',
              currentDriverPhone: '',
              currentDriverPhotoURL: '',
              driverName: '',
              driverLastName: '',
              driverDni: '',
              phone: '',
              photoURL: '',
              slug: '',
              driverAccessSlug: '',
              password: '',
              driverPassword: '',
              transportStatus: 'available',
              updatedAt: serverTimestamp()
            }
          );
        }

        if (selectedTransportDocRef) {
          await updateDoc(selectedTransportDocRef, {
            currentDriverId: routeToSave.driverId || '',
            currentDriverName: routeToSave.driverName || '',
            currentDriverLastName: routeToSave.driverLastName || '',
            currentDriverDni: routeToSave.driverDni || '',
            currentDriverPhone: routeToSave.driverPhone || '',
            currentDriverPhotoURL: routeToSave.driverPhotoURL || '',
            driverName: routeToSave.driverName || '',
            driverLastName: routeToSave.driverLastName || '',
            driverDni: routeToSave.driverDni || '',
            phone: routeToSave.driverPhone || '',
            photoURL: routeToSave.driverPhotoURL || '',
            slug: resolvedDriverAccessSlug || '',
            driverAccessSlug: resolvedDriverAccessSlug || '',
            password: resolvedDriverPassword || '',
            driverPassword: resolvedDriverPassword || '',
            transportStatus:
              resolvedSelectedTransport?.transportStatus &&
              resolvedSelectedTransport.transportStatus !== 'available'
                ? resolvedSelectedTransport.transportStatus
                : 'assigned',
            updatedAt: serverTimestamp()
          });
        }

        if (selectedDriverDocRef) {
          await updateDoc(selectedDriverDocRef, {
            currentTransportId: routeToSave.transporterId || '',
            currentTransportName: routeToSave.transporterName || '',
            currentTransportType: routeToSave.transportType || routeToSave.transport || '',
            currentTransportStatus:
              resolvedSelectedTransport?.transportStatus &&
              resolvedSelectedTransport.transportStatus !== 'available'
                ? resolvedSelectedTransport.transportStatus
                : 'assigned',
            driverStatus: 'assigned',
            status: 'assigned',
            updatedAt: serverTimestamp()
          });
        }
      }

      addLogisticsNotification(`Nueva ruta creada: ${routeToSave.name}`);
      showMessage('Hoja de ruta guardada con éxito.', 'success');

      onCreateSuccess({
        ...routeToSave,
        id: docRef.id
      });
    } catch (error) {
      console.error('Error al guardar la ruta:', error);
      showMessage(
        'Error al guardar la ruta. Revisa la consola para más detalles.',
        'error'
      );
    }
  };

  const handleProgramRouteClick = () => {
    setShowDatePickerModal(true);
  };

  const handleDateSelectedForProgrammedRoute = (date) => {
    handleSaveRoute('programmed', date);
    setShowDatePickerModal(false);
  };

  const handleRemoveDestination = (destinationId) => {
    setNewRoute((prev) => ({
      ...prev,
      destinations: prev.destinations
        .filter((d) => d.id !== destinationId)
        .map((d, index) => ({ ...d, order: index }))
    }));
  };

  const addressStatusTone =
    addressValidation.status === 'success'
      ? 'border-emerald-200 bg-emerald-50 text-emerald-700'
      : addressValidation.status === 'error'
      ? 'border-rose-200 bg-rose-50 text-rose-700'
      : addressValidation.status === 'loading'
      ? 'border-cyan-200 bg-cyan-50 text-cyan-700'
      : 'border-slate-200 bg-slate-50 text-slate-600';

  return (
      <div className="grid grid-cols-1 gap-6 lg:grid-cols-12">
      <section className="lg:order-1 lg:col-span-7" id="logistics-route-hero">
        <div className="rounded-[28px] border border-indigo-100 bg-[linear-gradient(180deg,#f6f7ff_0%,#ffffff_100%)] p-6 shadow-[0_18px_38px_rgba(99,102,241,0.08)]">
          <PanelHeader
            eyebrow="Planificación"
            title="Nueva hoja de ruta"
            description="Abrí la hoja, seguí las estaciones operativas y cargá el destino con una lectura clara según el plan activo."
            extra={
              <>
                <SectionBadge tone="indigo">{planLabel}</SectionBadge>
                {onStartTutorial ? (
                  <button
                    type="button"
                    onClick={onStartTutorial}
                    className={`inline-flex items-center justify-center gap-2 rounded-2xl border px-4 py-2.5 text-sm font-bold transition ${
                      tutorialSeen
                        ? 'border-fuchsia-200 bg-white text-fuchsia-700 shadow-[0_10px_24px_rgba(217,70,239,0.12)]'
                        : 'border-fuchsia-300 bg-[linear-gradient(180deg,#fff7ff_0%,#f5d0fe_100%)] text-fuchsia-800 shadow-[0_0_0_1px_rgba(217,70,239,0.08),0_14px_32px_rgba(217,70,239,0.18)] animate-pulse'
                    }`}
                  >
                    <Icon path="M11.25 9l3 3-3 3m-6-3h9m6.75 0a8.25 8.25 0 11-16.5 0 8.25 8.25 0 0116.5 0z" className="h-4 w-4" />
                    Tutorial guiado
                  </button>
                ) : null}
              </>
            }
          />

          <button
            type="button"
            onClick={() => setRoutePanelExpanded((prev) => !prev)}
            className={`mb-4 inline-flex w-full items-center justify-center gap-2 rounded-[18px] border px-4 py-3 text-sm font-bold transition ${
              routePanelExpanded
                ? 'border-indigo-200 bg-white/90 text-indigo-700'
                : 'border-indigo-300 bg-[linear-gradient(180deg,#eef2ff_0%,#ffffff_100%)] text-indigo-800 animate-pulse'
            }`}
          >
            <Icon
              path={routePanelExpanded ? 'M19.5 12h-15' : 'M12 4.5v15m7.5-7.5h-15'}
              className="h-4 w-4"
            />
            {routePanelExpanded ? 'Plegar nueva hoja de ruta' : 'Abrir nueva hoja de ruta'}
          </button>

          <div
            className={`overflow-hidden transition-all duration-500 ease-out ${
              routePanelExpanded ? 'max-h-[9800px] opacity-100' : 'max-h-0 opacity-0'
            }`}
          >
          <div className="mb-6 rounded-[24px] border border-indigo-200 bg-[linear-gradient(135deg,#eef2ff_0%,#ffffff_100%)] p-4 shadow-[0_16px_32px_rgba(99,102,241,0.08)]">
            <div className="flex flex-col gap-3 lg:flex-row lg:items-center lg:justify-between">
              <div>
                <p className="text-[11px] font-semibold uppercase tracking-[0.24em] text-indigo-600">
                  Ruta guiada
                </p>
                <h4 className="mt-1 text-lg font-bold text-slate-900">
                  Abrí la estación que necesitás y avanzá sin mezclar capas
                </h4>
                <p className="mt-1 text-sm text-slate-600">
                  Primero elegís chofer y unidad. Después podés usar contratos, programados o pendientes. Y cuando ya sabés la parada, cargás el destino.
                </p>
              </div>
              {!hasStartedDestinationForm ? (
                <button
                  type="button"
                  onClick={() => setDestinationSectionExpanded(true)}
                  className="inline-flex items-center justify-center gap-2 rounded-2xl border border-emerald-200 bg-white px-4 py-3 text-sm font-bold text-emerald-700 shadow-sm transition hover:bg-emerald-50"
                >
                  <Icon path="M12 4.5v15m7.5-7.5h-15" className="h-4 w-4" />
                  Ir a cargar destino
                </button>
              ) : (
                <div className="rounded-2xl border border-emerald-200 bg-emerald-50 px-4 py-3 text-sm font-semibold text-emerald-700">
                  Destino en preparación
                </div>
              )}
            </div>
          </div>

          <div className="mb-6 grid grid-cols-1 gap-3 md:grid-cols-4">
            <div className="rounded-2xl border border-slate-200 bg-slate-50 px-4 py-3">
              <p className="text-[11px] font-semibold uppercase tracking-[0.22em] text-slate-500">
                Plan activo
              </p>
              <p className="mt-2 text-sm font-semibold text-slate-800">{planLabel}</p>
            </div>
            <div className="rounded-2xl border border-slate-200 bg-slate-50 px-4 py-3">
              <p className="text-[11px] font-semibold uppercase tracking-[0.22em] text-slate-500">
                Unidad operativa
              </p>
              <p className="mt-2 text-sm font-semibold text-slate-800">
                {newRoute.transporterName || 'Sin unidad'}
              </p>
            </div>
            <div className="rounded-2xl border border-slate-200 bg-slate-50 px-4 py-3">
              <p className="text-[11px] font-semibold uppercase tracking-[0.22em] text-slate-500">
                Fecha operativa
              </p>
              <p className="mt-2 text-sm font-semibold text-slate-800">
                {new Date(`${newRoute.date}T00:00:00`).toLocaleDateString('es-AR')}
              </p>
            </div>
            <div className="rounded-2xl border border-slate-200 bg-slate-50 px-4 py-3">
              <p className="text-[11px] font-semibold uppercase tracking-[0.22em] text-slate-500">
                Destinos cargados
              </p>
              <p className="mt-2 text-sm font-semibold text-slate-800">
                {newRoute.destinations.length} en armado
              </p>
            </div>
          </div>

          <RouteFlowSection
            id="logistics-route-route-meta"
            step="1"
            tone="indigo"
            title="Asignación operativa"
            description="Elegí chofer, unidad y fecha para abrir la hoja sobre una base correcta."
            expanded={assignmentSectionExpanded}
            onToggle={() => setAssignmentSectionExpanded((prev) => !prev)}
            badge={
              selectedDriverId || selectedTransportId ? (
                <span className="rounded-full border border-indigo-200 bg-white px-2.5 py-1 text-[10px] font-bold uppercase tracking-[0.16em] text-indigo-700">
                  En preparación
                </span>
              ) : null
            }
            maxHeightClassName="max-h-[1600px]"
          >
          <div className="space-y-3">
            <div className="grid grid-cols-1 gap-3 md:grid-cols-2">
              <div>
                <label className="mb-2 block text-sm font-medium text-slate-700">
                  Chofer
                </label>
                <select
                  value={selectedDriverId}
                  onChange={(e) => setSelectedDriverId(e.target.value)}
                  className={INPUT_BASE_CLASS}
                >
                  <option value="">Seleccionar chofer</option>
                  {driverSelectionOptions.map(({ driver, disabled, reason }) => (
                    <option key={driver.id} value={driver.id} disabled={disabled}>
                      {buildDriverDisplayName(driver, 'Chofer sin nombre')}{reason ? ` · ${reason}` : ''}
                    </option>
                  ))}
                </select>
              </div>

              <div>
                <label className="mb-2 block text-sm font-medium text-slate-700">
                  Unidad
                </label>
                <select
                  value={selectedTransportId}
                  onChange={(e) => setSelectedTransportId(e.target.value)}
                  className={INPUT_BASE_CLASS}
                >
                  <option value="">Seleccionar unidad</option>
                  {transportSelectionOptions.map(({ transport, disabled, reason }) => {
                    const transportSummary = transportAssignmentSummaryById.get(String(transport?.id || '').trim());
                    const contractedLabel = transportSummary?.contractedClients?.length
                      ? ` · Fijo con ${transportSummary.contractedClients.slice(0, 2).join(', ')}${
                          transportSummary.contractedClients.length > 2 ? ` +${transportSummary.contractedClients.length - 2}` : ''
                        }`
                      : '';

                    return (
                      <option key={transport.id} value={transport.id} disabled={disabled}>
                        {buildTransportDisplayName(transport, 'Unidad')}
                        {buildTransportMeta(transport) ? ` · ${buildTransportMeta(transport)}` : ''}
                        {contractedLabel}
                        {reason ? ` · ${reason}` : ''}
                      </option>
                    );
                  })}
                </select>
              </div>
            </div>

            <div className="rounded-2xl border border-slate-200 bg-slate-50/80 px-4 py-3 text-xs font-medium text-slate-600">
              Los choferes y unidades ya asignados, en ruta o fuera de servicio aparecen bloqueados para mantener una lectura operativa coherente.
            </div>

            <div className="grid grid-cols-1 gap-3 md:grid-cols-2">
              <div className="rounded-2xl border border-slate-200 bg-slate-50 px-4 py-3">
                <p className="text-[11px] font-semibold uppercase tracking-[0.22em] text-slate-500">Chofer operativo</p>
                <p className="mt-2 text-sm font-semibold text-slate-800">
                  {buildDriverDisplayName(resolvedSelectedDriver || {}, newRoute.driverName || 'Sin chofer')}
                </p>
              </div>
              <div className="rounded-2xl border border-slate-200 bg-slate-50 px-4 py-3">
                <p className="text-[11px] font-semibold uppercase tracking-[0.22em] text-slate-500">Unidad operativa</p>
                <p className="mt-2 text-sm font-semibold text-slate-800">
                  {buildTransportDisplayName(resolvedSelectedTransport || {}, 'Sin unidad')}
                </p>
                <p className="mt-1 text-xs text-slate-500">
                  {buildTransportMeta(resolvedSelectedTransport || {}) || 'Disponible para asignar'}
                </p>
                {selectedTransportAssignmentSummary?.contractedClients?.length ? (
                  <div className="mt-3 rounded-2xl border border-cyan-200 bg-cyan-50/80 px-3 py-3">
                    <div className="flex flex-wrap items-center gap-2">
                      <span className="rounded-full border border-cyan-200 bg-white px-2.5 py-1 text-[10px] font-black uppercase tracking-[0.18em] text-cyan-700">
                        Cliente fijo
                      </span>
                      <span className="text-xs font-semibold text-cyan-900">
                        {selectedTransportAssignmentSummary.contractedClients.slice(0, 2).join(' · ')}
                        {selectedTransportAssignmentSummary.contractedClients.length > 2
                          ? ` +${selectedTransportAssignmentSummary.contractedClients.length - 2}`
                          : ''}
                      </span>
                    </div>
                    <p className="mt-2 text-[11px] leading-5 text-cyan-700">
                      Esta unidad ya tiene servicio contratado con{' '}
                      {selectedTransportAssignmentSummary.contractedClients.slice(0, 3).join(', ')}
                      {selectedTransportAssignmentSummary.contractedClients.length > 3
                        ? ` y ${selectedTransportAssignmentSummary.contractedClients.length - 3} más`
                        : ''}
                      .
                    </p>
                  </div>
                ) : null}
                {selectedTransportAssignmentSummary?.pendingServiceClients?.length ? (
                  <div className="mt-3 rounded-2xl border border-violet-200 bg-violet-50/80 px-3 py-3">
                    <div className="flex flex-wrap items-center gap-2">
                      <span className="rounded-full border border-violet-200 bg-white px-2.5 py-1 text-[10px] font-black uppercase tracking-[0.18em] text-violet-700">
                        Pendientes sugeridos
                      </span>
                      <span className="text-xs font-semibold text-violet-900">
                        {selectedTransportAssignmentSummary.pendingServiceClients.length} servicio
                        {selectedTransportAssignmentSummary.pendingServiceClients.length === 1 ? '' : 's'}
                      </span>
                    </div>
                    <div className="mt-2 flex flex-wrap gap-2">
                      {selectedTransportAssignmentSummary.pendingServiceClients.slice(0, 3).map((clientName, index) => (
                        <span
                          key={`${clientName}-${index}`}
                          className="rounded-full border border-violet-200 bg-white px-2.5 py-1 text-[11px] font-semibold text-violet-700"
                        >
                          {clientName}
                        </span>
                      ))}
                      {selectedTransportAssignmentSummary.pendingServiceClients.length > 3 ? (
                        <span className="rounded-full border border-violet-200 bg-white px-2.5 py-1 text-[11px] font-semibold text-violet-700">
                          +{selectedTransportAssignmentSummary.pendingServiceClients.length - 3} más
                        </span>
                      ) : null}
                    </div>
                  </div>
                ) : null}
              </div>
            </div>

            <div>
              <label htmlFor="route-date" className="mb-2 block text-sm font-medium text-slate-700">
                Fecha de la ruta
              </label>

              <input
                id="route-date"
                type="date"
                value={newRoute.date}
                onChange={(e) =>
                  setNewRoute((prev) => ({ ...prev, date: e.target.value }))
                }
                className={INPUT_BASE_CLASS}
              />
            </div>
          </div>
          </RouteFlowSection>

          <div className="mb-6 mt-6 space-y-4">
            <RouteFlowSection
              id="logistics-route-scheduled-services"
              step="2"
              tone="cyan"
              title="Contratos y programados"
              description="Revisá lo pactado para esta unidad y activá servicios recurrentes o excepciones del día."
              expanded={showScheduledTemplatesPanel}
              onToggle={() => setShowScheduledTemplatesPanel((prev) => !prev)}
              badge={
                <span className="rounded-full border border-cyan-200 bg-white px-2.5 py-1 text-[10px] font-bold uppercase tracking-[0.16em] text-cyan-700">
                  {routeScheduledTemplates.length} programado{routeScheduledTemplates.length === 1 ? '' : 's'}
                </span>
              }
            >
            <div
              className="rounded-[24px] border border-cyan-200 bg-[linear-gradient(180deg,#ecfeff_0%,#ffffff_100%)] shadow-[0_16px_35px_rgba(8,145,178,0.08)]"
            >
              <button
                type="button"
                onClick={() => setShowScheduledTemplatesPanel((prev) => !prev)}
                className="flex w-full items-center justify-between gap-3 px-4 py-4 text-left"
              >
                <div>
                  <p className="text-[11px] font-semibold uppercase tracking-[0.22em] text-cyan-600">
                    Servicios programados de la semana
                  </p>
                  <h4 className="mt-1 text-base font-bold text-slate-900">
                    Servicios listos para activar en esta ruta
                  </h4>
                  <p className="mt-1 text-xs leading-5 text-slate-500">
                    {routeScheduledTodayTemplates.length > 0
                      ? `${routeScheduledTodayTemplates.length} listo${routeScheduledTodayTemplates.length === 1 ? '' : 's'} para hoy`
                      : 'Sin viajes pactados para este día'}
                    {routeScheduledExceptionTemplates.length > 0
                      ? ` · ${routeScheduledExceptionTemplates.length} excepción${routeScheduledExceptionTemplates.length === 1 ? '' : 'es'} disponible${routeScheduledExceptionTemplates.length === 1 ? '' : 's'}`
                      : ''}
                  </p>
                </div>
                <div className="flex items-center gap-3">
                  <SectionBadge tone={routeScheduledTemplates.length > 0 ? 'cyan' : 'slate'}>
                    {routeScheduledTemplates.length} servicio{routeScheduledTemplates.length === 1 ? '' : 's'}
                  </SectionBadge>
                  <div className="flex h-10 w-10 items-center justify-center rounded-2xl border border-cyan-200 bg-white/90">
                    <Icon
                      path={showScheduledTemplatesPanel ? 'M19.5 8.25l-7.5 7.5-7.5-7.5' : 'M8.25 4.5l7.5 7.5-7.5 7.5'}
                      className="h-4 w-4 text-cyan-700"
                    />
                  </div>
                </div>
              </button>

              {showScheduledTemplatesPanel ? (
                <div className="border-t border-cyan-200 px-4 pb-4 pt-4">
                  <div className="grid gap-2 md:grid-cols-7">
                    {routeWeekDays.map((day) => (
                      <button
                        key={day.dateValue}
                        type="button"
                        onClick={() => setNewRoute((prev) => ({ ...prev, date: day.dateValue }))}
                        className={`rounded-2xl border px-3 py-3 text-left transition ${
                          day.isSelected
                            ? 'border-cyan-300 bg-cyan-600 text-white shadow-[0_14px_30px_rgba(8,145,178,0.22)]'
                            : 'border-cyan-100 bg-white/90 text-slate-700 hover:border-cyan-200 hover:bg-cyan-50'
                        }`}
                      >
                        <p className={`text-[10px] font-semibold uppercase tracking-[0.18em] ${day.isSelected ? 'text-cyan-100' : 'text-cyan-700'}`}>
                          {day.shortLabel}
                        </p>
                        <p className="mt-1 text-base font-black">
                          {day.date.getDate()}
                        </p>
                        <p className={`mt-1 text-[11px] ${day.isSelected ? 'text-cyan-50' : 'text-slate-500'}`}>
                          {day.date.toLocaleDateString('es-AR', { month: 'short' })}
                        </p>
                      </button>
                    ))}
                  </div>

                  {routeScheduledTemplates.length > 0 ? (
                    <div className="mt-4 space-y-3">
                  {routeScheduledTodayTemplates.length > 0 ? (
                    <div className="space-y-2">
                      {routeScheduledTodayTemplates.slice(0, 5).map((template) => (
                        <div
                          key={`route-template-${template.id}`}
                          className="rounded-2xl border border-cyan-200 bg-white/95 px-4 py-3 shadow-sm"
                        >
                          <div className="flex flex-wrap items-center justify-between gap-3">
                            <div className="min-w-0 flex-1">
                              <div className="flex flex-wrap items-center gap-2">
                                <p className="text-sm font-bold text-slate-900">
                                  {template.clientNameSnapshot || 'Cliente'}
                                </p>
                                <span className="rounded-full border border-cyan-200 bg-cyan-50 px-2.5 py-1 text-[10px] font-semibold uppercase tracking-wide text-cyan-700">
                                  {template.billingCadenceLabel || 'Por viaje'}
                                </span>
                                <span className="rounded-full border border-emerald-200 bg-emerald-50 px-2.5 py-1 text-[10px] font-semibold uppercase tracking-wide text-emerald-700">
                                  Hoy
                                </span>
                              </div>
                              <p className="mt-1 truncate text-sm text-slate-600">
                                {template.serviceTaskLabel || template.serviceModeLabel || 'Servicio'} · {template.origin || 'Sin origen'} → {template.destination || 'Sin destino'}
                              </p>
                              <div className="mt-2 flex flex-wrap gap-2 text-[11px] text-slate-500">
                                {template.weekDayLabels?.length ? (
                                  <span className="rounded-full border border-slate-200 bg-slate-50 px-2.5 py-1 font-semibold text-slate-600">
                                    {template.weekDayLabels.join(' · ')}
                                  </span>
                                ) : null}
                                {[template.serviceTimeWindowStart, template.serviceTimeWindowEnd].filter(Boolean).length ? (
                                  <span className="rounded-full border border-slate-200 bg-slate-50 px-2.5 py-1 font-semibold text-slate-600">
                                    {`${template.serviceTimeWindowStart || 'Sin hora'}${template.serviceTimeWindowEnd ? ` a ${template.serviceTimeWindowEnd}` : ''}`}
                                  </span>
                                ) : null}
                                <span className="rounded-full border border-slate-200 bg-slate-50 px-2.5 py-1 font-semibold text-slate-600">
                                  {formatMoney(template.estimatedTotalToCharge || 0)}
                                </span>
                              </div>
                            </div>
                            <button
                              type="button"
                              onClick={() =>
                                handleActivateTemplateToRoute(template, {
                                  date: newRoute.date,
                                  asException: false,
                                })
                              }
                              disabled={activatingTemplateId === template.id}
                              className="inline-flex items-center gap-2 rounded-2xl border border-cyan-300 bg-cyan-600 px-4 py-2 text-sm font-semibold text-white transition hover:bg-cyan-700 disabled:cursor-not-allowed disabled:opacity-60"
                            >
                              <Icon path="M12 4.5v15m7.5-7.5h-15" className="h-4 w-4" />
                              {activatingTemplateId === template.id ? 'Activando...' : 'Activar y cargar'}
                            </button>
                          </div>
                        </div>
                      ))}
                    </div>
                  ) : null}

                  {routeScheduledExceptionTemplates.length > 0 ? (
                    <div className="rounded-2xl border border-amber-200 bg-amber-50/60">
                      <button
                        type="button"
                        onClick={() => setShowProgrammedExceptions((prev) => !prev)}
                        className="flex w-full items-center justify-between gap-3 px-4 py-3 text-left"
                      >
                        <div>
                          <p className="text-[10px] font-semibold uppercase tracking-[0.18em] text-amber-700">
                            Excepciones y adelantos
                          </p>
                          <p className="mt-1 text-sm font-bold text-slate-900">
                            {routeScheduledExceptionTemplates.length} servicio{routeScheduledExceptionTemplates.length === 1 ? '' : 's'} fuera del día original
                          </p>
                        </div>
                        <div className="flex items-center gap-3">
                          <span className="rounded-full border border-amber-200 bg-white px-2.5 py-1 text-[10px] font-semibold uppercase tracking-wide text-amber-700">
                            {showProgrammedExceptions ? 'Ocultar' : 'Ver'}
                          </span>
                          <Icon
                            path={showProgrammedExceptions ? 'M19.5 8.25l-7.5 7.5-7.5-7.5' : 'M8.25 4.5l7.5 7.5-7.5 7.5'}
                            className="h-4 w-4 text-amber-700"
                          />
                        </div>
                      </button>

                      {showProgrammedExceptions ? (
                        <div className="space-y-2 border-t border-amber-200 px-4 py-3">
                          {routeScheduledExceptionTemplates.slice(0, 6).map((template) => (
                            <div
                              key={`route-template-exception-${template.id}`}
                              className="rounded-2xl border border-amber-200 bg-white/90 px-4 py-3 shadow-sm"
                            >
                              <div className="flex flex-wrap items-center justify-between gap-3">
                                <div className="min-w-0 flex-1">
                                  <div className="flex flex-wrap items-center gap-2">
                                    <p className="text-sm font-bold text-slate-900">
                                      {template.clientNameSnapshot || 'Cliente'}
                                    </p>
                                    <span className="rounded-full border border-amber-200 bg-amber-50 px-2.5 py-1 text-[10px] font-semibold uppercase tracking-wide text-amber-700">
                                      Excepción
                                    </span>
                                  </div>
                                  <p className="mt-1 truncate text-sm text-slate-600">
                                    {template.serviceTaskLabel || template.serviceModeLabel || 'Servicio'} · {template.origin || 'Sin origen'} → {template.destination || 'Sin destino'}
                                  </p>
                                </div>
                                <button
                                  type="button"
                                  onClick={() =>
                                    handleActivateTemplateToRoute(template, {
                                      date: newRoute.date,
                                      asException: true,
                                    })
                                  }
                                  disabled={activatingTemplateId === template.id}
                                  className="inline-flex items-center gap-2 rounded-2xl border border-amber-300 bg-white px-4 py-2 text-sm font-semibold text-amber-800 transition hover:bg-amber-100 disabled:cursor-not-allowed disabled:opacity-60"
                                >
                                  <Icon path="M12 4.5v15m7.5-7.5h-15" className="h-4 w-4" />
                                  {activatingTemplateId === template.id ? 'Activando...' : 'Agregar excepción'}
                                </button>
                              </div>
                            </div>
                          ))}
                        </div>
                      ) : null}
                    </div>
                  ) : null}
                    </div>
                  ) : (
                    <div className="mt-4 rounded-2xl border border-dashed border-cyan-200 bg-white/80 px-4 py-4 text-sm text-slate-500">
                      No vemos servicios programados para esta combinación de unidad, chofer y fecha. Si el contrato es libre o cae en otro día, podés cambiar la fecha de la ruta arriba y revisar la semana.
                    </div>
                  )}
                </div>
              ) : null}
            </div>
            </RouteFlowSection>

            <RouteFlowSection
              id="logistics-route-pending-services"
              step="3"
              tone="violet"
              title="Servicios pendientes"
              description="Tomá servicios que llegaron desde comercial y sumalos a esta hoja sin duplicar recorridos."
              expanded={pendingServicesExpanded}
              onToggle={() => setPendingServicesExpanded((prev) => !prev)}
              badge={
                <span className="rounded-full border border-violet-200 bg-white px-2.5 py-1 text-[10px] font-bold uppercase tracking-[0.16em] text-violet-700">
                  {routePendingAvailableServices.length} disponible{routePendingAvailableServices.length === 1 ? '' : 's'}
                </span>
              }
            >
            <div className="flex flex-wrap items-start justify-between gap-3">
              <div>
                <p className="text-[11px] font-semibold uppercase tracking-[0.22em] text-violet-600">
                  Servicios pendientes
                </p>
                <h4 className="mt-1 text-base font-bold text-slate-900">
                  Servicios listos para pasar a la ruta
                </h4>
                <p className="mt-1 text-xs leading-5 text-slate-500">
                  Esto lee la cola comercial del módulo Servicio y la convierte en retiro y entrega dentro de la hoja de ruta.
                </p>
              </div>
              <SectionBadge
                tone={routePendingAvailableServices.length > 0 ? 'violet' : routePendingTakenServices.length > 0 ? 'amber' : 'slate'}
              >
                {routePendingAvailableServices.length} disponibles
              </SectionBadge>
            </div>

            {routePendingServices.length > 0 ? (
              <div className="mt-4 space-y-3">
                <div className="flex flex-wrap items-center gap-2 text-[11px] font-semibold uppercase tracking-[0.16em] text-slate-500">
                  <span className="rounded-full border border-violet-200 bg-violet-50 px-2.5 py-1 text-violet-700">
                    Disponibles: {routePendingAvailableServices.length}
                  </span>
                  {routePendingTakenServices.length > 0 ? (
                    <span className="rounded-full border border-slate-200 bg-slate-100 px-2.5 py-1 text-slate-600">
                      Ya tomados: {routePendingTakenServices.length}
                    </span>
                  ) : null}
                </div>

                {routePendingAvailableServices.length > 0 ? (
                  <div className="space-y-3">
                    {routePendingAvailableServices.slice(0, 6).map((service) => (
                      <div
                        key={service.id}
                        className="rounded-2xl border border-violet-100 bg-white/90 px-4 py-4 shadow-sm transition"
                      >
                        <div className="flex flex-wrap items-start justify-between gap-3">
                          <div className="min-w-0 flex-1">
                            <div className="flex flex-wrap items-center gap-2">
                              <p className="text-sm font-bold text-slate-900">
                                {service.clientNameSnapshot || 'Cliente sin nombre'}
                              </p>
                              <span className="rounded-full border border-violet-200 bg-violet-50 px-2.5 py-1 text-[11px] font-semibold uppercase tracking-wide text-violet-700">
                                {service.serviceModeLabel || service.serviceMode || 'Servicio'}
                              </span>
                            </div>
                            <p className="mt-1 text-sm text-slate-500">
                              {(service.serviceTypeLabel || service.serviceType || 'Servicio')} · {service.origin || 'Sin origen'} → {service.destination || 'Sin destino'}
                            </p>
                            <p className="mt-2 text-xs text-slate-500">
                              Fecha {String(service.scheduledDate || service.date || '').trim() || 'sin fecha'} · Precio {formatMoney(service.salePrice || 0)}
                            </p>
                          </div>
                          <button
                            type="button"
                            onClick={() => handleAttachPendingService(service)}
                            className="inline-flex items-center gap-2 rounded-2xl border border-violet-300 bg-violet-600 px-4 py-2 text-sm font-semibold text-white transition hover:bg-violet-700"
                          >
                            <Icon path="M12 4.5v15m7.5-7.5h-15" className="h-4 w-4" />
                            Agregar a esta ruta
                          </button>
                        </div>
                      </div>
                    ))}
                  </div>
                ) : (
                  <div
                    className="rounded-2xl border border-dashed border-violet-200 bg-white/80 px-4 py-4 text-sm text-slate-500"
                  >
                    No hay servicios libres para esta unidad en este momento. Si todos ya fueron tomados, los vas a ver abajo en gris.
                  </div>
                )}

                {routePendingTakenServices.length > 0 ? (
                  <div className="rounded-2xl border border-slate-200 bg-slate-50/80">
                    <button
                      type="button"
                      onClick={() => setShowTakenPendingServices((prev) => !prev)}
                      className="flex w-full items-center justify-between gap-3 px-4 py-3 text-left"
                    >
                      <div>
                        <p className="text-[10px] font-semibold uppercase tracking-[0.18em] text-slate-500">
                          Ya tomados por otras rutas
                        </p>
                        <p className="mt-1 text-sm font-bold text-slate-900">
                          {routePendingTakenServices.length} servicio{routePendingTakenServices.length === 1 ? '' : 's'} bloqueado{routePendingTakenServices.length === 1 ? '' : 's'}
                        </p>
                      </div>
                      <div className="flex items-center gap-3">
                        <span className="rounded-full border border-slate-200 bg-white px-2.5 py-1 text-[10px] font-semibold uppercase tracking-wide text-slate-600">
                          {showTakenPendingServices ? 'Ocultar' : 'Ver'}
                        </span>
                        <Icon
                          path={showTakenPendingServices ? 'M19.5 8.25l-7.5 7.5-7.5-7.5' : 'M8.25 4.5l7.5 7.5-7.5 7.5'}
                          className="h-4 w-4 text-slate-600"
                        />
                      </div>
                    </button>

                    {showTakenPendingServices ? (
                      <div className="space-y-3 border-t border-slate-200 px-4 py-3">
                        {routePendingTakenServices.slice(0, 6).map((service) => (
                          <div
                            key={service.id}
                            className="rounded-2xl border border-slate-200 bg-slate-100/90 px-4 py-4 opacity-85 shadow-sm transition"
                          >
                            <div className="flex flex-wrap items-start justify-between gap-3">
                              <div className="min-w-0 flex-1">
                                <div className="flex flex-wrap items-center gap-2">
                                  <p className="text-sm font-bold text-slate-900">
                                    {service.clientNameSnapshot || 'Cliente sin nombre'}
                                  </p>
                                  <span className="rounded-full border border-violet-200 bg-violet-50 px-2.5 py-1 text-[11px] font-semibold uppercase tracking-wide text-violet-700">
                                    {service.serviceModeLabel || service.serviceMode || 'Servicio'}
                                  </span>
                                  <span
                                    className={`rounded-full border px-2.5 py-1 text-[10px] font-semibold uppercase tracking-[0.16em] ${
                                      service.lockTone === 'in_transit'
                                        ? 'border-cyan-200 bg-cyan-50 text-cyan-700'
                                        : 'border-slate-300 bg-white text-slate-600'
                                    }`}
                                  >
                                    {service.lockTone === 'in_transit' ? 'En tránsito' : 'Tomado por logística'}
                                  </span>
                                </div>
                                <p className="mt-1 text-sm text-slate-500">
                                  {(service.serviceTypeLabel || service.serviceType || 'Servicio')} · {service.origin || 'Sin origen'} → {service.destination || 'Sin destino'}
                                </p>
                                <p className="mt-2 text-xs text-slate-500">
                                  Fecha {String(service.scheduledDate || service.date || '').trim() || 'sin fecha'} · Precio {formatMoney(service.salePrice || 0)}
                                </p>
                                <p className="mt-2 text-xs font-medium text-slate-600">
                                  {service.lockTone === 'in_transit' ? 'En tránsito con' : 'Tomado por'}{' '}
                                  <span className="font-bold text-slate-800">
                                    {service.transportNameSnapshot ||
                                      service.driverNameSnapshot ||
                                      service.routeNameSnapshot ||
                                      'otra ruta'}
                                  </span>
                                  {service.routeNameSnapshot ? ` · ${service.routeNameSnapshot}` : ''}
                                </p>
                              </div>
                              <button
                                type="button"
                                disabled
                                className="inline-flex cursor-not-allowed items-center gap-2 rounded-2xl border border-slate-200 bg-white px-4 py-2 text-sm font-semibold text-slate-400"
                              >
                                <Icon
                                  path="M12 6v6m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
                                  className="h-4 w-4"
                                />
                                Ya tomado
                              </button>
                            </div>
                          </div>
                        ))}
                      </div>
                    ) : null}
                  </div>
                ) : null}
              </div>
            ) : (
              <div className="mt-4 rounded-2xl border border-dashed border-violet-200 bg-white/80 px-4 py-4 text-sm text-slate-500">
                No hay servicios pendientes de logística para sumar ahora. Cuando mandes uno desde el módulo Servicio, va a aparecer acá.
              </div>
            )}
          </RouteFlowSection>
          </div>

          <RouteFlowSection
            id="logistics-route-destination-form"
            step="4"
            tone="emerald"
            title="Cargar destino"
            description="Buscá un destino existente o completalo acá mismo para dejarlo listo para esta ruta y las próximas."
            expanded={destinationSectionExpanded}
            onToggle={() => setDestinationSectionExpanded((prev) => !prev)}
            recommended={!hasStartedDestinationForm}
            badge={
              editingDestinationId ? (
                <span className="rounded-full border border-amber-200 bg-amber-50 px-2.5 py-1 text-[10px] font-bold uppercase tracking-[0.16em] text-amber-700">
                  Editando destino
                </span>
              ) : null
            }
            maxHeightClassName="max-h-[5200px]"
          >
          <div
            className={`rounded-[24px] border border-dashed p-4 transition-all ${
              isDraggingOver
                ? 'border-blue-400 bg-blue-50 shadow-inner'
                : 'border-slate-200 bg-slate-50/70'
            }`}
            onDragOver={(e) => {
              e.preventDefault();
              setIsDraggingOver(true);
            }}
            onDragLeave={() => setIsDraggingOver(false)}
            onDrop={handleDrop}
          >
            <div className="mb-4 flex flex-wrap items-center justify-between gap-3">
              <div>
                <p className="text-[11px] font-semibold uppercase tracking-[0.24em] text-slate-500">
                  Nuevo destino
                </p>
                <h4 className="mt-1 text-lg font-bold text-slate-900">
                  Acá creás la parada de la hoja
                </h4>
                <p className="mt-1 text-sm text-slate-500">
                  Si ya existe, buscá por nombre. Si no existe, completalo una sola vez y queda guardado para próximas rutas.
                </p>
              </div>

              {isDraggingOver && (
                <SectionBadge tone="blue">Soltar aquí</SectionBadge>
              )}
            </div>

            <div className="space-y-5">
              {!hasStartedDestinationForm ? (
                <div className="rounded-[22px] border border-emerald-200 bg-[linear-gradient(180deg,#ecfdf5_0%,#ffffff_100%)] px-4 py-4 shadow-[0_12px_28px_rgba(16,185,129,0.08)]">
                  <div className="flex flex-wrap items-start justify-between gap-3">
                    <div>
                      <p className="text-[11px] font-semibold uppercase tracking-[0.22em] text-emerald-700">
                        Aviso operativo
                      </p>
                      <p className="mt-1 text-sm font-semibold text-slate-900">
                        Primero buscá el destino por nombre.
                      </p>
                      <p className="mt-1 text-sm text-slate-600">
                        Si no aparece en la lista, seguí con calle, localidad y validación de ubicación.
                      </p>
                    </div>
                    <span className="rounded-full border border-emerald-200 bg-white px-3 py-1 text-[10px] font-black uppercase tracking-[0.18em] text-emerald-700">
                      Paso principal
                    </span>
                  </div>
                </div>
              ) : null}

              <div className="rounded-[24px] border border-slate-200 bg-white p-4 shadow-[0_18px_45px_rgba(15,23,42,0.06)]">
                <div className="relative" ref={contactAutocompleteRef}>
                  {renderGuidedHint(
                    'name',
                    'Acá ponés el nombre del destino.'
                  )}
                  <label className={getGuidedLabelClass('name')}>
                    Nombre del destino
                  </label>

                  <input
                    type="text"
                    placeholder="Cliente, proveedor o destino"
                    value={searchTerm || newDestination.name}
                    onFocus={handleOpenContactSuggestions}
                    onClick={handleOpenContactSuggestions}
                      onChange={(e) => {
                        setSearchTerm(e.target.value);
                        setShowContactSuggestions(true);
                        setSelectedPendingDeliveryContext(null);
                        handleDestinationChange('name', e.target.value);

                      if (e.target.value.length > 0) {
                        setNewDestination((prev) => ({
                          ...prev,
                          name: e.target.value,
                          address: '',
                          apartment: '',
                          locality: '',
                          province: DEFAULT_PROVINCE,
                          partido: '',
                          postalCode: '',
                          latlng: null
                        }));
                        setProvinceExplicitlySet(false);
                        setAddressGeocodingError('');
                        setAddressValidation(normalizeAddressValidationState());
                        setAddressSuggestions([]);
                        setShowAddressSuggestions(false);
                        setManualLat('');
                        setManualLng('');
                      }
                    }}
                    className={getGuidedFieldClass('name')}
                  />

                  <p className="mt-2 text-xs text-slate-500">
                    Tocá el campo para ver retiros pendientes, productos listos para salida o escribí para autocompletar contactos existentes.
                  </p>

                   {showContactSuggestions && (
                      <div className="absolute z-20 mt-2 max-h-72 w-full overflow-y-auto rounded-2xl border border-slate-200 bg-white p-2 shadow-2xl">
                        {!toInputValue(searchTerm) ? (
                          <>
                            <div className="px-3 pb-2 pt-1 text-[11px] font-semibold uppercase tracking-[0.18em] text-slate-500">
                              Pendientes y destinos operativos
                            </div>
                            {supplierPendingContacts.length > 0 ? (
                              <div className="mb-2 space-y-2 px-2 pb-2">
                                <p className="px-1 text-[10px] font-bold uppercase tracking-[0.18em] text-amber-700">
                                  Retiros pendientes de proveedor
                                </p>
                                {supplierPendingContacts.slice(0, 6).map(({ contact, lines, totalUnits }) => (
                                  <button
                                    key={`supplier-pending-${contact.id}-${normalizeLookupText(contact.name)}`}
                                    type="button"
                                    onMouseDown={(event) => {
                                      event.preventDefault();
                                      handleSelectContact(contact);
                                    }}
                                    className="flex w-full items-start justify-between gap-3 rounded-2xl border border-amber-200 bg-[linear-gradient(180deg,#fff8eb_0%,#ffffff_100%)] px-3 py-3 text-left transition hover:border-amber-300 hover:bg-amber-50"
                                  >
                                    <div>
                                      <p className="font-semibold text-slate-900">{contact.name}</p>
                                      <p className="mt-1 text-xs text-slate-500">
                                        {lines.length} pendiente{lines.length > 1 ? 's' : ''} de retiro · {totalUnits} unidad{totalUnits !== 1 ? 'es' : ''} listas
                                      </p>
                                    </div>
                                    <span className="shrink-0 rounded-full border border-amber-200 bg-amber-50 px-2.5 py-1 text-[10px] font-bold uppercase tracking-[0.18em] text-amber-700">
                                      Retiro pendiente
                                    </span>
                                  </button>
                                ))}
                              </div>
                            ) : null}
                          </>
                        ) : null}
                        {filteredContacts.length > 0 ? (
                        <>
                      {filteredContacts.map((contact) => (
                        <button
                          key={contact.id}
                          type="button"
                          onMouseDown={(event) => {
                            event.preventDefault();
                            handleSelectContact(contact);
                          }}
                          className="w-full rounded-xl px-3 py-3 text-left transition hover:bg-blue-50"
                        >
                          <div className="flex items-start justify-between gap-3">
                            <div>
                              <p className="font-semibold text-slate-800">{contact.name}</p>
                              {contact.addresses && contact.addresses.length > 0 && (
                                <p className="mt-1 text-sm text-slate-500">
                                  {contact.addresses[0].address}
                                  {contact.addresses[0]?.locality
                                    ? ` · ${contact.addresses[0].locality}`
                                    : ''}
                                </p>
                              )}
                            </div>

                            <div className="flex flex-col items-end gap-1">
                                <span className={`rounded-full border px-2.5 py-1 text-[11px] font-semibold capitalize ${getContactTypeToneClass(contact.type)}`}>
                                  {formatContactTypeLabel(contact.type)}
                                </span>
                              {(() => {
                                const supplierPendingSummary = (contact?.id && supplierPendingSummaryByKey[`id:${contact.id}`])
                                  || supplierPendingSummaryByKey[`name:${normalizeLookupText(contact?.name)}`]
                                  || null;
                                const outboundPendingSummary = (contact?.id && pendingDeliverySummaryByKey[`id:${contact.id}`])
                                  || pendingDeliverySummaryByKey[`name:${normalizeLookupText(contact?.name)}`]
                                  || null;
                                const normalizedType = resolveContactTypeFamily(contact?.type);
                                const outboundReasonLabel = outboundPendingSummary?.orders?.length
                                  ? getPendingDeliverySummaryReason(outboundPendingSummary.orders)
                                  : '';

                                if (outboundPendingSummary?.orders?.length) {
                                  return (
                                    <span className="rounded-full border border-emerald-200 bg-emerald-50 px-2.5 py-1 text-[10px] font-bold uppercase tracking-[0.18em] text-emerald-700">
                                      {outboundReasonLabel === 'Retiro garantía' ? 'P. retiro' : 'P. salida'} · {outboundReasonLabel} · {outboundPendingSummary.totalUnits} u.
                                    </span>
                                  );
                                }

                                if (supplierPendingSummary?.lines?.length && normalizedType === 'proveedor') {
                                  return (
                                    <span className="rounded-full border border-amber-200 bg-amber-50 px-2.5 py-1 text-[10px] font-bold uppercase tracking-[0.18em] text-amber-700">
                                      P. entrada · {supplierPendingSummary.lines.length} pendiente{supplierPendingSummary.lines.length > 1 ? 's' : ''} · {supplierPendingSummary.totalUnits} u.
                                    </span>
                                  );
                                }

                                return null;
                              })()}
                            </div>
                          </div>
                        </button>
                      ))}
                        </>
                      ) : (
                        <div className="rounded-xl border border-dashed border-slate-200 bg-slate-50 px-3 py-4 text-sm text-slate-500">
                          No hay pendientes para este filtro todavía.
                        </div>
                      )}
                    </div>
                  )}
                </div>
              </div>


              {selectedContactRecord && resolveContactTypeFamily(selectedContactRecord?.type) === 'proveedor' && selectedSupplierPendingLines.length > 0 ? (
                <div className="rounded-[24px] border border-amber-200 bg-[linear-gradient(180deg,#fff9eb_0%,#ffffff_100%)] p-4 shadow-[0_16px_34px_rgba(245,158,11,0.12)]">
                  <div className="flex flex-wrap items-start justify-between gap-3">
                    <div>
                      <p className="text-[11px] font-semibold uppercase tracking-[0.22em] text-amber-700">Pendientes del proveedor</p>
                      <h4 className="mt-1 text-lg font-bold text-slate-900">Retiros disponibles para esta parada</h4>
                      <p className="mt-1 text-sm text-slate-600">
                        {selectedContactRecord.name} tiene {selectedSupplierPendingLines.length} producto{selectedSupplierPendingLines.length > 1 ? 's' : ''} pendiente{selectedSupplierPendingLines.length > 1 ? 's' : ''} de retiro, con {selectedSupplierPendingUnits} unidad{selectedSupplierPendingUnits !== 1 ? 'es' : ''} en total.
                      </p>
                    </div>
                    <div className="flex flex-wrap gap-2">
                      <button
                        type="button"
                        onClick={() => handleAttachSupplierPendingLines('all')}
                        className="inline-flex items-center gap-2 rounded-2xl border border-amber-300 bg-amber-100 px-4 py-2 text-sm font-semibold text-amber-900 transition hover:bg-amber-200"
                      >
                        <Icon path="M3 7.5h18M3 12h18m-18 4.5h18" className="h-4 w-4" />
                        Retirar todo
                      </button>
                      <button
                        type="button"
                        onClick={() => handleAttachSupplierPendingLines('selected')}
                        className="inline-flex items-center gap-2 rounded-2xl bg-slate-900 px-4 py-2 text-sm font-semibold text-white transition hover:bg-cyan-600"
                      >
                        <Icon path="M4.5 12.75l6 6 9-13.5" className="h-4 w-4" />
                        Añadir seleccionados
                      </button>
                    </div>
                  </div>

                  <div className="mt-4 space-y-3">
                    {selectedSupplierPendingLines.map((line) => {
                      const pendingQty = Math.max(0, Number(line?.pendingPickupQuantity || 0));
                      const isLinked = linkedPendingPurchaseLineIds.has(String(line.id));
                      const isChecked = selectedPendingPurchaseLineIds.includes(String(line.id));
                      return (
                        <div
                          key={line.id}
                          role="button"
                          tabIndex={isLinked ? -1 : 0}
                          onClick={() => !isLinked && handleAttachSinglePendingPurchaseLine(line)}
                          onKeyDown={(event) => {
                            if (isLinked) return;
                            if (event.key === 'Enter' || event.key === ' ') {
                              event.preventDefault();
                              handleAttachSinglePendingPurchaseLine(line);
                            }
                          }}
                          className={`flex ${isLinked ? 'cursor-not-allowed' : 'cursor-pointer'} items-start gap-3 rounded-2xl border px-4 py-3 transition ${isLinked ? 'border-slate-200 bg-slate-100 text-slate-500' : isChecked ? 'border-cyan-300 bg-cyan-50' : 'border-slate-200 bg-white hover:border-cyan-200 hover:bg-cyan-50/40'}`}
                        >
                          <input
                            type="checkbox"
                            checked={isLinked ? true : isChecked}
                            disabled={isLinked}
                            onClick={(event) => event.stopPropagation()}
                            onChange={() => handleTogglePendingPurchaseLine(line.id)}
                            className="mt-1 h-4 w-4 rounded border-slate-300 text-cyan-600 focus:ring-cyan-500"
                          />
                          <div className="min-w-0 flex-1">
                            <div className="flex flex-wrap items-center gap-2">
                              <p className="font-semibold text-slate-900">{line.productNameSnapshot || 'Producto pendiente'}</p>
                              <span className="rounded-full bg-slate-100 px-2 py-0.5 text-[11px] font-semibold text-slate-600">{pendingQty} {line.unitSnapshot || 'u.'}</span>
                              {isLinked ? <span className="rounded-full border border-cyan-200 bg-cyan-50 px-2 py-0.5 text-[11px] font-bold uppercase tracking-[0.18em] text-cyan-700">Ya cargado en la ruta</span> : <span className="rounded-full border border-emerald-200 bg-emerald-50 px-2 py-0.5 text-[11px] font-bold uppercase tracking-[0.18em] text-emerald-700">Click para cargar</span>}
                            </div>
                            <p className="mt-1 text-sm text-slate-500">{line.categorySnapshot || 'Sin categoría'}{line.skuSnapshot ? ` · SKU ${line.skuSnapshot}` : ''}</p>
                            <p className="mt-1 text-xs text-slate-500">Pendiente de retiro desde stock. Si hacés click en esta línea, se genera automáticamente una carga operativa de retiro.</p>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </div>
              ) : null}

              {shouldShowPendingDeliverySuggestions ? (
                <div className="rounded-[24px] border border-emerald-200 bg-[linear-gradient(180deg,#ecfdf5_0%,#ffffff_100%)] p-4 shadow-[0_16px_34px_rgba(16,185,129,0.12)]">
                  <div className="flex flex-wrap items-start justify-between gap-3">
                    <div>
                      <p className="text-[11px] font-semibold uppercase tracking-[0.22em] text-emerald-700">
                        {selectedPendingDeliveryStage === 'pickup' ? 'Retiros desde garantía' : 'Salidas desde stock'}
                      </p>
                      <h4 className="mt-1 text-lg font-bold text-slate-900">
                        {selectedPendingDeliveryStage === 'pickup' ? 'Retiros aprobados' : 'Pendientes de entrega'}
                      </h4>
                      <p className="mt-1 text-sm text-slate-600">
                        {newDestination.name || selectedContactRecord?.name
                          ? selectedPendingDeliveryStage === 'pickup'
                            ? `${newDestination.name || selectedContactRecord?.name} ya tiene ${selectedPendingDeliveryItems.length} retiro${selectedPendingDeliveryItems.length > 1 ? 's' : ''} de garantía aprobado${selectedPendingDeliveryItems.length > 1 ? 's' : ''}, con ${selectedPendingDeliveryUnits} unidad${selectedPendingDeliveryUnits !== 1 ? 'es' : ''} listas para volver.`
                            : `${newDestination.name || selectedContactRecord?.name} ya tiene ${selectedPendingDeliveryItems.length} pendiente${selectedPendingDeliveryItems.length > 1 ? 's' : ''} preparado${selectedPendingDeliveryItems.length > 1 ? 's' : ''} para entrega, con ${selectedPendingDeliveryUnits} unidad${selectedPendingDeliveryUnits !== 1 ? 'es' : ''} listas para salir.`
                          : selectedPendingDeliveryStage === 'pickup'
                            ? `Tenés ${selectedPendingDeliveryItems.length} retiro${selectedPendingDeliveryItems.length > 1 ? 's' : ''} de garantía aprobado${selectedPendingDeliveryItems.length > 1 ? 's' : ''}, con ${selectedPendingDeliveryUnits} unidad${selectedPendingDeliveryUnits !== 1 ? 'es' : ''} listas para volver.`
                            : `Tenés ${selectedPendingDeliveryItems.length} pendiente${selectedPendingDeliveryItems.length > 1 ? 's' : ''} de entrega listo${selectedPendingDeliveryItems.length > 1 ? 's' : ''} para logística, con ${selectedPendingDeliveryUnits} unidad${selectedPendingDeliveryUnits !== 1 ? 'es' : ''} preparadas.`}
                      </p>
                    </div>
                    <div className="flex flex-wrap gap-2">
                      <button
                        type="button"
                        onClick={() => handleAttachPendingDeliveryItems('all')}
                        className="inline-flex items-center gap-2 rounded-2xl border border-emerald-300 bg-emerald-100 px-4 py-2 text-sm font-semibold text-emerald-900 transition hover:bg-emerald-200"
                      >
                        <Icon path="M3 7.5h18M3 12h18m-18 4.5h18" className="h-4 w-4" />
                        {selectedPendingDeliveryStage === 'pickup' ? 'Retirar todo' : 'Cargar todo'}
                      </button>
                      <button
                        type="button"
                        onClick={() => handleAttachPendingDeliveryItems('selected')}
                        className="inline-flex items-center gap-2 rounded-2xl bg-slate-900 px-4 py-2 text-sm font-semibold text-white transition hover:bg-emerald-600"
                      >
                        <Icon path="M4.5 12.75l6 6 9-13.5" className="h-4 w-4" />
                        {selectedPendingDeliveryStage === 'pickup' ? 'Asignar retiros' : 'Asignar marcados'}
                      </button>
                    </div>
                  </div>

                  <div className="mt-4 space-y-3">
                    {selectedPendingDeliveryItems.map((item) => {
                      const isChecked = selectedPendingDeliveryLineIds.includes(item.lineKey);
                      const salePrice = Number(item.saleUnitPrice || 0);
                      const reasonLabel =
                        item.routePendingStage === 'pickup'
                          ? 'Retiro garantía'
                          : getStockReasonRouteLabel(item.operationType);
                      return (
                        <div
                          key={item.lineKey}
                          role="button"
                          tabIndex={0}
                          onClick={() => handleTogglePendingDeliveryLine(item.lineKey)}
                          onKeyDown={(event) => {
                            if (event.key === 'Enter' || event.key === ' ') {
                              event.preventDefault();
                              handleTogglePendingDeliveryLine(item.lineKey);
                            }
                          }}
                          className={`flex cursor-pointer items-start gap-3 rounded-2xl border px-4 py-3 transition ${isChecked ? 'border-emerald-300 bg-emerald-50' : 'border-slate-200 bg-white hover:border-emerald-200 hover:bg-emerald-50/40'}`}
                        >
                          <input
                            type="checkbox"
                            checked={isChecked}
                            onClick={(event) => event.stopPropagation()}
                            onChange={() => handleTogglePendingDeliveryLine(item.lineKey)}
                            className="mt-1 h-4 w-4 rounded border-slate-300 text-emerald-600 focus:ring-emerald-500"
                          />
                          <div className="min-w-0 flex-1">
                            <div className="flex flex-wrap items-center gap-2">
                              <p className="font-semibold text-slate-900">{item.productNameSnapshot || 'Producto preparado'}</p>
                              <span className="rounded-full bg-slate-100 px-2 py-0.5 text-[11px] font-semibold text-slate-600">
                                {item.routePendingQuantity || item.pendingDeliveryQuantity} {item.unitSnapshot || 'u.'} list.
                              </span>
                              <span className="rounded-full bg-cyan-50 px-2 py-0.5 text-[11px] font-semibold text-cyan-700">
                                Pedido {item.orderNumber || item.orderId}
                              </span>
                              <span className="rounded-full border border-fuchsia-200 bg-fuchsia-50 px-2 py-0.5 text-[11px] font-bold uppercase tracking-[0.16em] text-fuchsia-700">
                                {reasonLabel}
                              </span>
                              <span className="rounded-full border border-emerald-200 bg-emerald-50 px-2 py-0.5 text-[11px] font-bold uppercase tracking-[0.18em] text-emerald-700">
                                Marcá para asignar
                              </span>
                            </div>
                            <p className="mt-1 text-sm text-slate-500">
                              {item.reference
                                ? `Ref. ${item.reference}`
                                : item.routePendingStage === 'pickup'
                                  ? `${reasonLabel} listo para volver`
                                  : `${reasonLabel} lista para entrega`}{item.skuSnapshot ? ` · SKU ${item.skuSnapshot}` : ''}
                            </p>
                            <p className="mt-1 text-xs text-slate-500">
                              {item.routePendingStage === 'pickup'
                                ? 'La garantía ya respondió y este producto quedó listo para retiro. Primero lo marcás y después lo asignás a la carga operativa.'
                                : item.operationType === 'warranty'
                                ? 'La salida quedó preparada para garantía. Primero la marcás y después la asignás a la carga operativa.'
                                : salePrice > 0
                                ? `Venta base ${formatMoney(salePrice)} por unidad. Primero marcás el pedido y después lo asignás a la carga operativa.`
                                : 'La salida ya quedó preparada. Primero la marcás y después la asignás a la ruta.'}
                            </p>
                          </div>
                          <span className={`shrink-0 rounded-xl border px-3 py-2 text-[11px] font-bold uppercase tracking-[0.16em] ${isChecked ? 'border-emerald-400 bg-emerald-200 text-emerald-900' : 'border-slate-200 bg-slate-100 text-slate-500'}`}>
                            {isChecked ? 'Marcado' : 'Seleccionar'}
                          </span>
                        </div>
                      );
                    })}
                  </div>
                </div>
              ) : null}

              <div className="rounded-[24px] border border-cyan-200/80 bg-[linear-gradient(180deg,#ecfeff_0%,#f8fafc_100%)] p-4 shadow-[0_16px_40px_rgba(6,182,212,0.10)]">
                <div className="mb-4 flex flex-wrap items-start justify-between gap-3">
                  <div id="logistics-route-loads">
                    <p className="text-[11px] font-semibold uppercase tracking-[0.22em] text-cyan-700">
                      Formulario del destino
                    </p>
                    <h4 className="mt-1 text-lg font-bold text-slate-900">Nombre, dirección y datos operativos</h4>
                    <p className="mt-1 text-sm text-slate-600">
                      Si el destino ya existe, el sistema autocompleta. Si no existe, lo completás acá y queda guardado para próximas rutas.
                    </p>
                  </div>

                  <div className={`rounded-full border px-3 py-1 text-[11px] font-semibold ${addressStatusTone}`}>
                    {addressValidation.status === 'success'
                      ? 'Ubicación validada'
                      : addressValidation.status === 'loading'
                      ? 'Buscando dirección'
                      : addressValidation.status === 'error'
                      ? 'Falta validar ubicación'
                      : 'Pendiente de validación'}
                  </div>
                </div>

                <div className="grid grid-cols-1 gap-3 md:grid-cols-2">
                  <div className="md:col-span-2" ref={addressAutocompleteRef}>
                    {renderGuidedHint(
                      'address',
                      'Acá completás calle y número.'
                    )}
                    <label className={getGuidedLabelClass('address')}>
                      Dirección operativa
                    </label>
                    <div className="relative">
                      <div className="pointer-events-none absolute left-4 top-1/2 -translate-y-1/2 text-slate-400">
                        <Icon path="M12 2.25c-3.728 0-6.75 3.022-6.75 6.75 0 5.477 6.75 12.75 6.75 12.75S18.75 14.477 18.75 9c0-3.728-3.022-6.75-6.75-6.75z" className="h-5 w-5" />
                      </div>
                      <input
                        type="text"
                        placeholder="Calle y número"
                        value={newDestination.address}
                        onChange={(e) => updateAddressField('address', e.target.value)}
                        onFocus={() => {
                          if (addressSuggestions.length > 0) {
                            setShowAddressSuggestions(true);
                          }
                        }}
                        autoComplete="off"
                        className={getGuidedFieldClass('address', 'pl-11 pr-12')}
                      />
                      {loadingAddressSuggestions && (
                        <div className="absolute right-4 top-1/2 -translate-y-1/2 text-cyan-600">
                          <Icon
                            path="M4.75 12a7.25 7.25 0 0112.511-4.95M19.25 12a7.25 7.25 0 01-12.511 4.95"
                            className="h-5 w-5 animate-spin"
                          />
                        </div>
                      )}
                    </div>

                    {showAddressSuggestions && (
                      <div className="mt-2 overflow-hidden rounded-2xl border border-cyan-200 bg-white shadow-[0_18px_45px_rgba(6,182,212,0.14)]">
                        {addressSuggestions.length > 0 ? (
                          <div className="max-h-64 overflow-y-auto p-2">
                            {addressSuggestions.map((item, index) => (
                              <button
                                key={`${item.place_id || item.formatted || 'suggestion'}-${index}`}
                                type="button"
                                onClick={() => applyAddressSuggestion(item)}
                                className="flex w-full flex-col rounded-2xl px-3 py-3 text-left transition hover:bg-cyan-50"
                              >
                                <p className="font-semibold text-slate-900">
                                  {item.address_line1 || item.street || item.formatted || 'Dirección sugerida'}
                                </p>
                                <p className="mt-1 text-sm text-slate-500">
                                  {item.formatted || [item.city, item.state, item.country].filter(Boolean).join(', ')}
                                </p>
                              </button>
                            ))}
                          </div>
                        ) : (
                          <div className="px-4 py-3 text-sm text-slate-600">
                            No encontramos sugerencias todavía. Probá con calle, número y localidad.
                          </div>
                        )}
                      </div>
                    )}
                  </div>

                  <div>
                    {renderGuidedHint(
                      'province',
                      'Elegí la provincia correcta para afinar la geolocalización.'
                    )}
                    <label className={getGuidedLabelClass('province')}>
                      Provincia
                    </label>
                    <select
                      value={newDestination.province}
                      onChange={(e) => updateAddressField('province', e.target.value)}
                      className={getGuidedFieldClass('province')}
                    >
                      {ARGENTINA_PROVINCES.map((p) => (
                        <option key={p} value={p}>
                          {p}
                        </option>
                      ))}
                    </select>
                  </div>

                  {newDestination.province === 'Buenos Aires' && (
                    <div>
                      {renderGuidedHint(
                        'partido',
                        'Si el destino está en Buenos Aires, completá el partido.'
                      )}
                      <label className={getGuidedLabelClass('partido')}>
                        Partido
                      </label>
                      <input
                        type="text"
                        placeholder="Partido"
                        value={newDestination.partido}
                        onChange={(e) => updateAddressField('partido', e.target.value)}
                        className={getGuidedFieldClass('partido')}
                      />
                    </div>
                  )}

                  <div>
                    {renderGuidedHint(
                      'locality',
                      'Acá definís la localidad para ubicar mejor el destino.'
                    )}
                    <label className={getGuidedLabelClass('locality')}>
                      Localidad
                    </label>
                    <input
                      type="text"
                      value={newDestination.locality}
                      onChange={(e) => updateAddressField('locality', e.target.value)}
                      placeholder="Localidad"
                      className={getGuidedFieldClass('locality')}
                    />
                  </div>
                </div>

                <div className="mt-4 rounded-2xl border border-slate-200 bg-slate-50/80">
                  <button
                    type="button"
                    onClick={() => setDestinationDetailsExpanded((prev) => !prev)}
                    className="flex w-full items-center justify-between gap-3 px-4 py-3 text-left"
                  >
                    <div>
                      <p className="text-[10px] font-semibold uppercase tracking-[0.18em] text-slate-500">
                        Datos opcionales
                      </p>
                      <p className="mt-1 text-sm font-bold text-slate-900">
                        Tipo de registro, unidad interna y código postal
                      </p>
                    </div>
                    <div className="flex items-center gap-3">
                      <span className="rounded-full border border-slate-200 bg-white px-2.5 py-1 text-[10px] font-semibold uppercase tracking-wide text-slate-600">
                        {destinationDetailsExpanded ? 'Ocultar' : 'Ver'}
                      </span>
                      <Icon
                        path={destinationDetailsExpanded ? 'M19.5 8.25l-7.5 7.5-7.5-7.5' : 'M8.25 4.5l7.5 7.5-7.5 7.5'}
                        className="h-4 w-4 text-slate-600"
                      />
                    </div>
                  </button>

                  {destinationDetailsExpanded ? (
                    <div className="grid grid-cols-1 gap-3 border-t border-slate-200 px-4 py-4 md:grid-cols-3">
                      <div>
                        <label className={LABEL_CLASS}>
                          Piso / Depto / Casa
                        </label>
                        <input
                          type="text"
                          placeholder="Casa, depto o piso"
                          value={newDestination.apartment}
                          onChange={(e) => handleDestinationChange('apartment', e.target.value)}
                          className={INPUT_BASE_CLASS}
                        />
                      </div>

                      <div>
                        <label className={LABEL_CLASS}>
                          Tipo de registro
                        </label>
                        <select
                          value={newDestination.contactType}
                          onChange={(e) => handleDestinationChange('contactType', e.target.value)}
                          className={`${INPUT_BASE_CLASS} capitalize`}
                        >
                          {availableContactTypes.map((type) => (
                            <option key={type} value={type}>
                              {formatContactTypeLabel(type)}
                            </option>
                          ))}
                        </select>
                      </div>

                      <div>
                        <label className={LABEL_CLASS}>
                          Código postal
                        </label>
                        <input
                          type="text"
                          value={newDestination.postalCode}
                          onChange={(e) => updateAddressField('postalCode', e.target.value)}
                          placeholder="Código postal"
                          className={INPUT_BASE_CLASS}
                        />
                      </div>
                    </div>
                  ) : null}
                </div>

                <p className="mt-3 text-xs leading-relaxed text-slate-500">
                  Si el destino no es de CABA, elegí la provincia correcta o sumá la localidad antes de validar. Así la geolocalización se vuelve mucho más precisa y evita errores tempranos.
                </p>

                <div className="mt-4 rounded-2xl border border-white/70 bg-white/80 p-4 shadow-[inset_0_1px_0_rgba(255,255,255,0.75)]">
                  <div className="flex flex-wrap items-start justify-between gap-3">
                    <div>
                      <p className="text-[11px] font-semibold uppercase tracking-[0.18em] text-slate-500">
                        Validación de ubicación
                      </p>
                      <p className="mt-1 text-sm text-slate-600">{addressValidation.text}</p>
                    </div>

                    <button
                      type="button"
                      onClick={handleValidateAddress}
                      className="inline-flex min-h-[44px] items-center justify-center rounded-2xl border border-cyan-200 bg-cyan-600 px-4 text-sm font-semibold text-white transition hover:bg-cyan-700"
                    >
                      Validar dirección
                    </button>
                  </div>

                  {newDestination.latlng && (
                    <div className="mt-3 flex flex-wrap gap-2">
                      <span className="rounded-full border border-emerald-200 bg-emerald-50 px-3 py-1 text-[11px] font-semibold text-emerald-700">
                        Lat {newDestination.latlng.lat.toFixed(5)}
                      </span>
                      <span className="rounded-full border border-emerald-200 bg-emerald-50 px-3 py-1 text-[11px] font-semibold text-emerald-700">
                        Lng {newDestination.latlng.lng.toFixed(5)}
                      </span>
                    </div>
                  )}

                  <div className="mt-4 border-t border-slate-200 pt-4">
                    <button
                      type="button"
                      onClick={() => setShowManualCoordinates((prev) => !prev)}
                      className="inline-flex items-center gap-2 text-sm font-semibold text-slate-700 transition hover:text-slate-900"
                    >
                      <Icon
                        path={showManualCoordinates ? 'M19.5 12h-15' : 'M12 4.5v15m7.5-7.5h-15'}
                        className="h-4 w-4"
                      />
                      {showManualCoordinates
                        ? 'Ocultar coordenadas manuales'
                        : 'Usar coordenadas manuales como respaldo'}
                    </button>

                    {showManualCoordinates && (
                      <div className="mt-3 grid grid-cols-1 gap-3 rounded-2xl border border-slate-200 bg-slate-50 p-4 md:grid-cols-[1fr_1fr_auto]">
                        <div>
                          <label className={LABEL_CLASS}>
                            Latitud
                          </label>
                          <input
                            type="text"
                            placeholder="-34.6037"
                            value={manualLat}
                            onChange={(e) => setManualLat(e.target.value)}
                            className={INPUT_BASE_CLASS}
                          />
                        </div>

                        <div>
                          <label className={LABEL_CLASS}>
                            Longitud
                          </label>
                          <input
                            type="text"
                            placeholder="-58.3816"
                            value={manualLng}
                            onChange={(e) => setManualLng(e.target.value)}
                            className={INPUT_BASE_CLASS}
                          />
                        </div>

                        <div className="flex items-end">
                          <button
                            type="button"
                            onClick={handleApplyManualCoordinates}
                            className="inline-flex min-h-[50px] w-full items-center justify-center rounded-2xl border border-slate-200 bg-white px-4 text-sm font-semibold text-slate-800 transition hover:bg-slate-100"
                          >
                            Aplicar
                          </button>
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              </div>

              {addressGeocodingError && (
                <div className="flex items-start gap-2 rounded-2xl border border-rose-200 bg-rose-50 px-4 py-3 text-sm text-rose-600">
                  <Icon
                    path="M12 9v3.75m-9.303 3.376c-.866 1.5.217 3.374 1.948 3.374h14.71c1.73 0 2.813-1.874 1.948-3.374L13.949 3.378c-.866-1.5-3.032-1.5-3.898 0L2.697 16.126zM12 15.75h.007v.008H12v-.008z"
                    className="mt-0.5 h-4 w-4 shrink-0"
                  />
                  <span>{addressGeocodingError}</span>
                </div>
              )}

              <div className="rounded-[24px] border border-amber-200/80 bg-[linear-gradient(180deg,#fff7ed_0%,#ffffff_100%)] p-4 shadow-[0_16px_40px_rgba(249,115,22,0.10)]">
                <div className="grid grid-cols-1 gap-3 md:grid-cols-2">
                  <div id="logistics-route-loads-panel" className="md:col-span-2 rounded-[22px] border border-slate-200 bg-white/80 p-4 shadow-[inset_0_1px_0_rgba(255,255,255,0.8),0_12px_24px_rgba(15,23,42,0.06)]">
                    <div className="flex items-start justify-between gap-4">
                      <button
                        type="button"
                        onClick={() => setOperationalLoadsExpanded((prev) => !prev)}
                        className="flex flex-1 items-start justify-between gap-4 text-left"
                      >
                        <div>
                          <p className="text-[11px] font-semibold uppercase tracking-[0.22em] text-slate-500">
                            Carga operativa
                          </p>
                          <h5 className="mt-1 text-base font-bold text-slate-900">
                            Datos de parada y mercadería
                          </h5>
                        </div>

                        <span className="inline-flex h-10 w-10 shrink-0 items-center justify-center rounded-full border border-slate-200 bg-white text-slate-600">
                          <Icon
                            path={operationalLoadsExpanded ? 'M19.5 12h-15' : 'M12 4.5v15m7.5-7.5h-15'}
                            className="h-4 w-4"
                          />
                        </span>
                      </button>

                      {(newDestination.operationalLoads || []).length > 0 && (
                        <span className="rounded-full border border-slate-200 bg-slate-100 px-3 py-1 text-[11px] font-semibold text-slate-600">
                          {(newDestination.operationalLoads || []).length} carga{(newDestination.operationalLoads || []).length > 1 ? 's' : ''}
                        </span>
                      )}
                    </div>

                    {operationalLoadsExpanded && (
                      <>
                        <div className="mt-4 grid grid-cols-1 gap-3 md:grid-cols-2">
                          {availableContactPeople.length > 0 && (
                            <div className="md:col-span-2">
                              <label className={LABEL_CLASS}>
                                Contactos guardados para este destino
                              </label>
                              <select
                                value={
                                  newDestination.contactName || newDestination.contactPhone
                                    ? `${newDestination.contactName}::${newDestination.contactPhone}`
                                    : '__new__'
                                }
                                onChange={(e) => handleSelectContactPerson(e.target.value)}
                                className={INPUT_BASE_CLASS}
                              >
                                <option value="__new__">Agregar contacto nuevo</option>
                                {availableContactPeople.map((person) => (
                                  <option
                                    key={`${person.name}-${person.phone}`}
                                    value={`${person.name}::${person.phone}`}
                                  >
                                    {person.name || 'Sin nombre'}{person.phone ? ` · ${person.phone}` : ''}
                                  </option>
                                ))}
                              </select>
                            </div>
                          )}

                          <div>
                            <label className={LABEL_CLASS}>
                              Nombre de contacto
                            </label>
                            <input
                              type="text"
                              placeholder="Nombre de contacto"
                              value={newDestination.contactName}
                              onChange={(e) => handleDestinationChange('contactName', e.target.value)}
                              className={INPUT_BASE_CLASS}
                            />
                          </div>

                          <div>
                            <label className={LABEL_CLASS}>
                              Teléfono
                            </label>
                            <input
                              type="text"
                              placeholder="Teléfono de contacto"
                              value={newDestination.contactPhone}
                              onChange={(e) => handleDestinationChange('contactPhone', e.target.value)}
                              className={INPUT_BASE_CLASS}
                            />
                          </div>

                          <div>
                            <label className={LABEL_CLASS}>
                              Prioridad
                            </label>
                            <select
                              value={newDestination.priority}
                              onChange={(e) => handleDestinationChange('priority', e.target.value)}
                              className={INPUT_BASE_CLASS}
                            >
                              <option value="Normal">Normal</option>
                              <option value="Importante">Importante</option>
                              <option value="Urgente">Urgente</option>
                            </select>
                          </div>

                          <div>
                            <label className={LABEL_CLASS}>
                              Franja horaria
                            </label>
                            <input
                              type="text"
                              placeholder="Ej: antes de las 15:00"
                              value={newDestination.schedule}
                              onChange={(e) => handleDestinationChange('schedule', e.target.value)}
                              className={INPUT_BASE_CLASS}
                            />
                          </div>

                          {pendingOperationalLoadOptions.length > 0 && (
                            <div className="md:col-span-2">
                              <label className={LABEL_CLASS}>
                                Recibe carga pendiente
                              </label>
                              <select
                                defaultValue=""
                                onChange={(e) => {
                                  handleAttachPendingOperationalLoad(e.target.value);
                                  e.target.value = '';
                                }}
                                className={INPUT_BASE_CLASS}
                              >
                                <option value="">Seleccionar carga pendiente</option>
                                {pendingOperationalLoadOptions.map((option) => (
                                  <option key={option.value} value={option.value}>
                                    {option.label}
                                  </option>
                                ))}
                              </select>

                              {(newDestination.incomingOperationalLoads || []).length > 0 && (
                                <div className="mt-3 flex flex-wrap gap-2">
                                  {newDestination.incomingOperationalLoads.map((incoming) => {
                                    const sourceDestination = newRoute.destinations.find(
                                      (item) => item.id === incoming.sourceDestinationId
                                    );
                                    const sourceOrder = Number(sourceDestination?.order ?? 0) + 1;
                                    const incomingLabel =
                                      String(
                                        incoming.title ||
                                          incoming.productNameSnapshot ||
                                          incoming.stockProductNameSnapshot ||
                                          incoming.productName ||
                                          ''
                                      ).trim() || 'producto';

                                    return (
                                      <span
                                        key={`${incoming.sourceDestinationId}-${incoming.loadId}`}
                                        className="inline-flex items-center gap-2 rounded-full border border-cyan-200 bg-cyan-50 px-3 py-1.5 text-xs font-semibold text-cyan-700"
                                      >
                                        Recibe {incomingLabel} · destino {sourceOrder}
                                        <button
                                          type="button"
                                          onClick={() =>
                                            handleRemoveIncomingOperationalLoad(
                                              incoming.loadId,
                                              incoming.sourceDestinationId
                                            )
                                          }
                                          className="rounded-full p-0.5 text-cyan-600 transition hover:bg-cyan-100"
                                          aria-label="Quitar vínculo"
                                        >
                                          <Icon path="M6 18L18 6M6 6l12 12" className="h-3.5 w-3.5" />
                                        </button>
                                      </span>
                                    );
                                  })}
                                </div>
                              )}
                            </div>
                          )}

                        </div>

                        <div className="mt-4 flex justify-end">
                          <button
                            type="button"
                            onClick={handleAddOperationalLoad}
                            className="inline-flex items-center justify-center gap-2 rounded-2xl border border-slate-200 bg-slate-100 px-4 py-2.5 text-sm font-semibold text-slate-700 transition hover:bg-slate-200"
                          >
                            <Icon path="M12 4.5v15m7.5-7.5h-15" className="h-4 w-4" />
                            Añadir carga
                          </button>
                        </div>

                        {(newDestination.operationalLoads || []).length === 0 ? (
                          <div className="mt-4 rounded-2xl border border-dashed border-slate-300 bg-slate-50 px-4 py-5 text-sm text-slate-500">
                            Añadí la primera carga cuando ya tengas claro qué producto entra, sale o se traslada.
                          </div>
                        ) : (
                          <div className="mt-4 space-y-3">
                            {(newDestination.operationalLoads || []).map((load, index) => (
                              <div
                                key={load.id}
                                className="rounded-[20px] border border-slate-200 bg-[linear-gradient(180deg,#ffffff_0%,#f8fafc_100%)] p-4 shadow-sm"
                              >
                                <div className="flex items-start justify-between gap-3">
                                  <div>
                                    <p className="text-xs font-semibold uppercase tracking-[0.2em] text-slate-400">
                                      Carga {index + 1}
                                    </p>
                                    <p className="mt-1 text-sm text-slate-500">
                                      Origen: esta parada. Destino final:{' '}
                                      {getOperationalLoadTargetLabel(
                                        load.targetStopId,
                                        newRoute.destinations
                                      )}
                                    </p>
                                    <div className="mt-2 flex flex-wrap items-center gap-2">
                                      {load.stockOutboundOrderNumber ? (
                                        <span className="rounded-full bg-cyan-50 px-2.5 py-1 text-[11px] font-semibold text-cyan-700">
                                          Pedido {load.stockOutboundOrderNumber}
                                        </span>
                                      ) : null}
                                      {load.stockProductId &&
                                      STOCK_OUTBOUND_OPERATION_TYPES.has(String(load.operationType || '').trim().toLowerCase()) ? (
                                        <span className="rounded-full border border-fuchsia-200 bg-fuchsia-50 px-2.5 py-1 text-[11px] font-bold uppercase tracking-[0.16em] text-fuchsia-700">
                                          {getStockReasonRouteLabel(load.stockOperationReason)}
                                        </span>
                                      ) : null}
                                    </div>
                                  </div>

                                  <button
                                    type="button"
                                    onClick={() => handleRemoveOperationalLoad(load.id)}
                                    className="rounded-full p-2 text-slate-400 transition hover:bg-rose-50 hover:text-rose-600"
                                    aria-label="Quitar carga"
                                  >
                                    <Icon path="M6 18L18 6M6 6l12 12" className="h-4 w-4" />
                                  </button>
                                </div>

                                <div className="mt-3 grid grid-cols-1 gap-3 md:grid-cols-2">
                                  <div>
                                    <label className={LABEL_CLASS}>Producto</label>
                                    <input
                                      type="text"
                                      list="operational-load-memory"
                                      autoComplete="off"
                                      value={load.title}
                                      onChange={(e) => handleOperationalLoadTitleInput(load.id, e.target.value)}
                                      onBlur={(e) => handleOperationalLoadTitleSelect(load.id, e.target.value)}
                                      placeholder="Ej: Computadora, documentación, repuestos"
                                      className={INPUT_BASE_CLASS}
                                    />
                                  </div>

                                  <div>
                                    <label className={LABEL_CLASS}>Vincular a stock</label>
                                    <select
                                      value={load.stockProductId || ''}
                                      onChange={(e) => handleOperationalLoadStockProductSelect(load.id, e.target.value)}
                                      className={INPUT_BASE_CLASS}
                                    >
                                      <option value="">Carga libre sin stock</option>
                                      {stockProductOptions.map((product) => {
                                        const availableQty = getStockProductAvailableOutboundQty(product);
                                        return (
                                          <option key={product.id} value={product.id}>
                                            {product.name} · disp. {availableQty}
                                          </option>
                                        );
                                      })}
                                    </select>
                                  </div>

                                  <div>
                                    <label className={LABEL_CLASS}>Categoría</label>
                                    <input
                                      type="text"
                                      value={load.category}
                                      onChange={(e) => handleOperationalLoadChange(load.id, 'category', e.target.value)}
                                      placeholder="Ej: equipo, cajas, sobres"
                                      className={INPUT_BASE_CLASS}
                                    />
                                  </div>

                                  {load.stockProductId && STOCK_OUTBOUND_OPERATION_TYPES.has(String(load.operationType || '').trim().toLowerCase()) ? (
                                    <div>
                                      <label className={LABEL_CLASS}>Motivo de salida</label>
                                      <select
                                        value={load.stockOperationReason || 'sale'}
                                        onChange={(e) => handleOperationalLoadChange(load.id, 'stockOperationReason', e.target.value)}
                                        className={INPUT_BASE_CLASS}
                                      >
                                        {STOCK_OPERATION_REASONS.map((reason) => (
                                          <option key={reason} value={reason}>
                                            {STOCK_REASON_LABELS[reason] || reason}
                                          </option>
                                        ))}
                                      </select>
                                    </div>
                                  ) : null}

                                  <div>
                                    <label className={LABEL_CLASS}>Cantidad</label>
                                    <input
                                      type="number"
                                      min="1"
                                      value={load.quantity}
                                      onChange={(e) => handleOperationalLoadChange(load.id, 'quantity', e.target.value)}
                                      className={INPUT_BASE_CLASS}
                                    />
                                  </div>

                                  <div>
                                    <label className={LABEL_CLASS}>Presentación</label>
                                    <select
                                      value={load.unit}
                                      onChange={(e) => handleOperationalLoadChange(load.id, 'unit', e.target.value)}
                                      className={INPUT_BASE_CLASS}
                                    >
                                      {OPERATIONAL_LOAD_UNITS.map((unit) => (
                                        <option key={unit} value={unit}>
                                          {unit}
                                        </option>
                                      ))}
                                    </select>
                                  </div>

                                  <div>
                                    <label className={LABEL_CLASS}>Acción</label>
                                    <select
                                      value={load.operationType}
                                      onChange={(e) => handleOperationalLoadChange(load.id, 'operationType', e.target.value)}
                                      className={INPUT_BASE_CLASS}
                                    >
                                      {OPERATIONAL_LOAD_OPERATION_TYPES.map((operationType) => (
                                        <option key={operationType} value={operationType}>
                                          {getDestinationOperationRoleLabel(operationType)}
                                        </option>
                                      ))}
                                    </select>
                                  </div>

                                  <div>
                                    <label className={LABEL_CLASS}>Código de referencia</label>
                                    <input
                                      type="text"
                                      value={load.referenceCode}
                                      onChange={(e) => handleOperationalLoadChange(load.id, 'referenceCode', e.target.value)}
                                      placeholder="Opcional"
                                      className={INPUT_BASE_CLASS}
                                    />
                                  </div>

                                  {load.stockProductId ? (
                                    <div className="md:col-span-2 rounded-2xl border border-slate-200 bg-slate-50 px-4 py-3 text-xs text-slate-600">
                                      <span className="font-semibold text-slate-800">Salida trazada:</span>{' '}
                                      esta carga ya queda vinculada a un producto de stock y deja base de motivo,
                                      destino y recorrido para la salida operativa.
                                    </div>
                                  ) : null}

                                  <div className="md:col-span-2">
                                    <label className={LABEL_CLASS}>Descripción breve</label>
                                    <textarea
                                      value={load.description}
                                      onChange={(e) => handleOperationalLoadChange(load.id, 'description', e.target.value)}
                                      placeholder="Qué se retira, entrega, guarda o traslada"
                                      className={`${INPUT_BASE_CLASS} min-h-[104px] resize-none leading-relaxed`}
                                    />
                                  </div>

                                  <div>
                                    <label className={LABEL_CLASS}>Destino final</label>
                                    <select
                                      value={load.targetStopId || ''}
                                      onChange={(e) => handleOperationalLoadChange(load.id, 'targetStopId', e.target.value)}
                                      className={INPUT_BASE_CLASS}
                                    >
                                      <option value="">A definir después</option>
                                      <option value="pending">Pendiente</option>
                                      <option value="oficina">Oficina</option>
                                      {newRoute.destinations
                                        .filter((d) => d.id !== newDestination.id)
                                        .map((d) => (
                                          <option key={d.id} value={d.id}>
                                            {d.name}
                                          </option>
                                        ))}
                                    </select>
                                  </div>

                                  <div className="rounded-2xl border border-slate-200 bg-slate-50 px-4 py-3">
                                    <p className="text-sm font-semibold text-slate-800">Evidencia requerida</p>
                                    <label className="mt-3 flex items-center gap-2 text-sm text-slate-600">
                                      <input
                                        type="checkbox"
                                        checked={Boolean(load.requiresEvidenceOnPickup)}
                                        onChange={(e) =>
                                          handleOperationalLoadChange(load.id, 'requiresEvidenceOnPickup', e.target.checked)
                                        }
                                      />
                                      Al retirar
                                    </label>
                                    <label className="mt-2 flex items-center gap-2 text-sm text-slate-600">
                                      <input
                                        type="checkbox"
                                        checked={Boolean(load.requiresEvidenceOnDelivery)}
                                        onChange={(e) =>
                                          handleOperationalLoadChange(load.id, 'requiresEvidenceOnDelivery', e.target.checked)
                                        }
                                      />
                                      Al entregar
                                    </label>
                                  </div>
                                </div>
                              </div>
                            ))}
                          </div>
                        )}
                      </>
                    )}
                    <datalist id="operational-load-memory">
                      {operationalLoadMemory.map((item) => (
                        <option key={item.title} value={item.title} />
                      ))}
                    </datalist>
                  </div>
                </div>
              </div>

              <button
                type="button"
                onClick={handleAddDestination}
                className="inline-flex w-full items-center justify-center gap-2 rounded-2xl bg-indigo-600 px-4 py-3 text-sm font-bold text-white shadow-lg shadow-indigo-200 transition hover:bg-indigo-700"
              >
                <Icon path={editingDestinationId ? 'M16.862 4.487l1.687-1.688a1.875 1.875 0 112.652 2.652L10.582 16.07a4.5 4.5 0 01-1.897 1.13L6 18l.8-2.685a4.5 4.5 0 011.13-1.897l8.932-8.931z' : 'M12 4.5v15m7.5-7.5h-15'} className="h-5 w-5" />
                {editingDestinationId ? 'Actualizar destino' : 'Añadir destino a la hoja'}
              </button>
            </div>
          </div>
          </RouteFlowSection>
          </div>
        </div>
      </section>

      <section className="lg:order-2 lg:col-span-5">
        <div id="logistics-route-preview" className="rounded-[28px] border border-violet-100 bg-[linear-gradient(180deg,#faf7ff_0%,#ffffff_100%)] p-6 shadow-[0_18px_38px_rgba(139,92,246,0.08)]">
          <PanelHeader
            eyebrow="Hoja en armado"
            title="Vista previa del recorrido"
            description="Revisá el orden, controlá la información cargada y ajustá la secuencia antes de guardar la ruta."
            extra={
              <>
                <SectionBadge tone="violet">
                  Destinos: {newRoute.destinations.length}
                </SectionBadge>
                <SectionBadge tone="slate">
                  Fecha: {new Date(`${newRoute.date}T00:00:00`).toLocaleDateString('es-AR')}
                </SectionBadge>
              </>
            }
          />

          <button
            type="button"
            onClick={() => setPreviewPanelExpanded((prev) => !prev)}
            className={`mb-4 inline-flex w-full items-center justify-center gap-2 rounded-[18px] border px-4 py-3 text-sm font-bold transition ${
              previewPanelExpanded
                ? 'border-violet-200 bg-white/90 text-violet-700'
                : 'border-violet-300 bg-[linear-gradient(180deg,#faf5ff_0%,#ffffff_100%)] text-violet-800 animate-pulse'
            }`}
          >
            <Icon
              path={previewPanelExpanded ? 'M19.5 12h-15' : 'M12 4.5v15m7.5-7.5h-15'}
              className="h-4 w-4"
            />
            {previewPanelExpanded ? 'Plegar vista previa' : 'Desplegar vista previa'}
          </button>

          <div
            className={`overflow-hidden transition-all duration-500 ease-out ${
              previewPanelExpanded ? 'max-h-[4200px] opacity-100' : 'max-h-0 opacity-0'
            }`}
          >

          {newRoute.destinations.length === 0 ? (
            <div className="rounded-[24px] border border-dashed border-slate-300 bg-slate-50 px-6 py-14 text-center">
              <div className="mx-auto flex h-14 w-14 items-center justify-center rounded-2xl bg-white shadow-sm">
                <Icon
                  path="M19.5 14.25v-2.625a3.375 3.375 0 00-1.028-2.426L13.5 4.5a3.375 3.375 0 00-2.386-.99H6.75A2.25 2.25 0 004.5 5.76v12.48a2.25 2.25 0 002.25 2.25h10.5a2.25 2.25 0 002.25-2.25z"
                  className="h-7 w-7 text-slate-500"
                />
              </div>

              <h4 className="mt-5 text-lg font-semibold text-slate-800">
                Todavía no hay destinos cargados
              </h4>
              <p className="mx-auto mt-2 max-w-md text-sm leading-relaxed text-slate-500">
                Sumá la primera parada desde el formulario para empezar a construir la hoja de
                ruta y revisar el recorrido en esta vista.
              </p>
            </div>
          ) : (
            <ol className="space-y-4">
              {newRoute.destinations.map((dest) => (
                <li
                  key={dest.id}
                  className="group rounded-[24px] border border-slate-200 bg-slate-50 p-4 transition hover:border-slate-300 hover:bg-white"
                >
                  <div className="flex items-start justify-between gap-4">
                    <div className="flex items-start gap-4">
                      <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-2xl bg-white text-sm font-bold text-slate-700 shadow-sm">
                        {dest.order + 1}
                      </div>

                      <div>
                        <div className="flex flex-wrap items-center gap-2">
                          <h4 className="text-base font-bold text-slate-800">{dest.name}</h4>
                          <span className="rounded-full bg-slate-200 px-2.5 py-1 text-[11px] font-semibold text-slate-700">
                            {dest.type}
                          </span>
                          <span className="rounded-full bg-blue-100 px-3 py-1.5 text-xs font-bold text-blue-700">
                            {dest.priority}
                          </span>
                        </div>

                        {dest.serviceLinked || dest.serviceId ? (
                          <div className={`mt-3 flex flex-wrap items-center gap-2 rounded-2xl border px-3 py-2 ${getServiceBadgeTheme(dest.serviceId).shell}`}>
                            <span className={`rounded-full border px-2.5 py-1 text-[10px] font-black uppercase tracking-[0.18em] ${getServiceBadgeTheme(dest.serviceId).soft}`}>
                              {dest.serviceReferenceCode || `Servicio ${String(dest.serviceId || '').slice(0, 6).toUpperCase()}`}
                            </span>
                            <span className={`text-xs font-bold ${getServiceBadgeTheme(dest.serviceId).text}`}>
                              {dest.contactName || 'Cliente sin nombre'}
                            </span>
                            <span className={`rounded-full border px-2.5 py-1 text-[10px] font-semibold uppercase tracking-[0.16em] ${getServiceBadgeTheme(dest.serviceId).soft}`}>
                              {dest.serviceTaskLabel || dest.serviceTask || 'Tarea'}
                            </span>
                            <span className="rounded-full border border-white/70 bg-white/90 px-2.5 py-1 text-[10px] font-semibold uppercase tracking-[0.16em] text-slate-600">
                              {dest.serviceTransportLabel || dest.serviceTypeLabel || dest.serviceTransport || dest.serviceType || 'Transporte'}
                            </span>
                          </div>
                        ) : null}

                        <div className="mt-2 text-sm leading-relaxed text-slate-600">
                          {dest.address} {dest.apartment && `(${dest.apartment})`}
                          <br />
                          {[dest.locality, dest.partido, dest.province, dest.postalCode]
                            .filter(Boolean)
                            .join(', ')}
                        </div>

                        {dest.description && (
                          <div className="mt-3 rounded-2xl bg-white px-3 py-2 text-sm text-slate-600">
                            {dest.description}
                          </div>
                        )}

                        {(dest.operationalLoads || []).length > 0 && (
                          <div className="mt-3 rounded-2xl border border-slate-200 bg-white px-3 py-3">
                            <div className="mb-2 flex items-center justify-between gap-2">
                              <span className="text-[11px] font-semibold uppercase tracking-[0.2em] text-slate-400">
                                Carga operativa
                              </span>
                              <span className="rounded-full bg-slate-100 px-2.5 py-1 text-[11px] font-semibold text-slate-600">
                                {dest.type || 'Parada'}
                              </span>
                            </div>

                              <div className="space-y-2">
                                {(dest.operationalLoads || []).map((load) => (
                                  <div
                                    key={load.id}
                                    className="rounded-xl border border-slate-100 bg-slate-50 px-3 py-2"
                                  >
                                    <div className="flex flex-wrap items-center gap-2">
                                      <span className="text-sm font-semibold text-slate-900">
                                        {load.title || 'Producto sin definir'}
                                      </span>
                                      <span className="rounded-full bg-white px-2.5 py-1 text-xs font-bold text-slate-600">
                                        {load.quantity} {load.unit}
                                      </span>
                                      {load.stockOutboundOrderNumber ? (
                                        <span className="rounded-full bg-cyan-50 px-2.5 py-1 text-[11px] font-semibold text-cyan-700">
                                          Pedido {load.stockOutboundOrderNumber}
                                        </span>
                                      ) : null}
                                      {load.stockProductId &&
                                      STOCK_OUTBOUND_OPERATION_TYPES.has(String(load.operationType || '').trim().toLowerCase()) ? (
                                        <span className="rounded-full border border-fuchsia-200 bg-fuchsia-50 px-2.5 py-1 text-[11px] font-bold uppercase tracking-[0.16em] text-fuchsia-700">
                                          {getStockReasonRouteLabel(load.stockOperationReason)}
                                        </span>
                                      ) : null}
                                    </div>

                                  <div className="mt-1 text-sm font-medium text-slate-600">
                                    {getDestinationOperationRoleLabel(load.operationType)} →{' '}
                                    {getOperationalLoadTargetLabel(
                                      load.targetStopId,
                                      newRoute.destinations
                                    )}
                                  </div>

                                  {load.description && (
                                    <p className="mt-1 text-sm text-slate-600">{load.description}</p>
                                  )}
                                </div>
                              ))}
                            </div>
                          </div>
                        )}

                        {(dest.incomingOperationalLoads || []).length > 0 && (
                          <div className="mt-3 rounded-2xl border border-cyan-200 bg-cyan-50 px-3 py-3">
                            <div className="mb-2 text-[11px] font-semibold uppercase tracking-[0.2em] text-cyan-700">
                              Recibe pendientes
                            </div>

                            <div className="flex flex-wrap gap-2">
                              {dest.incomingOperationalLoads.map((incoming) => {
                                const sourceDestination = newRoute.destinations.find(
                                  (item) => item.id === incoming.sourceDestinationId
                                );
                                const sourceOrder = Number(sourceDestination?.order ?? 0) + 1;

                                return (
                                  <span
                                    key={`${incoming.sourceDestinationId}-${incoming.loadId}`}
                                    className="inline-flex items-center rounded-full border border-cyan-300 bg-white px-3.5 py-2 text-xs font-bold text-cyan-700"
                                  >
                                    {incoming.title || 'Producto'} · viene del destino {sourceOrder}
                                  </span>
                                );
                              })}
                            </div>
                          </div>
                        )}

                        <div className="mt-3 flex flex-wrap items-center gap-2">
                          {dest.deliveries ? (
                            <span className="rounded-full bg-emerald-100 px-3 py-1.5 text-xs font-bold text-emerald-700">
                              Entregas: {dest.deliveries}
                            </span>
                          ) : null}

                          {dest.pickups ? (
                            <span className="rounded-full bg-violet-100 px-3 py-1.5 text-xs font-bold text-violet-700">
                              Retiros: {dest.pickups}
                            </span>
                          ) : null}

                          {dest.schedule ? (
                            <span className="rounded-full bg-amber-100 px-3 py-1.5 text-xs font-bold text-amber-700">
                              {dest.schedule}
                            </span>
                          ) : null}

                          {dest.contactName ? (
                            <span className="rounded-full bg-slate-200 px-3 py-1.5 text-xs font-bold text-slate-700">
                              {dest.contactName}
                              {dest.contactPhone ? ` · ${dest.contactPhone}` : ''}
                            </span>
                          ) : null}

                          {dest.contactPhone && !dest.contactName ? (
                            <span className="rounded-full bg-slate-200 px-3 py-1.5 text-xs font-bold text-slate-700">
                              {dest.contactPhone}
                            </span>
                          ) : null}
                        </div>

                        <div className="mt-4 flex flex-wrap items-center gap-2">
                          <button
                            type="button"
                            onClick={() => handleEditDestination(dest)}
                            className="inline-flex items-center gap-2 rounded-full border border-sky-200 bg-sky-50 px-3 py-1.5 text-xs font-bold text-sky-700 transition hover:bg-sky-100"
                          >
                            <Icon path="M16.862 4.487l1.687-1.688a1.875 1.875 0 112.652 2.652L10.582 16.07a4.5 4.5 0 01-1.897 1.13L6 18l.8-2.685a4.5 4.5 0 011.13-1.897l8.932-8.931z" className="h-3.5 w-3.5" />
                            Editar
                          </button>

                          <button
                            type="button"
                            onClick={() => handleMoveDestination(dest.id, 'up')}
                            disabled={dest.order === 0}
                            className="inline-flex items-center gap-2 rounded-full border border-slate-200 bg-white px-3 py-1.5 text-xs font-bold text-slate-700 transition hover:bg-slate-100 disabled:cursor-not-allowed disabled:opacity-40"
                          >
                            <Icon path="M7.5 15l4.5-4.5 4.5 4.5" className="h-3.5 w-3.5" />
                            Subir
                          </button>

                          <button
                            type="button"
                            onClick={() => handleMoveDestination(dest.id, 'down')}
                            disabled={dest.order === newRoute.destinations.length - 1}
                            className="inline-flex items-center gap-2 rounded-full border border-slate-200 bg-white px-3 py-1.5 text-xs font-bold text-slate-700 transition hover:bg-slate-100 disabled:cursor-not-allowed disabled:opacity-40"
                          >
                            <Icon path="M16.5 9l-4.5 4.5L7.5 9" className="h-3.5 w-3.5" />
                            Bajar
                          </button>
                        </div>

                      </div>
                    </div>

                    <button
                      type="button"
                      onClick={() => handleRemoveDestination(dest.id)}
                      className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full text-rose-400 transition hover:bg-rose-100 hover:text-rose-600 md:opacity-0 md:group-hover:opacity-100"
                    >
                      <Icon path="M6 18L18 6M6 6l12 12" className="h-5 w-5" />
                    </button>
                  </div>
                </li>
              ))}
            </ol>
          )}

          <div id="logistics-route-save-actions" className="mt-6 grid grid-cols-1 gap-3 sm:grid-cols-3">
            <button
              type="button"
              onClick={onBack}
              className="inline-flex items-center justify-center gap-2 rounded-2xl border border-slate-200 bg-slate-100 px-4 py-3 text-sm font-bold text-slate-800 transition hover:bg-slate-200"
            >
              <Icon path="M15.75 19.5L8.25 12l7.5-7.5" className="h-4 w-4" />
              Volver
            </button>

            <button
              type="button"
              onClick={handleProgramRouteClick}
              className="inline-flex items-center justify-center gap-2 rounded-2xl bg-violet-600 px-4 py-3 text-sm font-bold text-white shadow-lg shadow-violet-200 transition hover:bg-violet-700"
            >
              <Icon
                path="M8.25 6.75h7.5M8.25 10.5h7.5M8.25 14.25h4.5M6.75 3.75h10.5A2.25 2.25 0 0119.5 6v12A2.25 2.25 0 0117.25 20.25H6.75A2.25 2.25 0 014.5 18V6a2.25 2.25 0 012.25-2.25z"
                className="h-4 w-4"
              />
              Guardar programada
            </button>

            <button
              type="button"
              onClick={() => handleSaveRoute('in_progress')}
              className="inline-flex items-center justify-center gap-2 rounded-2xl bg-emerald-600 px-4 py-3 text-sm font-bold text-white shadow-lg shadow-emerald-200 transition hover:bg-emerald-700"
            >
              <Icon path="M5.25 13.5l4.5 4.5L18.75 9" className="h-4 w-4" />
              Guardar y activar
            </button>
          </div>
          </div>
        </div>
      </section>

      {showSucursalModal && (
        <SucursalSelectionModal
          contact={contactForSucursal}
          existingDestinations={newRoute.destinations}
          onSelect={handleSucursalSelected}
          onClose={handleCloseSucursalModal}
          showMessage={showMessage}
        />
      )}

      {showDatePickerModal && (
        <DatePickerModal
          show={showDatePickerModal}
          onClose={() => setShowDatePickerModal(false)}
          onSelectDate={handleDateSelectedForProgrammedRoute}
          showMessage={showMessage}
        />
      )}
    </div>
  );
};

export default RouteCreator;
